import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import * as React from 'react';
import React__default, { useState, useCallback, forwardRef, useRef, useMemo } from 'react';
import { compose, space, layout, color, typography, flexbox, background, borders, borderWidth, borderRadius, position, variant } from 'styled-system';
import css, { get } from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import { renderToStaticMarkup } from 'react-dom/server';
import { LazyMotion, m, domAnimation } from 'framer-motion';
import ReactSelect, { components } from 'react-select';
import lottie from 'lottie-web';

var textVariants = [
    'display',
    'medium',
    'regular',
    'body',
    'caption',
].reduce(function (acc, variant) {
    acc[variant] = {
        fontFamily: variant,
        lineHeight: variant,
        fontWeight: variant,
        letterSpacing: variant,
    };
    return acc;
}, {});

var buttonVariants = {
    primary: {
        border: 'none',
        bg: 'brandMidnightBlue',
        color: 'neutralsWhite',
        '&:disabled': {
            opacity: '.24',
        },
        '&:active': {
            bg: '#020D2A',
        },
        '&:hover': {
            bg: '#475D98',
        },
    },
    secondary: {
        borderWidth: 'input',
        borderStyle: 'solid',
        borderColor: 'overlayOutlineBlue',
        bg: 'utilityTransparent',
        color: 'brandMidnightBlue',
        '&:disabled': {
            opacity: '.40',
        },
        '&:active': {
            borderColor: 'overlayOutlineBlue',
            bg: 'overlayBlack8',
        },
        '&:hover': {
            borderColor: 'brandMidnightBlue',
            bg: 'overlayWhite50',
        },
    },
    tertiary: {
        borderWidth: 'input',
        borderColor: 'utilityTransparent',
        bg: 'utilityTransparent',
        color: 'utilityLinkBlue',
        '&:disabled': {
            opacity: '.40',
        },
        '&:active': {
            bg: 'overlayBlack8',
        },
        '&:hover': {
            borderWidth: 'input',
            borderStyle: 'solid',
            borderColor: 'neutralsBorderGray',
            bg: 'overlayWhite50',
        },
    },
    white: {
        bg: 'neutralsWhite',
        color: 'neutralsTextBlack',
        border: 'none',
        '&:disabled': {
            bg: 'overlayWhite30',
            color: 'overlayBlack30',
        },
        '&:active': {
            bg: 'overlayWhite50',
            color: 'utilityTrueBlack',
        },
        '&:hover': {
            bg: 'overlayWhite70',
            color: 'neutralsTextBlack',
        },
    },
    secondaryWhite: {
        bg: 'utilityTransparent',
        color: 'neutralsWhite',
        borderWidth: 'input',
        borderColor: 'overlayWhite50',
        '&:disabled': {
            borderColor: 'overlayWhite15',
            color: 'overlayWhite30',
        },
        '&:active': {
            bg: 'overlayBlack30',
            borderColor: 'overlayWhite30',
            color: 'overlayWhite50',
        },
        '&:hover': {
            bg: 'overlayWhite8',
            borderColor: 'overlayWhite70',
            color: 'neutralsWhite',
        },
    },
    circle: {
        borderWidth: 'input',
        borderColor: 'neutralsWhite',
        bg: 'utilityTransparent',
        color: 'brandMidnightBlue',
        '&:disabled': {
            opacity: '.40',
        },
        '&:active': {
            bg: 'neutralsWhite',
        },
        '&:hover': {
            bg: 'neutralsWhite',
        },
    },
};

var colors = {
    text: '#000',
    background: '#fff',
    primary: '#07c',
    secondary: '#30c',
    muted: '#f6f6f6',
    // Brand
    brandBackgroundBlue: '#D9EFFB',
    brandBackgroundSeafoam: '#E0F1F2',
    brandBlueWash: '#EFF7FB',
    brandDarkBlue: '#2A5697',
    brandDarkSeafoam: '#00626A',
    brandLightBlue: '#ABD7EF',
    brandLightSeafoam: '#B6DEE0',
    brandMediumBlue: '#5990BC',
    brandMidnightBlue: '#041A55',
    brandSeafoamWash: '#F0F8F9',
    // Ground
    groundDarkStone: '#141F3C',
    groundLightStone: '#D9E1E4',
    // Neutrals
    neutralsBackgroundGray: '#F1F1F1',
    neutralsBorderGray: '#B4B5B0',
    neutralsGrayWash: '#F8F8F8',
    neutralsLightGray: '#D9DAD5',
    neutralsPlaceholderGray: '#898A86',
    neutralsTextBlack: '#242423',
    neutralsTextGray: '#4F504E',
    neutralsWhite: '#FFFFFF',
    // Overlay
    overlayBlack15: '#00000026',
    overlayBlack30: '#0000004D',
    overlayBlack50: '#00000080',
    overlayBlack70: '#000000B3',
    overlayBlack8: '#00000014',
    overlayBlack90: '#000000E6',
    overlayWhite15: '#FFFFFF26',
    overlayWhite30: '#FFFFFF4D',
    overlayWhite50: '#FFFFFF80',
    overlayWhite70: '#FFFFFFB3',
    overlayWhite90: '#FFFFFFE6',
    overlayWhite8: '#FFFFFF14',
    overlayOutlineBlue: '#041a5566',
    // Utility
    utilityBrightCyan: '#00FFFF',
    utilityDarkErrorRed: '#CF1313',
    utilityDarkLegacyAmber: '#CB8C0A',
    utilityDarkSuccessGreen: '#007536',
    utilityTSASuccessGreen: '#51A831',
    utilityErrorRedWash: '#FFE6E6',
    utilityFocusBlue: '#0098EE',
    utilityHazardYellow: '#FFEB38',
    utilityLegacyAmberWash: '#FDEDCE',
    utilityLightErrorRed: '#FF4646',
    utilityLightLegacyAmber: '#F2BE55',
    utilityLightSuccessGreen: '#15D66E',
    utilityLinkBlue: '#0044C7',
    utilityLinkPurpleVisited: '#7B03B4',
    utilitySuccessGreenWash: '#EDF9F2',
    utilityTrueBlack: '#000000',
    utilityLinkBlueLight: '#68B7FF',
    utilityTransparent: 'transparent',
    utilityCircleButtonGray: '#E5E5E5',
};

var inputColors = {
    default: {
        borderColor: 'neutralsBorderGray',
        color: 'neutralsTextBlack',
        ':focus': {
            borderColor: 'utilityFocusBlue',
            boxShadow: "0 0 0 0.0625rem ".concat(colors.utilityFocusBlue),
        },
    },
    success: {
        borderColor: 'utilityDarkSuccessGreen',
        color: 'neutralsTextBlack',
        ':focus': {
            boxShadow: "0 0 0 0.0625rem ".concat(colors.utilityDarkSuccessGreen),
        },
    },
    error: {
        borderColor: 'utilityDarkErrorRed',
        color: 'neutralsTextBlack',
        ':focus': {
            boxShadow: "0 0 0 0.0625rem ".concat(colors.utilityDarkErrorRed),
        },
    },
};
var inputVariants = {
    default: {
        borderWidth: 'input',
        borderStyle: 'solid',
        borderRadius: 'input',
        fontSize: 2,
        px: 4,
        py: 3,
    },
    borderless: {
        borderStyle: 'none',
        fontSize: 3,
        px: 0,
        py: 3,
        ':focus': {
            boxShadow: 'none important!',
        },
    },
};

var dropdownVariants = {
    default: {
        borderColor: 'neutralsBorderGray',
        ':focus': {
            borderColor: 'utilityFocusBlue',
            boxShadow: "0 0 0 0.0625rem ".concat(colors.utilityFocusBlue),
        },
    },
};

var radioVariants = {
    default: {
        borderColor: 'neutralsPlaceholderGray',
        ':focus': {
            borderWidth: 'inputFocus',
            borderColor: 'utilityFocusBlue',
        },
        ':checked': {
            borderWidth: 'radioChecked',
            borderColor: 'brandDarkBlue',
        },
    },
};

var labelVariants = {
    default: {
        color: 'neutralsTextBlack',
    },
    disabled: {
        color: 'neutralsPlaceholderGray',
    },
};

var messageVariants = {
    default: {
        color: 'neutralsTextBlack',
    },
    success: {
        color: 'utilityDarkSuccessGreen',
    },
    error: {
        color: 'utilityDarkErrorRed',
    },
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var style$1 = {
    borderRadius: '0.5rem',
    padding: '0.75rem',
    paddingLeft: '1rem',
};
var alertVariants = {
    success: __assign({ backgroundColor: 'utilitySuccessGreenWash', variantIcon: 'check', variantIconColor: 'utilityDarkSuccessGreen' }, style$1),
    error: __assign({ backgroundColor: 'utilityErrorRedWash', variantIcon: 'alert-triangle', variantIconColor: 'utilityDarkErrorRed' }, style$1),
    info: __assign({ backgroundColor: 'brandBlueWash', variantIcon: 'alert-circle', variantIconColor: 'brandMediumBlue' }, style$1),
    warning: __assign({ backgroundColor: 'utilityLegacyAmberWash', variantIcon: 'alert-circle', variantIconColor: 'utilityDarkLegacyAmber' }, style$1),
    seafoam: __assign({ backgroundColor: 'brandSeafoamWash', variantIcon: 'wifi', variantIconColor: 'brandDarkSeafoam' }, style$1),
    gray: __assign({ backgroundColor: 'neutralsGrayWash', variantIcon: 'bell', variantIconColor: 'neutralsTextGray' }, style$1),
};

var style = {
    borderRadius: 4,
    paddingX: 2,
    paddingY: 1,
};
var tagVariants = {
    success: __assign({ backgroundColor: 'utilitySuccessGreenWash', variantIconColor: 'utilityDarkSuccessGreen' }, style),
    error: __assign({ backgroundColor: 'utilityErrorRedWash', variantIconColor: 'utilityDarkErrorRed' }, style),
    info: __assign({ backgroundColor: 'brandBackgroundBlue', variantIconColor: 'brandDarkBlue' }, style),
    warning: __assign({ backgroundColor: 'utilityLegacyAmberWash', variantIconColor: 'overlayBlack70' }, style),
    seafoam: __assign({ backgroundColor: 'brandBackgroundSeafoam', variantIconColor: 'brandDarkSeafoam' }, style),
    gray: __assign({ backgroundColor: 'neutralsBackgroundGray', variantIconColor: 'neutralsTextGray' }, style),
};

var cardVariants = {
    primary: {
        borderRadius: '0.5rem',
    },
    secondary: {
        borderRadius: '1.5rem',
    },
};

var selectVariants = {
    default: {
        borderColor: 'neutralsBorderGray',
        ':focus': {
            borderColor: 'utilityFocusBlue',
            boxShadow: "0 0 0 0.0625rem ".concat(colors.utilityFocusBlue),
        },
    },
};

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  textVariants: textVariants,
  buttonVariants: buttonVariants,
  inputColors: inputColors,
  inputVariants: inputVariants,
  dropdownVariants: dropdownVariants,
  radioVariants: radioVariants,
  labelVariants: labelVariants,
  messageVariants: messageVariants,
  alertVariants: alertVariants,
  tagVariants: tagVariants,
  cardVariants: cardVariants,
  selectVariants: selectVariants
});

var boxSelectorVariants = {
    singleSelect: {
        bg: 'neutralsWhite',
        borderColor: 'neutralsLightGray',
        ':active': {
            bg: 'overlayBlack15',
            borderColor: 'neutralsBorderGray',
        },
    },
};

var GlobalStyle = createGlobalStyle(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  body {\n    box-sizing: border-box;\n    margin: 0;\n    padding: 0;\n    font: ", " !important;\n    line-height: ", ";\n    letter-spacing: ", ";\n  }\n  \n  input, select, textarea, button {\n    font-family: inherit;\n    font-size: inherit;\n    font-weight: inherit;\n  }\n}\n"], ["\n  body {\n    box-sizing: border-box;\n    margin: 0;\n    padding: 0;\n    font: ", " !important;\n    line-height: ", ";\n    letter-spacing: ", ";\n  }\n  \n  input, select, textarea, button {\n    font-family: inherit;\n    font-size: inherit;\n    font-weight: inherit;\n  }\n}\n"])), function (_a) {
    var _b, _c;
    var theme = _a.theme;
    return "".concat((_b = theme === null || theme === void 0 ? void 0 : theme.fontWeights) === null || _b === void 0 ? void 0 : _b.body, " ").concat(theme === null || theme === void 0 ? void 0 : theme.fontSizes[2], " ").concat((_c = theme === null || theme === void 0 ? void 0 : theme.fonts) === null || _c === void 0 ? void 0 : _c.body);
}, function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = theme === null || theme === void 0 ? void 0 : theme.lineHeights) === null || _b === void 0 ? void 0 : _b.body;
}, function (_a) {
    var _b;
    var theme = _a.theme;
    return "".concat((_b = theme === null || theme === void 0 ? void 0 : theme.letterSpacings) === null || _b === void 0 ? void 0 : _b.body);
});
var templateObject_1$1;

var ADAThemeProvider = function (_a) {
    var children = _a.children;
    return (React__default.createElement(ThemeProvider, { theme: theme },
        children,
        " "));
};

var theme = {
    name: 'dojocat',
    breakpoints: [
        '48rem',
        '58rem',
        '64rem',
        '80rem',
        '90rem', // 1440px
    ],
    colors: colors,
    sizes: {
        tooltip: '18rem',
        stepper: '48rem',
        tablet: '48rem',
        content: '58rem',
        header: '90rem',
        full: '100%',
        half: '50%',
        inputMaxWidth: '20.5rem',
        inputHeight: '3rem',
        radioHeight: '1.75rem',
        viewportWidth: '100vw',
        viewportHeight: '100vh',
        topNavigationHeight: '4rem',
        checkbox: '1.75rem',
        alertIcon: '1.5rem',
    },
    borderWidths: {
        input: '0.063rem',
        inputFocus: '0.125rem',
        radioChecked: '0.5rem', // form radio element border
    },
    radii: {
        input: '0.25rem',
        circle: '50%',
        card: '1.5rem',
    },
    space: [
        '0rem',
        '0.25rem',
        '0.5rem',
        '0.75rem',
        '1rem',
        '1.5rem',
        '2rem',
        '2.5rem',
        '3rem',
        '3.5rem',
        '4rem',
        '4.5rem', //  11  72px
    ],
    fonts: {
        display: 'Inter-Bold, serif',
        medium: 'Inter-SemiBold, sans-serif',
        regular: 'Inter-Regular, sans-serif',
        body: 'Inter-Regular, sans-serif',
        caption: 'Inter-Regular, sans-serif',
    },
    letterSpacings: {
        display: '-0.03em',
        medium: '-0.02em',
        regular: '-0.02em',
        body: '-0.02em',
        caption: '-0.02em',
    },
    fontWeights: {
        display: 700,
        medium: 600,
        regular: 400,
        body: 400,
        caption: 400,
    },
    lineHeights: {
        display: 1.21,
        medium: 1.21,
        regular: 1.21,
        body: 1.6,
        caption: 1.21,
    },
    fontSizes: [
        '0.75rem',
        '0.875rem',
        '1rem',
        '1.125rem',
        '1.25rem',
        '1.5rem',
        '2rem',
        '2.5rem',
        '3rem',
        '3.5rem',
        '4.0rem', //   10  64px
    ],
    text: textVariants,
    boxSelector: boxSelectorVariants,
    button: buttonVariants,
    card: cardVariants,
    alert: alertVariants,
    tag: tagVariants,
    form: {
        input: inputVariants,
        label: labelVariants,
        message: messageVariants,
        radio: radioVariants,
        dropdown: dropdownVariants,
        select: selectVariants,
    },
};
// Aliases
theme.fontSizes.header = theme.fontSizes[7];
theme.fontSizes.subheader = theme.fontSizes[5];
theme.fontSizes.body = theme.fontSizes[2];

var Button$1 = styled.button(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  padding: 0;\n  border: none;\n  background-color: transparent;\n  cursor: pointer;\n"], ["\n  padding: 0;\n  border: none;\n  background-color: transparent;\n  cursor: pointer;\n"])));
var AccordionText = function (_a) {
    var children = _a.children, limitLines = _a.limitLines, other = __rest(_a, ["children", "limitLines"]);
    return (React__default.createElement(Text, __assign({ variant: "body", color: "neutralsTextGray", fontSize: 2, lineHeight: "body", overflow: "hidden", display: "-webkit-box", sx: {
            '-webkit-line-clamp': limitLines || 'unset',
            '-webkit-box-orient': 'vertical',
        } }, other), children));
};
function Accordion(_a) {
    var title = _a.title, _b = _a.defaultExpanded, defaultExpanded = _b === void 0 ? false : _b, _c = _a.previewContent, previewContent = _c === void 0 ? false : _c, containerProps = _a.containerProps, children = _a.children;
    var _d = useState(defaultExpanded), expanded = _d[0], setExpanded = _d[1];
    var onToggleClick = useCallback(function () { return setExpanded(!expanded); }, [expanded]);
    var iconName = expanded ? 'minus' : 'plus';
    return (React__default.createElement(Flex, __assign({ flexDirection: "column" }, containerProps),
        React__default.createElement(Button$1, { onClick: onToggleClick },
            React__default.createElement(Flex, { justifyContent: "space-between", alignItems: "center", py: 4, borderBottom: "1px solid", borderColor: "neutralsBackgroundGray" },
                React__default.createElement(Text, { fontSize: 3, variant: "medium", color: "brandDarkBlue", mr: 3, textAlign: "left" }, title),
                React__default.createElement(Icon, { name: iconName, size: "1.5rem", color: "brandDarkBlue" }))),
        expanded && !previewContent && (React__default.createElement(Box, { mt: 4 },
            React__default.createElement(AccordionText, null, children))),
        previewContent && (React__default.createElement(Box, { mt: 4 },
            React__default.createElement(AccordionText, { limitLines: expanded ? undefined : '3' }, children)))));
}
var templateObject_1;

var Alert = function (_a) {
    var defaultIcon = _a.defaultIcon, customIcon = _a.customIcon, text = _a.text, subText = _a.subText, _b = _a.onClose, onClose = _b === void 0 ? null : _b, _c = _a.onClick, onClick = _c === void 0 ? null : _c, _d = _a.variant, variant = _d === void 0 ? 'info' : _d, props = __rest(_a, ["defaultIcon", "customIcon", "text", "subText", "onClose", "onClick", "variant"]);
    var _e = alertVariants[variant] || {}, variantIcon = _e.variantIcon, variantIconColor = _e.variantIconColor;
    var displayIcon = defaultIcon ? variantIcon : customIcon;
    return (React__default.createElement(Flex, __assign({ tx: "alert", variant: variant, justifyContent: "space-between", onClick: onClick }, props),
        React__default.createElement(Flex, { flexDirection: "column" },
            React__default.createElement(Flex, { alignItems: "flex-start" },
                displayIcon && (React__default.createElement(Box, { mr: 3, minWidth: "1.25rem" },
                    React__default.createElement(Icon, { name: displayIcon, color: variantIconColor, size: "alertIcon" }))),
                text && (React__default.createElement(Flex, { flexDirection: "column" },
                    React__default.createElement(Text, { variant: subText ? 'medium' : 'body' }, text),
                    subText && (React__default.createElement(Text, { color: "neutralsTextGray", variant: "body", mt: 1, fontSize: 1 }, subText)))))),
        onClose && (React__default.createElement(Box, { flexShrink: 0 },
            React__default.createElement(Icon, { name: "x", size: "alertIcon", cursor: "pointer", color: "neutralsTextGray", iconClick: function (event) {
                    event.stopPropagation();
                    onClose(event);
                } })))));
};
Alert.displayName = 'Alert';

var sx = function (props) { return css(props.sx)(props.theme); };
var base = function (props) { return css(props.__css)(props.theme); };
var variantFn = function (_a) {
    var _b = _a.theme, theme = _b === void 0 ? {} : _b, _c = _a.variant, variant = _c === void 0 ? 'default' : _c, _d = _a.tx, tx = _d === void 0 ? 'variants' : _d;
    return css(get(theme, "".concat(tx, ".").concat(variant), get(theme, variant)))(theme);
};
var Box = styled.div.withConfig({
    shouldForwardProp: function (prop) { return shouldForwardProp(prop); },
})({
    boxSizing: 'border-box',
    margin: 0,
    minWidth: 0,
}, base, variantFn, sx, compose(space, layout, color, typography, flexbox, background, borders, borderWidth, borderRadius, position));

var StyledBoxSelector = styled(Box)(variant({}));
var BoxSelector = forwardRef(function (_a, ref) {
    var onClick = _a.onClick, props = __rest(_a, ["onClick"]);
    var disabledListRow = {
        labelColor: 'neutralsBorderGray',
        subLabelColor: 'neutralsBorderGray',
        iconColor: 'neutralsBorderGray',
        iconContainerColor: 'neutralsGrayWash',
    };
    return (React__default.createElement(StyledBoxSelector, __assign({ as: "button", tx: "boxSelector", variant: "singleSelect", ref: ref, borderWidth: "input", borderStyle: "solid", borderRadius: "0.5rem", textAlign: "start", p: 4, width: "full", onClick: onClick }, props),
        React__default.createElement(ListRow, __assign({}, props, { variant: "actionIcon" }, (props.disabled ? disabledListRow : {})))));
});
BoxSelector.displayName = 'BoxSelector';

var StyledButton = styled(Box)(variant({
    prop: 'size',
    variants: {
        large: {
            height: '3.5rem',
            py: 4,
            px: 10,
            fontSize: 2,
        },
        small: {
            height: '2.5rem',
            py: 2,
            px: 6,
            fontSize: 1,
        },
    },
}));
var ButtonIcon = function (_a) {
    var _b = _a.variant, variant = _b === void 0 ? 'primary' : _b, _c = _a.size, size = _c === void 0 ? 'large' : _c, icon = _a.icon, isLoading = _a.isLoading;
    var buttonIconColorVariants = {
        primary: 'neutralsWhite',
        secondary: 'brandMidnightBlue',
        tertiary: 'utilityLinkBlue',
        white: 'neutralsTextBlack',
        secondaryWhite: 'neutralsWhite',
        circle: 'neutralsTextBlack',
    };
    var buttonIconSizeVariants = {
        large: '1.375rem',
        small: '1.125rem',
    };
    var color = buttonIconColorVariants[variant];
    var iconSize = buttonIconSizeVariants[size];
    return (React__default.createElement(Icon, { name: icon, color: color, size: iconSize, visibility: isLoading ? 'hidden' : 'visible' }));
};
var Button = forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'primary' : _b, _c = _a.size, size = _c === void 0 ? 'large' : _c, icon = _a.icon, _d = _a.circleIconColor, circleIconColor = _d === void 0 ? 'brandMidnightBlue' : _d, _e = _a.iconPosition, iconPosition = _e === void 0 ? 'left' : _e, text = _a.text, textProps = _a.textProps, _f = _a.fullWidth, fullWidth = _f === void 0 ? false : _f, lottieComponent = _a.lottieComponent, isLoading = _a.isLoading, children = _a.children, color = _a.color, _g = _a.borderColor, borderColor = _g === void 0 ? 'unset' : _g, _h = _a.backgroundColor, backgroundColor = _h === void 0 ? 'neutralsWhite' : _h, _j = _a.borderStyle, borderStyle = _j === void 0 ? 'none' : _j, borderWidth = _a.borderWidth, props = __rest(_a, ["variant", "size", "icon", "circleIconColor", "iconPosition", "text", "textProps", "fullWidth", "lottieComponent", "isLoading", "children", "color", "borderColor", "backgroundColor", "borderStyle", "borderWidth"]);
    var buttonType = props.as ? undefined : 'button';
    var comp = lottieComponent || (React__default.createElement(Lottie, { name: variant === 'primary' ? 'WhiteLoader' : 'MidnightBlueLoader' }));
    return (React__default.createElement(React__default.Fragment, null, variant === 'circle' ? (React__default.createElement(Flex, __assign({ as: "button", type: buttonType, ref: ref, sx: {
            cursor: 'pointer',
            display: 'inline-block',
            textDecoration: 'none',
        }, justifyContent: "center", height: "3rem", width: "3rem", minWidth: "2.7rem", backgroundColor: backgroundColor, alignItems: "center", borderRadius: "50%", borderStyle: borderStyle, borderWidth: borderWidth || 0, borderColor: borderColor, "aria-disabled": (props === null || props === void 0 ? void 0 : props.disabled) || isLoading }, props),
        icon && (React__default.createElement(Icon, { size: size === 'small' ? '1.5rem' : '2rem', name: icon, color: circleIconColor })),
        !icon && text && (React__default.createElement(Text, { variant: "medium", color: color }, text)))) : (React__default.createElement(StyledButton, __assign({ as: "button", type: buttonType, ref: ref, variant: variant, size: size, tx: "button", position: "relative", sx: {
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'inline-block',
            textDecoration: 'none',
            pointerEvents: isLoading ? 'none' : 'all',
        }, alignItems: "center", px: 9, borderRadius: "15.5rem", iconPosition: iconPosition, width: fullWidth && '100%', "aria-disabled": (props === null || props === void 0 ? void 0 : props.disabled) || isLoading }, props),
        React__default.createElement(Flex, { flexDirection: iconPosition === 'left' ? 'row-reverse' : undefined, justifyContent: "center", alignItems: "center" },
            React__default.createElement(Text, __assign({ paddingLeft: icon && iconPosition === 'left' ? 3 : 0, paddingRight: icon && iconPosition === 'right' ? 3 : 0, flexShrink: 0, variant: "medium" }, textProps, { sx: {
                    visibility: isLoading ? 'hidden' : 'visible',
                } }), children || text),
            isLoading && (React__default.createElement(Box, { position: "absolute", justifyContent: "center", alignItems: "center", zIndex: "3", width: "1.5rem", height: "1.5rem" }, comp)),
            icon && (React__default.createElement(ButtonIcon, { size: size, variant: variant, icon: icon, iconPosition: iconPosition, isLoading: isLoading })))))));
});
Button.displayName = 'Button';

var Card = function (props) {
    return (React__default.createElement(Box, __assign({ tx: "card", p: 6, justifyItems: "center", alignItems: "center", variant: "primary", __css: {
            boxShadow: '1px 0px 16px 0px #0000001A',
        } }, props)));
};

var Checkbox = forwardRef(function (props, ref) {
    return (React__default.createElement(Flex, __assign({ as: "label", position: "relative", display: "block", p: 4, width: "100%", alignItems: "center", justifyContent: "left", __css: {
            cursor: 'pointer',
            ':active': {
                backgroundColor: 'overlayBlack15',
            },
        } }, props.containerProps),
        React__default.createElement(Box, __assign({ as: "input", type: "checkbox", ref: ref, borderColor: "neutralsBorderGray", borderStyle: "solid", borderWidth: "input", height: "checkbox", width: "checkbox", minWidth: "checkbox", backgroundColor: "neutralsWhite", borderRadius: 2, __css: {
                appearance: 'none',
                ':checked': {
                    backgroundColor: 'brandMidnightBlue',
                    borderColor: 'brandMidnightBlue',
                    borderRadius: 2,
                },
                ':disabled': {
                    backgroundColor: 'neutralsBackgroundGray',
                },
                ':checked:disabled': {
                    backgroundColor: 'neutralsBorderGray',
                    borderColor: 'neutralsBorderGray',
                },
                ':checked + div': {
                    display: 'flex',
                },
                ':not(:checked) + div': {
                    display: 'none',
                },
            } }, props)),
        React__default.createElement(Flex, { position: "absolute", height: "checkbox", width: "checkbox", alignItems: "center", justifyContent: "center" },
            React__default.createElement(Icon, { name: props.icon || 'check', size: "1.5rem", color: "neutralsWhite" })),
        React__default.createElement(Text, { ml: 4, fontSize: 1, color: props.disabled ? 'neutralsPlaceholderGray' : 'neutralsTextBlack', fontWeight: "display" }, props.text)));
});
Checkbox.displayName = 'Checkbox';

var Datepicker = forwardRef(function (props, ref) { return (React__default.createElement(Input, __assign({ ref: ref, id: props.id || props.name, type: "date", icon: "calendar", max: "2999-12-31", pr: 4, sx: {
        ':focus': {
            outline: 'none',
        },
        '::-webkit-datetime-edit': {
            color: (props === null || props === void 0 ? void 0 : props.value) ? 'neutralsTextBlack' : 'neutralsPlaceholderGray',
        },
        '::-webkit-calendar-picker-indicator': {
            opacity: 0,
        },
        ':disabled': {
            color: 'neutralsPlaceholderGray',
            borderColor: 'neutralsLightGray',
            bg: 'neutralsBackgroundGray',
        },
        ':disabled::-webkit-datetime-edit': {
            color: 'neutralsBorderGray',
        },
    } }, props))); });
Datepicker.displayName = 'Datepicker';
var DatepickerGroup = forwardRef(function (_a, ref) {
    var label = _a.label, message = _a.message, messageProps = _a.messageProps, containerProps = _a.containerProps, color = _a.color, error = _a.error, props = __rest(_a, ["label", "message", "messageProps", "containerProps", "color", "error"]);
    var variantType = error ? 'error' : color;
    var messageText = error || message;
    return (React__default.createElement(Flex, __assign({ flexDirection: "column", p: 4, width: "100%" }, containerProps),
        React__default.createElement(Label$1, { variant: props.disabled ? 'disabled' : 'default', mb: 2, htmlFor: props.id || props.name }, label),
        React__default.createElement(Datepicker, __assign({ ref: ref, mb: 1, maxWidth: "none", width: "100%", color: color }, props)),
        messageText && (React__default.createElement(Message, __assign({ variant: variantType, mt: 1 }, messageProps), messageText))));
});
DatepickerGroup.displayName = 'DatepickerGroup';

var Dropdown = forwardRef(function (_a, ref) {
    var options = _a.options, placeholder = _a.placeholder, props = __rest(_a, ["options", "placeholder"]);
    return (React__default.createElement(Box, __assign({ as: "select", ref: ref, id: props.id || props.name, "aria-label": props.id || props.name, tx: "form.dropdown", variant: "default", width: "full", maxWidth: "inputMaxWidth", height: "inputHeight", py: 3, pl: 4, pr: 7, borderWidth: "input", borderStyle: "solid", borderRadius: "input", color: "neutralsTextBlack" }, props, { __css: {
            appearance: 'none',
            background: "".concat(colors.neutralsWhite, " ").concat(getSVGUrl({
                name: 'drop-down',
                color: 'neutralsTextGray',
            }), " no-repeat calc(100% - 0.55rem) center"),
            backgroundSize: '1.5rem',
            outline: 'none',
            ':disabled': {
                borderColor: 'neutralsBorderGray',
                bg: 'neutralsBackgroundGray',
            },
            textOverflow: 'ellipsis',
        } }),
        placeholder && React__default.createElement("option", { value: "" }, placeholder), options === null || options === void 0 ? void 0 :
        options.map(function (option, index) { return (React__default.createElement(Box, { as: "option", key: "option-".concat(option.value, "-").concat(index), value: option.value }, option.label)); })));
});
Dropdown.displayName = 'Dropdown';
var DropdownGroup = forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'default' : _b, label = _a.label, message = _a.message, disabled = _a.disabled, messageProps = _a.messageProps, containerProps = _a.containerProps, error = _a.error, props = __rest(_a, ["variant", "label", "message", "disabled", "messageProps", "containerProps", "error"]);
    var variantType = error ? 'error' : variant;
    var messageText = error || message;
    return (React__default.createElement(Flex, __assign({ flexDirection: "column", p: 4, width: "100%" }, containerProps),
        React__default.createElement(Label$1, { variant: disabled ? 'disabled' : 'default', mb: 2, htmlFor: props.id || props.name }, label),
        React__default.createElement(Dropdown, __assign({ ref: ref, disabled: disabled, maxWidth: "none", width: "100%" }, props)),
        messageText && (React__default.createElement(Message, __assign({ variant: variantType, mt: 1 }, messageProps), messageText))));
});
DropdownGroup.displayName = 'DropdownGroup';

var Flex = styled(Box)({
    display: 'flex',
});

var _path$3v;

function _extends$3y() { _extends$3y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3y.apply(this, arguments); }

function SvgActivity(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3y({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3v || (_path$3v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.025 2a1 1 0 01.939.732l4.123 14.845 1.964-5.893A1 1 0 0117 11h4a1 1 0 110 2h-3.28l-2.771 8.316a1 1 0 01-1.912-.048L8.913 6.423l-1.964 5.893A1 1 0 016 13H3a1 1 0 110-2h2.28l2.77-8.316A1 1 0 019.025 2z",
    clipRule: "evenodd"
  })));
}

var _path$3u;

function _extends$3x() { _extends$3x = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3x.apply(this, arguments); }

function SvgAirplane(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3x({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3u || (_path$3u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.766 3C8.212 3 7.251 4.696 8.051 6.029L9.234 8H8.066L6.857 5.986A1 1 0 006 5.5H4.344c-1.824 0-3.034 1.891-2.27 3.548l1.482 3.21A3 3 0 006.28 14h1.3l-1.068 2.991C5.815 18.945 7.263 21 9.338 21h.633a3 3 0 002.441-1.256L16.515 14H19a3 3 0 000-6h-1.98l-2.604-3.72A3 3 0 0011.96 3H9.766zm6.722 7H19a1 1 0 110 2h-3a1 1 0 00-.814.419l-4.402 6.162a1 1 0 01-.813.419h-.633a1 1 0 01-.942-1.336l1.546-4.328A1 1 0 009 12H6.28a1 1 0 01-.908-.58L3.89 8.21a.5.5 0 01.454-.71h1.09l1.208 2.014A1 1 0 007.5 10h8.988zm-1.909-2l-1.801-2.573a1 1 0 00-.82-.427H9.767l1.8 3h3.013z",
    clipRule: "evenodd"
  })));
}

var _path$3t, _path2$2i, _path3$L;

function _extends$3w() { _extends$3w = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3w.apply(this, arguments); }

function SvgAlertCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3w({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3t || (_path$3t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 20a8 8 0 110-16 8 8 0 010 16zM2 12c0 5.523 4.477 10 10 10s10-4.477 10-10S17.523 2 12 2 2 6.477 2 12z",
    clipRule: "evenodd"
  })), _path2$2i || (_path2$2i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 13a1 1 0 01-1-1V7a1 1 0 112 0v5a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })), _path3$L || (_path3$L = /*#__PURE__*/React.createElement("path", {
    d: "M13 16a1 1 0 11-2 0 1 1 0 012 0z"
  })));
}

var _path$3s, _path2$2h;

function _extends$3v() { _extends$3v = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3v.apply(this, arguments); }

function SvgAlertOctagon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3v({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3s || (_path$3s = /*#__PURE__*/React.createElement("path", {
    d: "M12 13a1 1 0 01-1-1V7a1 1 0 112 0v5a1 1 0 01-1 1zm0 4a1 1 0 100-2 1 1 0 000 2z"
  })), _path2$2h || (_path2$2h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.98 2.879A3 3 0 019.1 2h5.8a3 3 0 012.12.879l4.101 4.1A3 3 0 0122 9.101v5.798a3 3 0 01-.879 2.122l-4.1 4.1a3 3 0 01-2.122.879H9.101a3 3 0 01-2.122-.879l-4.1-4.1A3 3 0 012 14.899V9.101a3 3 0 01.879-2.122l4.1-4.1zM9.1 4a1 1 0 00-.707.293l-4.1 4.1A1 1 0 004 9.101v5.798a1 1 0 00.293.708l4.1 4.1a1 1 0 00.708.293h5.798a1 1 0 00.708-.293l4.1-4.1A1 1 0 0020 14.9V9.1a1 1 0 00-.293-.707l-4.1-4.1A1 1 0 0014.9 4H9.1z",
    clipRule: "evenodd"
  })));
}

var _path$3r, _path2$2g, _path3$K;

function _extends$3u() { _extends$3u = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3u.apply(this, arguments); }

function SvgAlertTriangle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3u({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3r || (_path$3r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.674 4.883c1.48-2.51 5.163-2.51 6.643 0l6.15 10.435C22.974 17.872 21.07 21 18.148 21H5.845c-2.923 0-4.827-3.128-3.322-5.682L8.674 4.883zM13.595 5.9c-.707-1.199-2.492-1.199-3.199 0l-6.15 10.435C3.563 17.491 4.393 19 5.844 19h12.301c1.451 0 2.282-1.51 1.6-2.666L13.595 5.9z",
    clipRule: "evenodd"
  })), _path2$2g || (_path2$2g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.996 14a1 1 0 01-1-1v-3a1 1 0 112 0v3a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })), _path3$K || (_path3$K = /*#__PURE__*/React.createElement("path", {
    d: "M12.995 16a1 1 0 11-2 0 1 1 0 012 0z"
  })));
}

var _path$3q, _path2$2f, _path3$J;

function _extends$3t() { _extends$3t = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3t.apply(this, arguments); }

function SvgArrowDownCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3t({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3q || (_path$3q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.707 11.293a1 1 0 00-1.414 0L12 14.586l-3.293-3.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l4-4a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })), _path2$2f || (_path2$2f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 17a1 1 0 001-1V8a1 1 0 10-2 0v8a1 1 0 001 1z",
    clipRule: "evenodd"
  })), _path3$J || (_path3$J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 20a8 8 0 110-16 8 8 0 010 16zM2 12c0 5.523 4.477 10 10 10s10-4.477 10-10S17.523 2 12 2 2 6.477 2 12z",
    clipRule: "evenodd"
  })));
}

var _path$3p, _path2$2e;

function _extends$3s() { _extends$3s = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3s.apply(this, arguments); }

function SvgArrowDownLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3s({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3p || (_path$3p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 6a1 1 0 011 1v9h9a1 1 0 110 2H7a1 1 0 01-1-1V7a1 1 0 011-1z",
    clipRule: "evenodd"
  })), _path2$2e || (_path2$2e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17.707 6.293a1 1 0 010 1.414l-9.9 9.9a1 1 0 01-1.414-1.415l9.9-9.9a1 1 0 011.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$3o, _path2$2d;

function _extends$3r() { _extends$3r = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3r.apply(this, arguments); }

function SvgArrowDownRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3r({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3o || (_path$3o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 17a1 1 0 011-1h9V7a1 1 0 112 0v10a1 1 0 01-1 1H7a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path2$2d || (_path2$2d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 6.293a1 1 0 011.414 0l9.9 9.9a1 1 0 01-1.415 1.414l-9.9-9.9a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$3n;

function _extends$3q() { _extends$3q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3q.apply(this, arguments); }

function SvgArrowDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3q({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3n || (_path$3n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 19.707a1 1 0 001.414 0l7-7a1 1 0 00-1.414-1.414L13 16.586V5a1 1 0 10-2 0v11.586l-5.293-5.293a1 1 0 00-1.414 1.414l7 7z",
    clipRule: "evenodd"
  })));
}

var _path$3m, _path2$2c, _path3$I;

function _extends$3p() { _extends$3p = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3p.apply(this, arguments); }

function SvgArrowLeftCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3p({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3m || (_path$3m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.707 16.707a1 1 0 000-1.414L9.414 12l3.293-3.293a1 1 0 00-1.414-1.414l-4 4a1 1 0 000 1.414l4 4a1 1 0 001.414 0z",
    clipRule: "evenodd"
  })), _path2$2c || (_path2$2c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 12a1 1 0 001 1h8a1 1 0 100-2H8a1 1 0 00-1 1z",
    clipRule: "evenodd"
  })), _path3$I || (_path3$I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 12a8 8 0 1116 0 8 8 0 01-16 0zm8-10C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z",
    clipRule: "evenodd"
  })));
}

var _path$3l;

function _extends$3o() { _extends$3o = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3o.apply(this, arguments); }

function SvgArrowLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3o({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3l || (_path$3l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.293 11.293a1 1 0 000 1.414l7 7a1 1 0 101.414-1.414L7.414 13H19a1 1 0 100-2H7.414l5.293-5.293a1 1 0 00-1.414-1.414l-7 7z",
    clipRule: "evenodd"
  })));
}

var _path$3k, _path2$2b, _path3$H;

function _extends$3n() { _extends$3n = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3n.apply(this, arguments); }

function SvgArrowRightCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3n({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3k || (_path$3k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 16.707a1 1 0 010-1.414L14.586 12l-3.293-3.293a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z",
    clipRule: "evenodd"
  })), _path2$2b || (_path2$2b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17 12a1 1 0 01-1 1H8a1 1 0 110-2h8a1 1 0 011 1z",
    clipRule: "evenodd"
  })), _path3$H || (_path3$H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20 12a8 8 0 10-16 0 8 8 0 0016 0zM12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2z",
    clipRule: "evenodd"
  })));
}

var _path$3j;

function _extends$3m() { _extends$3m = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3m.apply(this, arguments); }

function SvgArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3m({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3j || (_path$3j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.707 12.707a1 1 0 000-1.414l-7-7a1 1 0 10-1.414 1.414L16.586 11H5a1 1 0 100 2h11.586l-5.293 5.293a1 1 0 101.414 1.414l7-7z",
    clipRule: "evenodd"
  })));
}

var _path$3i, _path2$2a, _path3$G;

function _extends$3l() { _extends$3l = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3l.apply(this, arguments); }

function SvgArrowUpCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3l({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3i || (_path$3i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.707 12.707a1 1 0 01-1.414 0L12 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })), _path2$2a || (_path2$2a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 7a1 1 0 011 1v8a1 1 0 11-2 0V8a1 1 0 011-1z",
    clipRule: "evenodd"
  })), _path3$G || (_path3$G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })));
}

var _path$3h, _path2$29;

function _extends$3k() { _extends$3k = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3k.apply(this, arguments); }

function SvgArrowUpLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3k({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3h || (_path$3h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18 7a1 1 0 01-1 1H8v9a1 1 0 11-2 0V7a1 1 0 011-1h10a1 1 0 011 1z",
    clipRule: "evenodd"
  })), _path2$29 || (_path2$29 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17.707 17.707a1 1 0 01-1.414 0l-9.9-9.9a1 1 0 111.415-1.414l9.9 9.9a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })));
}

var _path$3g, _path2$28;

function _extends$3j() { _extends$3j = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3j.apply(this, arguments); }

function SvgArrowUpRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3j({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3g || (_path$3g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17 18a1 1 0 01-1-1V8H7a1 1 0 110-2h10a1 1 0 011 1v10a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })), _path2$28 || (_path2$28 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 17.707a1 1 0 010-1.414l9.9-9.9a1 1 0 111.414 1.415l-9.9 9.9a1 1 0 01-1.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$3f;

function _extends$3i() { _extends$3i = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3i.apply(this, arguments); }

function SvgArrowUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3i({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3f || (_path$3f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 4.293a1 1 0 011.414 0l7 7a1 1 0 01-1.414 1.414L13 7.414V19a1 1 0 11-2 0V7.414l-5.293 5.293a1 1 0 01-1.414-1.414l7-7z",
    clipRule: "evenodd"
  })));
}

var _path$3e, _path2$27;

function _extends$3h() { _extends$3h = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3h.apply(this, arguments); }

function SvgAward(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3h({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3e || (_path$3e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a5 5 0 100 10 5 5 0 000-10zM5 9a7 7 0 1114 0A7 7 0 015 9z",
    clipRule: "evenodd"
  })), _path2$27 || (_path2$27 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.133 13.201a1 1 0 011.105.884l.756 6.805a1 1 0 01-1.441 1.004L12 20.118l-3.553 1.776a1 1 0 01-1.44-1.004l.74-6.67a1 1 0 111.988.22l-.538 4.844 2.356-1.178a1 1 0 01.894 0l2.356 1.177-.553-4.978a1 1 0 01.883-1.104z",
    clipRule: "evenodd"
  })));
}

var _path$3d;

function _extends$3g() { _extends$3g = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3g.apply(this, arguments); }

function SvgBarChart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3g({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3d || (_path$3d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 3a1 1 0 011-1h6a1 1 0 011 1v3h5a1 1 0 011 1v14a1 1 0 01-1 1H3a1 1 0 01-1-1V11a1 1 0 011-1h5V3zm6 17V4h-4v16h4zm2 0h4V8h-4v12zm-8-8v8H4v-8h4z",
    clipRule: "evenodd"
  })));
}

var _path$3c;

function _extends$3f() { _extends$3f = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3f.apply(this, arguments); }

function SvgBarChart2(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3f({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3c || (_path$3c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14 3a1 1 0 011-1h6a1 1 0 011 1v18a1 1 0 01-1 1H3a1 1 0 01-1-1V11a1 1 0 011-1h5V7a1 1 0 011-1h5V3zm2 17h4V4h-4v16zM14 8h-4v12h4V8zm-6 4H4v8h4v-8z",
    clipRule: "evenodd"
  })));
}

var _path$3b;

function _extends$3e() { _extends$3e = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3e.apply(this, arguments); }

function SvgBaseball(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3e({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3b || (_path$3b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12zm2.202-1.792a8.004 8.004 0 005.329 9.404c.08-.538.122-1.067.122-1.583h-.016a1 1 0 01-.198-1.983 7.978 7.978 0 00-.47-1.44A1 1 0 017.941 12.9a8.01 8.01 0 00-1.066-1.106 1 1 0 01-1.692-1.061l.032-.058a10.55 10.55 0 00-1.014-.466zM4.89 8.33c.45.176.884.375 1.302.598a1 1 0 011.686 1.07l-.012.02a9.99 9.99 0 011.8 1.84l.018-.01a1 1 0 111.03 1.714l-.018.011a9.97 9.97 0 01.774 2.424l.08-.002a1 1 0 01.103 1.997c.002.657-.052 1.323-.156 1.992a8.001 8.001 0 007.744-4.579 12.683 12.683 0 01-1.912-.824 1 1 0 01-1.714-1.028l.039-.07a9.986 9.986 0 01-1.777-1.822l-.018.012a1 1 0 11-1.03-1.715l.018-.011a9.99 9.99 0 01-.78-2.453h-.024a1 1 0 01-.153-1.99c0-.497.03-1 .092-1.504A8 8 0 004.89 8.33zm9.077-4.086c-.05.412-.076.819-.077 1.218h.066a1 1 0 01.143 1.991c.113.505.271.992.476 1.46a1 1 0 011.026 1.708 7.97 7.97 0 001.05 1.091 1 1 0 011.657 1.107l-.007.014c.487.258 1.007.482 1.558.67a8.003 8.003 0 00-5.892-9.26z",
    clipRule: "evenodd"
  })));
}

var _path$3a;

function _extends$3d() { _extends$3d = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3d.apply(this, arguments); }

function SvgBasketball(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3d({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3a || (_path$3a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 11.913C2.05 6.39 6.565 1.953 12.087 2c5.523.048 9.96 4.565 9.913 10.087-.049 5.523-4.565 9.96-10.087 9.913C6.39 21.95 1.953 17.434 2 11.913zm5.332-6.41A7.958 7.958 0 0111 4.061v5.965c-.713.038-1.414.118-2.1.235a12.937 12.937 0 00-1.568-4.76zm-.393 5.211A10.938 10.938 0 005.82 6.918 7.966 7.966 0 004 11.87c.926-.468 1.91-.856 2.938-1.156zM4.244 13.97c.08-.02.16-.051.236-.093a14.99 14.99 0 012.482-1.076 10.942 10.942 0 01-1.215 4.19 7.975 7.975 0 01-1.503-3.021zm4.75-1.693A16.16 16.16 0 0111 12.03v7.908a7.957 7.957 0 01-3.763-1.51 12.953 12.953 0 001.757-6.15zM13 12.03v7.908a7.96 7.96 0 003.763-1.51 12.952 12.952 0 01-1.757-6.15A16.144 16.144 0 0013 12.03zm4.038.77c.123 1.504.549 2.92 1.215 4.19a7.97 7.97 0 001.503-3.02 1 1 0 01-.236-.093 14.987 14.987 0 00-2.482-1.076zm2.961-.93a17.148 17.148 0 00-2.938-1.156 10.94 10.94 0 011.118-3.796 7.966 7.966 0 011.82 4.952zm-4.9-1.608A18.216 18.216 0 0013 10.027V4.062c1.359.17 2.61.679 3.668 1.44a12.939 12.939 0 00-1.568 4.76z",
    clipRule: "evenodd"
  })));
}

var _path$39;

function _extends$3c() { _extends$3c = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3c.apply(this, arguments); }

function SvgBed(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3c({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$39 || (_path$39 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 2a3 3 0 00-3 3v6.17A3.001 3.001 0 002 14v7a1 1 0 102 0v-2h16v2a1 1 0 102 0v-7a3.001 3.001 0 00-2-2.83V5a3 3 0 00-3-3H7zm13 15v-3a1 1 0 00-1-1H5a1 1 0 00-1 1v3h16zM7 11H6V5a1 1 0 011-1h10a1 1 0 011 1v6h-1v-1a3 3 0 00-3-3h-4a3 3 0 00-3 3v1zm8-1v1H9v-1a1 1 0 011-1h4a1 1 0 011 1z",
    clipRule: "evenodd"
  })));
}

var _path$38, _path2$26;

function _extends$3b() { _extends$3b = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3b.apply(this, arguments); }

function SvgBellOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3b({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$38 || (_path$38 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4c-.767 0-1.48.215-2.087.587a1 1 0 01-1.045-1.705A6 6 0 0118 8c0 .868.224 2.26.699 3.684a1 1 0 01-1.898.632C16.276 10.74 16 9.132 16 8a4 4 0 00-4-4zM7.256 6.124a1 1 0 01.807 1.161A4.03 4.03 0 008 8c0 1.132-.276 2.74-.801 4.316-.3.901-.696 1.834-1.198 2.684H15.5a1 1 0 110 2H4a1 1 0 01-.707-1.707c.844-.844 1.531-2.178 2.008-3.61C5.776 10.26 6 8.869 6 8c0-.364.033-.721.095-1.069a1 1 0 011.161-.807zm1.841 13.131a1 1 0 011.412.078 1.996 1.996 0 002.982 0 1 1 0 111.49 1.334A3.993 3.993 0 0112 22a3.993 3.993 0 01-2.981-1.333 1 1 0 01.078-1.412z",
    clipRule: "evenodd"
  })), _path2$26 || (_path2$26 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414l-18-18a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$37;

function _extends$3a() { _extends$3a = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3a.apply(this, arguments); }

function SvgBell(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3a({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$37 || (_path$37 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a4 4 0 00-4 4c0 1.132-.276 2.74-.801 4.316-.3.901-.696 1.834-1.198 2.684H18a14.116 14.116 0 01-1.198-2.684C16.276 10.74 16 9.132 16 8a4 4 0 00-4-4zM6 8a6 6 0 0112 0c0 .868.224 2.26.699 3.684.477 1.431 1.164 2.765 2.008 3.609A1 1 0 0120 17H4a1 1 0 01-.707-1.707c.844-.844 1.531-2.178 2.008-3.61C5.776 10.26 6 8.869 6 8zm3.097 11.255a1 1 0 011.412.078 1.996 1.996 0 002.982 0 1 1 0 111.49 1.334A3.993 3.993 0 0112 22a3.993 3.993 0 01-2.981-1.333 1 1 0 01.078-1.412z",
    clipRule: "evenodd"
  })));
}

var _path$36, _path2$25, _path3$F;

function _extends$39() { _extends$39 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$39.apply(this, arguments); }

function SvgBookOpen(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$39({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$36 || (_path$36 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 3a1 1 0 00-1-1h-5a5 5 0 00-5 5v.5a1 1 0 102 0V7a3 3 0 013-3h4v13h-5c-.925 0-1.685.234-2.29.637a3.632 3.632 0 00-1.23 1.416 4.684 4.684 0 00-.48 1.922V21h1-1a1 1 0 002 .007V21l.003-.058a2.688 2.688 0 01.266-.995c.129-.256.305-.482.551-.646.24-.16.605-.301 1.18-.301h6a1 1 0 001-1V3z",
    clipRule: "evenodd"
  })), _path2$25 || (_path2$25 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 3a1 1 0 011-1h5a5 5 0 015 5v.5a1 1 0 11-2 0V7a3 3 0 00-3-3H4v13h5c.925 0 1.685.234 2.29.637.597.398.983.922 1.23 1.416a4.684 4.684 0 01.48 1.922V21h-1 1a1 1 0 01-2 .007V21l-.003-.058a2.688 2.688 0 00-.266-.995 1.635 1.635 0 00-.551-.646C9.94 19.14 9.575 19 9 19H3a1 1 0 01-1-1V3z",
    clipRule: "evenodd"
  })), _path3$F || (_path3$F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 6a1 1 0 011 1v12.5a1 1 0 11-2 0V7a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$35;

function _extends$38() { _extends$38 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$38.apply(this, arguments); }

function SvgBookmark(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$38({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$35 || (_path$35 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 5a3 3 0 013-3h10a3 3 0 013 3v16a1 1 0 01-1.65.76L12 16.316 5.65 21.76A1 1 0 014 21V5zm3-1a1 1 0 00-1 1v13.826l5.35-4.585a1 1 0 011.3 0L18 18.826V5a1 1 0 00-1-1H7z",
    clipRule: "evenodd"
  })));
}

var _path$34;

function _extends$37() { _extends$37 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$37.apply(this, arguments); }

function SvgBriefcase(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$37({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$34 || (_path$34 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 2a3 3 0 00-3 3v1H5a3 3 0 00-3 3v10a3 3 0 003 3h14a3 3 0 003-3V9a3 3 0 00-3-3h-2V5a3 3 0 00-3-3h-4zm5 4V5a1 1 0 00-1-1h-4a1 1 0 00-1 1v1h6zM5 8a1 1 0 00-1 1v10a1 1 0 001 1h14a1 1 0 001-1V9a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })));
}

var _path$33, _path2$24;

function _extends$36() { _extends$36 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$36.apply(this, arguments); }

function SvgCalendar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$36({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$33 || (_path$33 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 7a3 3 0 013-3h14a3 3 0 013 3v12a3 3 0 01-3 3H5a3 3 0 01-3-3V7zm3-1a1 1 0 00-1 1v12a1 1 0 001 1h14a1 1 0 001-1V7a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })), _path2$24 || (_path2$24 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 2a1 1 0 011 1v4a1 1 0 01-2 0V3a1 1 0 011-1zm8 0a1 1 0 011 1v4a1 1 0 11-2 0V3a1 1 0 011-1zM2 11a1 1 0 011-1h18a1 1 0 110 2H3a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$32, _path2$23, _path3$E;

function _extends$35() { _extends$35 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$35.apply(this, arguments); }

function SvgCameraOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$35({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$32 || (_path$32 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.5 4a1 1 0 011-1h4.059a3 3 0 012.845 2.051l.089.265a1 1 0 00.949.684H19a3 3 0 013 3v6.25a1 1 0 11-2 0V9a1 1 0 00-1-1h-1.558a3 3 0 01-2.847-2.051l-.088-.265A1 1 0 0013.56 5H9.5a1 1 0 01-1-1zM5 8a1 1 0 00-1 1v9a1 1 0 001 1h15a1 1 0 110 2H5a3 3 0 01-3-3V9a3 3 0 013-3h2a1 1 0 010 2H5z",
    clipRule: "evenodd"
  })), _path2$23 || (_path2$23 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.174 9.946a1 1 0 01-.28 1.387 2 2 0 102.804 2.723 1 1 0 011.698 1.058 4 4 0 11-5.608-5.447 1 1 0 011.386.28z",
    clipRule: "evenodd"
  })), _path3$E || (_path3$E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414l-18-18a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$31, _path2$22;

function _extends$34() { _extends$34 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$34.apply(this, arguments); }

function SvgCamera(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$34({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$31 || (_path$31 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.441 5a1 1 0 00-.948.684l-.088.265A3 3 0 016.558 8H5a1 1 0 00-1 1v9a1 1 0 001 1h14a1 1 0 001-1V9a1 1 0 00-1-1h-1.558a3 3 0 01-2.847-2.051l-.088-.265A1 1 0 0013.56 5h-3.117zm-2.846.051A3 3 0 0110.441 3h3.117a3 3 0 012.846 2.051l.089.265a1 1 0 00.949.684H19a3 3 0 013 3v9a3 3 0 01-3 3H5a3 3 0 01-3-3V9a3 3 0 013-3h1.558a1 1 0 00.95-.684l.087-.265z",
    clipRule: "evenodd"
  })), _path2$22 || (_path2$22 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 11a2 2 0 100 4 2 2 0 000-4zm-4 2a4 4 0 118 0 4 4 0 01-8 0z",
    clipRule: "evenodd"
  })));
}

var _path$30, _path2$21;

function _extends$33() { _extends$33 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$33.apply(this, arguments); }

function SvgCar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$33({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$30 || (_path$30 = /*#__PURE__*/React.createElement("path", {
    d: "M6.5 16a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm11 0a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
  })), _path2$21 || (_path2$21 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.038 3.725A1 1 0 015 3h14a1 1 0 01.962.725l1.993 6.978c.03.094.045.194.045.297v10a1 1 0 01-1 1h-2a1 1 0 01-1-1v-2H6v2a1 1 0 01-1 1H3a1 1 0 01-1-1V11a1 1 0 01.045-.297l1.993-6.978zM4 17h16v-5H4v5zM18.246 5l1.428 5H4.326l1.428-5h12.492z",
    clipRule: "evenodd"
  })));
}

var _path$2$, _path2$20;

function _extends$32() { _extends$32 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$32.apply(this, arguments); }

function SvgCdcInFrame(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$32({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2$ || (_path$2$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 9a3 3 0 013-3h8a3 3 0 013 3v6a3 3 0 01-3 3H8a3 3 0 01-3-3V9zm3-1a1 1 0 00-1 1v6a1 1 0 001 1h8a1 1 0 001-1V9a1 1 0 00-1-1H8z",
    clipRule: "evenodd"
  })), _path2$20 || (_path2$20 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 10a1 1 0 011-1h6a1 1 0 110 2H9a1 1 0 01-1-1zm0 3a1 1 0 011-1h5a1 1 0 110 2H9a1 1 0 01-1-1zm-5 3a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 110 2H5a3 3 0 01-3-3v-2a1 1 0 011-1zm0-8a1 1 0 001-1V5a1 1 0 011-1h2a1 1 0 000-2H5a3 3 0 00-3 3v2a1 1 0 001 1zm18 8a1 1 0 00-1 1v2a1 1 0 01-1 1h-2a1 1 0 100 2h2a3 3 0 003-3v-2a1 1 0 00-1-1zm0-8a1 1 0 01-1-1V5a1 1 0 00-1-1h-2a1 1 0 110-2h2a3 3 0 013 3v2a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$2_, _path2$1$, _path3$D;

function _extends$31() { _extends$31 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$31.apply(this, arguments); }

function SvgCdcReview(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$31({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2_ || (_path$2_ = /*#__PURE__*/React.createElement("path", {
    d: "M2 7a3 3 0 013-3h14a3 3 0 013 3v4.25a1 1 0 11-2 0V7a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h5.25a1 1 0 110 2H5a3 3 0 01-3-3V7z"
  })), _path2$1$ || (_path2$1$ = /*#__PURE__*/React.createElement("path", {
    d: "M6 13a1 1 0 011-1h4a1 1 0 110 2H7a1 1 0 01-1-1zm1-5a1 1 0 000 2h10a1 1 0 100-2H7z"
  })), _path3$D || (_path3$D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13 17a4 4 0 117.446 2.032l1.261 1.26a1 1 0 01-1.414 1.415l-1.261-1.26A4 4 0 0113 17zm4-2a2 2 0 100 4 2 2 0 000-4z",
    clipRule: "evenodd"
  })));
}

var _path$2Z;

function _extends$30() { _extends$30 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$30.apply(this, arguments); }

function SvgCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$30({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2Z || (_path$2Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.707 5.293a1 1 0 00-1.414 0L9 15.586l-4.293-4.293a1 1 0 00-1.414 1.414l5 5a1 1 0 001.414 0l11-11a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2Y, _path2$1_;

function _extends$2$() { _extends$2$ = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2$.apply(this, arguments); }

function SvgCheckmarkCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2$({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2Y || (_path$2Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.707 9.293a1 1 0 00-1.414 0L11 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })), _path2$1_ || (_path2$1_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })));
}

var _path$2X;

function _extends$2_() { _extends$2_ = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2_.apply(this, arguments); }

function SvgChevronDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2_({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2X || (_path$2X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.293 8.293a1 1 0 011.414 0L12 14.586l6.293-6.293a1 1 0 111.414 1.414l-7 7a1 1 0 01-1.414 0l-7-7a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2W;

function _extends$2Z() { _extends$2Z = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2Z.apply(this, arguments); }

function SvgChevronLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2W || (_path$2W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.707 4.293a1 1 0 010 1.414L9.414 12l6.293 6.293a1 1 0 01-1.414 1.414l-7-7a1 1 0 010-1.414l7-7a1 1 0 011.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$2V;

function _extends$2Y() { _extends$2Y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2Y.apply(this, arguments); }

function SvgChevronRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2V || (_path$2V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.293 19.707a1 1 0 010-1.414L14.586 12 8.293 5.707a1 1 0 111.414-1.414l7 7a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$2U;

function _extends$2X() { _extends$2X = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2X.apply(this, arguments); }

function SvgChevronUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2X({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2U || (_path$2U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.707 15.707a1 1 0 01-1.414 0L12 9.414l-6.293 6.293a1 1 0 01-1.414-1.414l7-7a1 1 0 011.414 0l7 7a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2T, _path2$1Z;

function _extends$2W() { _extends$2W = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2W.apply(this, arguments); }

function SvgChevronsDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2W({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2T || (_path$2T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.707 4.293a1 1 0 00-1.414 0L12 9.586 6.707 4.293a1 1 0 10-1.414 1.414l6 6a1 1 0 001.414 0l6-6a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })), _path2$1Z || (_path2$1Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.707 12.293a1 1 0 00-1.414 0L12 17.586l-5.293-5.293a1 1 0 00-1.414 1.414l6 6a1 1 0 001.414 0l6-6a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2S, _path2$1Y;

function _extends$2V() { _extends$2V = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2V.apply(this, arguments); }

function SvgChevronsLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2V({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2S || (_path$2S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.707 18.707a1 1 0 000-1.414L14.414 12l5.293-5.293a1 1 0 00-1.414-1.414l-6 6a1 1 0 000 1.414l6 6a1 1 0 001.414 0z",
    clipRule: "evenodd"
  })), _path2$1Y || (_path2$1Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.707 18.707a1 1 0 000-1.414L6.414 12l5.293-5.293a1 1 0 00-1.414-1.414l-6 6a1 1 0 000 1.414l6 6a1 1 0 001.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$2R, _path2$1X;

function _extends$2U() { _extends$2U = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2U.apply(this, arguments); }

function SvgChevronsRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2U({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2R || (_path$2R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.293 5.293a1 1 0 000 1.414L9.586 12l-5.293 5.293a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414l-6-6a1 1 0 00-1.414 0z",
    clipRule: "evenodd"
  })), _path2$1X || (_path2$1X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.293 5.293a1 1 0 000 1.414L17.586 12l-5.293 5.293a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414l-6-6a1 1 0 00-1.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$2Q, _path2$1W;

function _extends$2T() { _extends$2T = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2T.apply(this, arguments); }

function SvgChevronsUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2T({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2Q || (_path$2Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.707 19.707a1 1 0 01-1.414 0L12 14.414l-5.293 5.293a1 1 0 01-1.414-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })), _path2$1W || (_path2$1W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.707 11.707a1 1 0 01-1.414 0L12 6.414l-5.293 5.293a1 1 0 01-1.414-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2P, _path2$1V;

function _extends$2S() { _extends$2S = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2S.apply(this, arguments); }

function SvgCircleDelete(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2S({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2P || (_path$2P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$1V || (_path2$1V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.707 5.293a1 1 0 00-1.414 0l-12 12a1 1 0 101.414 1.414l12-12a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2O;

function _extends$2R() { _extends$2R = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2R.apply(this, arguments); }

function SvgCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2R({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2O || (_path$2O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })));
}

var _path$2N, _path2$1U;

function _extends$2Q() { _extends$2Q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2Q.apply(this, arguments); }

function SvgClipboard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2N || (_path$2N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 6a1 1 0 00-1 1v12a1 1 0 001 1h10a1 1 0 001-1V7a1 1 0 00-1-1h-1.5a1 1 0 110-2H17a3 3 0 013 3v12a3 3 0 01-3 3H7a3 3 0 01-3-3V7a3 3 0 013-3h1.5a1 1 0 010 2H7z",
    clipRule: "evenodd"
  })), _path2$1U || (_path2$1U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 4v2h4V4h-4zm-2-.5A1.5 1.5 0 019.5 2h5A1.5 1.5 0 0116 3.5v3A1.5 1.5 0 0114.5 8h-5A1.5 1.5 0 018 6.5v-3z",
    clipRule: "evenodd"
  })));
}

var _path$2M, _path2$1T;

function _extends$2P() { _extends$2P = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2P.apply(this, arguments); }

function SvgClock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2P({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2M || (_path$2M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$1T || (_path2$1T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 6a1 1 0 011 1v4.586l2.707 2.707a1 1 0 01-1.414 1.414l-3-3A1 1 0 0111 12V7a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$2L, _path2$1S;

function _extends$2O() { _extends$2O = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2O.apply(this, arguments); }

function SvgCloudDrizzle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2O({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2L || (_path$2L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 4a6 6 0 00-4.659 9.782 1 1 0 11-1.551 1.261 8 8 0 1113.973-6.985 5.001 5.001 0 012.755 8.495 1 1 0 01-1.407-1.421A3 3 0 0017 10c-.544 0-.99-.4-1.07-.919A6.002 6.002 0 0010 4z",
    clipRule: "evenodd"
  })), _path2$1S || (_path2$1S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 14a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zm-4-2a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zm8 0a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zm-4 7a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zm-4-2a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zm8 0a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$2K, _path2$1R;

function _extends$2N() { _extends$2N = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2N.apply(this, arguments); }

function SvgCloudLightning(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2N({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2K || (_path$2K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 4a6 6 0 00-4.659 9.782 1 1 0 11-1.551 1.261 8 8 0 1113.973-6.985 5.001 5.001 0 012.755 8.495 1 1 0 01-1.407-1.421A3 3 0 0017 10c-.544 0-.99-.4-1.07-.919A6.002 6.002 0 0010 4z",
    clipRule: "evenodd"
  })), _path2$1R || (_path2$1R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.447 8.106a1 1 0 01.447 1.341L10.618 14H14a1 1 0 01.894 1.447l-3 6a1 1 0 11-1.788-.894L12.382 16H9a1 1 0 01-.894-1.447l3-6a1 1 0 011.341-.447z",
    clipRule: "evenodd"
  })));
}

var _path$2J, _path2$1Q;

function _extends$2M() { _extends$2M = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2M.apply(this, arguments); }

function SvgCloudOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2M({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2J || (_path$2J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.731 4.935a1 1 0 011.097-.893 8.006 8.006 0 016.934 6.016 5.001 5.001 0 014.233 5.175 1 1 0 01-1.998-.092L20 15a3 3 0 00-3-3c-.544 0-.99-.4-1.07-.919a6.004 6.004 0 00-5.307-5.05 1 1 0 01-.892-1.096zm-2.773.68A1 1 0 016.68 7 6 6 0 0010 18h7c.437 0 .85-.093 1.222-.26a1 1 0 11.816 1.827A4.986 4.986 0 0117 20h-7A8 8 0 015.572 5.337a1 1 0 011.386.278z",
    clipRule: "evenodd"
  })), _path2$1Q || (_path2$1Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414l-18-18a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2I, _path2$1P;

function _extends$2L() { _extends$2L = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2L.apply(this, arguments); }

function SvgCloudRain(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2L({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2I || (_path$2I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 4a6 6 0 00-4.659 9.782 1 1 0 11-1.551 1.261 8 8 0 1113.973-6.985 5.001 5.001 0 012.755 8.495 1 1 0 01-1.407-1.421A3 3 0 0017 10c-.544 0-.99-.4-1.07-.919A6.002 6.002 0 0010 4z",
    clipRule: "evenodd"
  })), _path2$1P || (_path2$1P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 12a1 1 0 011 1v6a1 1 0 11-2 0v-6a1 1 0 011-1zm4 2a1 1 0 011 1v6a1 1 0 11-2 0v-6a1 1 0 011-1zm4-2a1 1 0 011 1v6a1 1 0 11-2 0v-6a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$2H, _path2$1O;

function _extends$2K() { _extends$2K = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2K.apply(this, arguments); }

function SvgCloudSnow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2K({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2H || (_path$2H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 4a6 6 0 00-4.659 9.782 1 1 0 11-1.551 1.261 8 8 0 1113.973-6.985 5.001 5.001 0 012.755 8.495 1 1 0 01-1.407-1.421A3 3 0 0017 10c-.544 0-.99-.4-1.07-.919A6.002 6.002 0 0010 4z",
    clipRule: "evenodd"
  })), _path2$1O || (_path2$1O = /*#__PURE__*/React.createElement("path", {
    d: "M9 15a1 1 0 11-2 0 1 1 0 012 0zm4 2a1 1 0 11-2 0 1 1 0 012 0zm4-2a1 1 0 11-2 0 1 1 0 012 0zm-8 4a1 1 0 11-2 0 1 1 0 012 0zm4 2a1 1 0 11-2 0 1 1 0 012 0zm4-2a1 1 0 11-2 0 1 1 0 012 0z"
  })));
}

var _path$2G;

function _extends$2J() { _extends$2J = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2J.apply(this, arguments); }

function SvgCloud(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2J({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2G || (_path$2G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 6a6 6 0 100 12h7a3 3 0 100-6c-.544 0-.99-.4-1.07-.919A6.002 6.002 0 0010 6zm7.762 4.058A5.001 5.001 0 0117 20h-7a8 8 0 117.762-9.942z",
    clipRule: "evenodd"
  })));
}

var _path$2F, _path2$1N;

function _extends$2I() { _extends$2I = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2I.apply(this, arguments); }

function SvgColumns(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2I({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2F || (_path$2F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 013-3h14a3 3 0 013 3v14a3 3 0 01-3 3H5a3 3 0 01-3-3V5zm3-1a1 1 0 00-1 1v14a1 1 0 001 1h14a1 1 0 001-1V5a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })), _path2$1N || (_path2$1N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 011 1v18a1 1 0 11-2 0V3a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$2E, _path2$1M;

function _extends$2H() { _extends$2H = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2H.apply(this, arguments); }

function SvgCompass(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2H({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2E || (_path$2E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$1M || (_path2$1M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.707 7.293a1 1 0 01.234 1.046l-2.122 5.88a1 1 0 01-.6.6l-5.88 2.122a1 1 0 01-1.28-1.28l2.122-5.88a1 1 0 01.6-.6l5.88-2.122a1 1 0 011.046.234zm-5.805 3.61l-1.239 3.434 3.434-1.24 1.24-3.434-3.434 1.24z",
    clipRule: "evenodd"
  })));
}

var _path$2D, _path2$1L;

function _extends$2G() { _extends$2G = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2G.apply(this, arguments); }

function SvgCopy(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2G({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2D || (_path$2D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 11a3 3 0 013-3h8a3 3 0 013 3v8a3 3 0 01-3 3h-8a3 3 0 01-3-3v-8zm3-1a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1v-8a1 1 0 00-1-1h-8z",
    clipRule: "evenodd"
  })), _path2$1L || (_path2$1L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 013-3h8a3 3 0 013 3 1 1 0 11-2 0 1 1 0 00-1-1H5a1 1 0 00-1 1v8a1 1 0 001 1 1 1 0 110 2 3 3 0 01-3-3V5z",
    clipRule: "evenodd"
  })));
}

var _path$2C, _path2$1K;

function _extends$2F() { _extends$2F = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2F.apply(this, arguments); }

function SvgCornerDownLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2F({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2C || (_path$2C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.707 10.293a1 1 0 010 1.414L4.414 16l4.293 4.293a1 1 0 11-1.414 1.414l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 0z",
    clipRule: "evenodd"
  })), _path2$1K || (_path2$1K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 16a1 1 0 011-1h14a3 3 0 003-3V3a1 1 0 112 0v9a5 5 0 01-5 5H3a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$2B, _path2$1J;

function _extends$2E() { _extends$2E = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2E.apply(this, arguments); }

function SvgCornerDownRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2E({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2B || (_path$2B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.293 10.293a1 1 0 000 1.414L19.586 16l-4.293 4.293a1 1 0 001.414 1.414l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 0z",
    clipRule: "evenodd"
  })), _path2$1J || (_path2$1J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 16a1 1 0 00-1-1H7a3 3 0 01-3-3V3a1 1 0 10-2 0v9a5 5 0 005 5h14a1 1 0 001-1z",
    clipRule: "evenodd"
  })));
}

var _path$2A, _path2$1I;

function _extends$2D() { _extends$2D = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2D.apply(this, arguments); }

function SvgCornerLeftDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2D({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2A || (_path$2A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.707 15.293a1 1 0 00-1.414 0L8 19.586l-4.293-4.293a1 1 0 00-1.414 1.414l5 5a1 1 0 001.414 0l5-5a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })), _path2$1I || (_path2$1I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 22a1 1 0 001-1V7a3 3 0 013-3h9a1 1 0 100-2h-9a5 5 0 00-5 5v14a1 1 0 001 1z",
    clipRule: "evenodd"
  })));
}

var _path$2z, _path2$1H;

function _extends$2C() { _extends$2C = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2C.apply(this, arguments); }

function SvgCornerLeftUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2C({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2z || (_path$2z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.707 8.707a1 1 0 01-1.414 0L8 4.414 3.707 8.707a1 1 0 11-1.414-1.414l5-5a1 1 0 011.414 0l5 5a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })), _path2$1H || (_path2$1H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 2a1 1 0 011 1v14a3 3 0 003 3h9a1 1 0 110 2h-9a5 5 0 01-5-5V3a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$2y, _path2$1G;

function _extends$2B() { _extends$2B = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2B.apply(this, arguments); }

function SvgCornerRightDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2B({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2y || (_path$2y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.293 15.293a1 1 0 011.414 0L16 19.586l4.293-4.293a1 1 0 011.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })), _path2$1G || (_path2$1G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16 22a1 1 0 01-1-1V7a3 3 0 00-3-3H3a1 1 0 010-2h9a5 5 0 015 5v14a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$2x, _path2$1F;

function _extends$2A() { _extends$2A = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2A.apply(this, arguments); }

function SvgCornerRightUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2A({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2x || (_path$2x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.293 8.707a1 1 0 001.414 0L16 4.414l4.293 4.293a1 1 0 101.414-1.414l-5-5a1 1 0 00-1.414 0l-5 5a1 1 0 000 1.414z",
    clipRule: "evenodd"
  })), _path2$1F || (_path2$1F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16 2a1 1 0 00-1 1v14a3 3 0 01-3 3H3a1 1 0 100 2h9a5 5 0 005-5V3a1 1 0 00-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$2w, _path2$1E;

function _extends$2z() { _extends$2z = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2z.apply(this, arguments); }

function SvgCornerUpLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2z({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2w || (_path$2w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.707 13.707a1 1 0 000-1.414L4.414 8l4.293-4.293a1 1 0 00-1.414-1.414l-5 5a1 1 0 000 1.414l5 5a1 1 0 001.414 0z",
    clipRule: "evenodd"
  })), _path2$1E || (_path2$1E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 8a1 1 0 001 1h14a3 3 0 013 3v9a1 1 0 102 0v-9a5 5 0 00-5-5H3a1 1 0 00-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$2v, _path2$1D;

function _extends$2y() { _extends$2y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2y.apply(this, arguments); }

function SvgCornerUpRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2y({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2v || (_path$2v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.293 13.707a1 1 0 010-1.414L19.586 8l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z",
    clipRule: "evenodd"
  })), _path2$1D || (_path2$1D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 8a1 1 0 01-1 1H7a3 3 0 00-3 3v9a1 1 0 11-2 0v-9a5 5 0 015-5h14a1 1 0 011 1z",
    clipRule: "evenodd"
  })));
}

var _path$2u;

function _extends$2x() { _extends$2x = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2x.apply(this, arguments); }

function SvgCreditCard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2x({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2u || (_path$2u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a3 3 0 00-3 3v10a3 3 0 003 3h14a3 3 0 003-3V7a3 3 0 00-3-3H5zm15 4V7a1 1 0 00-1-1H5a1 1 0 00-1 1v1h16zM4 10h16v7a1 1 0 01-1 1H5a1 1 0 01-1-1v-7z",
    clipRule: "evenodd"
  })));
}

var _path$2t, _path2$1C;

function _extends$2w() { _extends$2w = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2w.apply(this, arguments); }

function SvgCrop(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2w({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2t || (_path$2t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18 22a1 1 0 01-1-1V8a1 1 0 00-1-1H3a1 1 0 110-2h13a3 3 0 013 3v13a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })), _path2$1C || (_path2$1C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 2a1 1 0 011 1v13a1 1 0 001 1h13a1 1 0 110 2H8a3 3 0 01-3-3V3a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$2s, _path2$1B;

function _extends$2v() { _extends$2v = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2v.apply(this, arguments); }

function SvgCrosshair(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2v({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2s || (_path$2s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$1B || (_path2$1B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 16a1 1 0 00-1 1v4a1 1 0 102 0v-4a1 1 0 00-1-1zm0-14a1 1 0 00-1 1v4a1 1 0 102 0V3a1 1 0 00-1-1zm4 10a1 1 0 001 1h4a1 1 0 100-2h-4a1 1 0 00-1 1zM2 12a1 1 0 001 1h4a1 1 0 100-2H3a1 1 0 00-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$2r, _path2$1A, _path3$C;

function _extends$2u() { _extends$2u = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2u.apply(this, arguments); }

function SvgDelete(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2u({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2r || (_path$2r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.27 11.265a1 1 0 000 1.47l5.413 5a1 1 0 00.678.265H19a1 1 0 001-1V7a1 1 0 00-1-1h-7.639a1 1 0 00-.678.265l-5.414 5zm-1.358 2.939a3 3 0 010-4.408l5.414-5A3 3 0 0111.36 4H19a3 3 0 013 3v10a3 3 0 01-3 3h-7.639a3 3 0 01-2.035-.796l-5.414-5z",
    clipRule: "evenodd"
  })), _path2$1A || (_path2$1A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.414 14.828a1 1 0 010-1.414l4.243-4.242a1 1 0 111.414 1.414l-4.243 4.242a1 1 0 01-1.414 0z",
    clipRule: "evenodd"
  })), _path3$C || (_path3$C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17.071 14.828a1 1 0 01-1.414 0l-4.243-4.242a1 1 0 011.414-1.414l4.243 4.242a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2q;

function _extends$2t() { _extends$2t = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2t.apply(this, arguments); }

function SvgDollarSign(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2t({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2q || (_path$2q = /*#__PURE__*/React.createElement("path", {
    d: "M13 3a1 1 0 10-2 0v1.062c-.854.11-1.694.38-2.394.9C7.582 5.724 7 6.915 7 8.492c0 .785.15 1.471.458 2.059.31.591.749 1.026 1.242 1.351.908.597 2.065.85 3 1.055l.086.019c1.05.23 1.85.419 2.415.79.257.168.442.362.57.606.13.247.229.6.229 1.128 0 1.049-.355 1.604-.798 1.934C13.71 17.8 12.955 18 12 18c-.953 0-1.708-.2-2.202-.569C9.355 17.102 9 16.544 9 15.5a1 1 0 10-2 0c0 1.577.579 2.77 1.602 3.534.7.523 1.542.794 2.398.904V21a1 1 0 102 0v-1.061c.855-.11 1.696-.38 2.397-.901C16.422 18.274 17 17.08 17 15.5c0-.785-.15-1.47-.458-2.057a3.609 3.609 0 00-1.243-1.35c-.906-.594-2.061-.847-2.995-1.051l-.09-.02c-1.05-.23-1.85-.42-2.414-.79a1.616 1.616 0 01-.57-.609C9.1 9.375 9 9.02 9 8.491c0-1.04.355-1.593.8-1.924C10.293 6.2 11.05 6 12 6c.951 0 1.707.199 2.201.566.444.33.799.884.799 1.925a1 1 0 102 0c0-1.577-.582-2.77-1.606-3.53-.7-.52-1.54-.79-2.394-.9V3z"
  })));
}

var _path$2p, _path2$1z, _path3$B;

function _extends$2s() { _extends$2s = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2s.apply(this, arguments); }

function SvgDownloadCloud(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2s({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2p || (_path$2p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 4a6 6 0 00-4.659 9.782 1 1 0 11-1.551 1.261 8 8 0 1113.973-6.985 5.001 5.001 0 012.755 8.495 1 1 0 01-1.407-1.421A3 3 0 0017 10c-.544 0-.99-.4-1.07-.919A6.002 6.002 0 0010 4z",
    clipRule: "evenodd"
  })), _path2$1z || (_path2$1z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.707 16.293a1 1 0 00-1.414 0L12 19.586l-3.293-3.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l4-4a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })), _path3$B || (_path3$B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 22a1 1 0 001-1v-9a1 1 0 00-2 0v9a1 1 0 001 1z",
    clipRule: "evenodd"
  })));
}

var _path$2o, _path2$1y, _path3$A;

function _extends$2r() { _extends$2r = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2r.apply(this, arguments); }

function SvgDownload(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2r({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2o || (_path$2o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.707 9.293a1 1 0 00-1.414 0L12 14.586 6.707 9.293a1 1 0 10-1.414 1.414l6 6a1 1 0 001.414 0l6-6a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })), _path2$1y || (_path2$1y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 17a1 1 0 001-1V3a1 1 0 10-2 0v13a1 1 0 001 1z",
    clipRule: "evenodd"
  })), _path3$A || (_path3$A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 15a1 1 0 011 1v3a1 1 0 001 1h14a1 1 0 001-1v-3a1 1 0 112 0v3a3 3 0 01-3 3H5a3 3 0 01-3-3v-3a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$2n;

function _extends$2q() { _extends$2q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2q.apply(this, arguments); }

function SvgDropDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2q({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2n || (_path$2n = /*#__PURE__*/React.createElement("path", {
    d: "M7.207 9a.5.5 0 00-.353.854l4.792 4.792a.5.5 0 00.708 0l4.792-4.792A.5.5 0 0016.793 9H7.207z"
  })));
}

var _path$2m;

function _extends$2p() { _extends$2p = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2p.apply(this, arguments); }

function SvgDropLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2p({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2m || (_path$2m = /*#__PURE__*/React.createElement("path", {
    d: "M15 16.793a.5.5 0 01-.854.353l-4.792-4.792a.5.5 0 010-.708l4.792-4.792a.5.5 0 01.854.353v9.586z"
  })));
}

var _path$2l;

function _extends$2o() { _extends$2o = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2o.apply(this, arguments); }

function SvgDropRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2o({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2l || (_path$2l = /*#__PURE__*/React.createElement("path", {
    d: "M9 16.793a.5.5 0 00.854.353l4.792-4.792a.5.5 0 000-.708L9.854 6.854A.5.5 0 009 7.207v9.586z"
  })));
}

var _path$2k;

function _extends$2n() { _extends$2n = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2n.apply(this, arguments); }

function SvgDropUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2n({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2k || (_path$2k = /*#__PURE__*/React.createElement("path", {
    d: "M7.207 15a.5.5 0 01-.353-.854l4.792-4.792a.5.5 0 01.708 0l4.792 4.792a.5.5 0 01-.353.854H7.207z"
  })));
}

var _path$2j;

function _extends$2m() { _extends$2m = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2m.apply(this, arguments); }

function SvgDroplet(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2m({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2j || (_path$2j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.65 2.24L12 3l-.65-.76a1 1 0 011.3 0zM12 4.353A32.25 32.25 0 009.272 7.26c-.848 1.03-1.677 2.189-2.29 3.371C6.363 11.823 6 12.972 6 14a6 6 0 0012 0c0-1.028-.364-2.177-.982-3.368-.613-1.182-1.442-2.341-2.29-3.371A32.256 32.256 0 0012 4.35zm-.65-2.111L12 3l.65-.76.003.003.005.004.018.015a15.071 15.071 0 01.297.265 34.258 34.258 0 013.299 3.462c.902 1.095 1.823 2.374 2.522 3.723C19.489 11.052 20 12.528 20 14a8 8 0 11-16 0c0-1.472.511-2.948 1.206-4.288.7-1.35 1.62-2.628 2.522-3.723a34.26 34.26 0 013.596-3.727l.018-.015.005-.004.002-.002z",
    clipRule: "evenodd"
  })));
}

var _path$2i;

function _extends$2l() { _extends$2l = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2l.apply(this, arguments); }

function SvgEdit2(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2l({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2i || (_path$2i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.958 3.04a3.534 3.534 0 015.007 0 3.55 3.55 0 010 5.012L8.545 20.505a.998.998 0 01-.466.264L3.24 21.971a1 1 0 01-1.209-1.223l1.248-4.8a1 1 0 01.26-.455L15.958 3.039zm3.59 1.412a1.534 1.534 0 00-2.173 0L5.147 16.712l-.756 2.912 2.935-.729L19.548 6.64a1.55 1.55 0 000-2.187z",
    clipRule: "evenodd"
  })));
}

var _path$2h, _path2$1x;

function _extends$2k() { _extends$2k = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2k.apply(this, arguments); }

function SvgEdit(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2k({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2h || (_path$2h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.886 4.12a.99.99 0 00-1.403 0l-8.443 8.465-.356 1.74 1.757-.34 8.445-8.467a.99.99 0 000-1.399zm-2.819-1.413a2.99 2.99 0 114.235 4.223l-8.662 8.686a1 1 0 01-.519.276l-3.527.68a1 1 0 01-1.169-1.183l.716-3.498a1 1 0 01.271-.506l8.655-8.678z",
    clipRule: "evenodd"
  })), _path2$1x || (_path2$1x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 7a3 3 0 013-3h5.5a1 1 0 110 2H5a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1v-5.5a1 1 0 112 0V19a3 3 0 01-3 3H5a3 3 0 01-3-3V7z",
    clipRule: "evenodd"
  })));
}

var _path$2g, _path2$1w;

function _extends$2j() { _extends$2j = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2j.apply(this, arguments); }

function SvgExternalLink(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2j({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2g || (_path$2g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 7a1 1 0 00-1 1v11a1 1 0 001 1h11a1 1 0 001-1v-6a1 1 0 112 0v6a3 3 0 01-3 3H5a3 3 0 01-3-3V8a3 3 0 013-3h6a1 1 0 110 2H5zm16 3a1 1 0 01-1-1V4h-5a1 1 0 110-2h6a1 1 0 011 1v6a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })), _path2$1w || (_path2$1w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 12.707a1 1 0 010-1.414l8.9-8.9a1 1 0 111.414 1.415l-8.9 8.9a1 1 0 01-1.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$2f, _path2$1v, _path3$z;

function _extends$2i() { _extends$2i = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2i.apply(this, arguments); }

function SvgEyeOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2i({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2f || (_path$2f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 7c-.105 0-.208.002-.31.006a1 1 0 01-.08-1.998c.13-.005.26-.008.39-.008 3.359 0 5.848 1.684 7.457 3.293a14.75 14.75 0 011.813 2.215 12.695 12.695 0 01.61 1.016l.01.019.002.006.002.003L21 12l.894.447v.001l-.001.002-.002.004-.006.012-.02.039c-.017.032-.04.076-.072.132-.062.112-.152.27-.27.461-.237.383-.59.908-1.058 1.487a1 1 0 11-1.555-1.257c.407-.503.71-.956.911-1.281l.03-.047a12.67 12.67 0 00-1.807-2.293C16.65 8.317 14.64 7 11.999 7zm9 5l.894.447c.141-.281.14-.613 0-.895L21 12zM8.044 6.89a1 1 0 01-.31 1.38c-1.267.804-2.234 1.852-2.888 2.713-.304.401-.537.756-.696 1.017a12.679 12.679 0 001.807 2.293C7.348 15.683 9.36 17 12 17c1.597 0 2.955-.479 4.088-1.161a1 1 0 011.032 1.713C15.728 18.39 14.017 19 12 19c-3.359 0-5.848-1.684-7.457-3.293a14.672 14.672 0 01-1.813-2.215 12.714 12.714 0 01-.577-.954 6.22 6.22 0 01-.033-.062l-.01-.019-.002-.006-.002-.002v-.001L3 12l-.894-.448v-.002l.003-.003.005-.01.016-.032a10.322 10.322 0 01.276-.487c.19-.315.473-.75.847-1.244.746-.982 1.88-2.222 3.41-3.193a1 1 0 011.38.31zM3 12l-.894-.448c-.14.282-.14.614 0 .896L3 12z",
    clipRule: "evenodd"
  })), _path2$1v || (_path2$1v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.557 9.186a1 1 0 01.015 1.414 2 2 0 002.761 2.89 1 1 0 111.334 1.491A4 4 0 019.143 9.2a1 1 0 011.414-.014z",
    clipRule: "evenodd"
  })), _path3$z || (_path3$z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414l-18-18a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2e, _path2$1u;

function _extends$2h() { _extends$2h = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2h.apply(this, arguments); }

function SvgEye(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2h({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2e || (_path$2e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.15 12a12.679 12.679 0 001.807 2.293C7.348 15.683 9.36 17 12 17s4.652-1.316 6.043-2.707A12.679 12.679 0 0019.85 12a12.678 12.678 0 00-1.807-2.293C16.652 8.317 14.64 7 12 7S7.348 8.316 5.957 9.707A12.678 12.678 0 004.15 12zM21 12l.894-.448-.002-.003-.003-.006-.01-.019a12.695 12.695 0 00-.61-1.016 14.677 14.677 0 00-1.812-2.215C17.848 6.683 15.36 5 12 5S6.152 6.684 4.543 8.293a14.675 14.675 0 00-1.813 2.215 12.714 12.714 0 00-.577.954 6.22 6.22 0 00-.033.062l-.01.019-.002.006-.002.002v.001L3 12l-.894-.448c-.14.282-.141.614 0 .895L3 12l-.894.447v.002l.002.002.003.006.01.019a6.22 6.22 0 00.151.278c.104.182.257.436.458.738a14.672 14.672 0 001.813 2.215C6.152 17.317 8.64 19 12 19s5.848-1.684 7.457-3.293a14.674 14.674 0 001.813-2.215 12.695 12.695 0 00.61-1.016l.01-.019.002-.006.002-.003L21 12zm0 0l.894.448c.14-.282.14-.614 0-.896L21 12z",
    clipRule: "evenodd"
  })), _path2$1u || (_path2$1u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 10a2 2 0 100 4 2 2 0 000-4zm-4 2a4 4 0 118 0 4 4 0 01-8 0z",
    clipRule: "evenodd"
  })));
}

var _path$2d;

function _extends$2g() { _extends$2g = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2g.apply(this, arguments); }

function SvgFaceInFrame(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2g({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2d || (_path$2d = /*#__PURE__*/React.createElement("path", {
    d: "M4 7a1 1 0 01-2 0V5a3 3 0 013-3h2a1 1 0 010 2H5a1 1 0 00-1 1v2zm6 2a1 1 0 11-2 0 1 1 0 012 0zm-1.163 4.057a1 1 0 011.277.61 2.001 2.001 0 003.772 0 1 1 0 111.886.666 4.001 4.001 0 01-7.544 0 1 1 0 01.61-1.276zM15 10a1 1 0 100-2 1 1 0 000 2zM3 16a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 110 2H5a3 3 0 01-3-3v-2a1 1 0 011-1zm18 0a1 1 0 00-1 1v2a1 1 0 01-1 1h-2a1 1 0 100 2h2a3 3 0 003-3v-2a1 1 0 00-1-1zm-1-9a1 1 0 102 0V5a3 3 0 00-3-3h-2a1 1 0 100 2h2a1 1 0 011 1v2z"
  })));
}

var _path$2c, _path2$1t;

function _extends$2f() { _extends$2f = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2f.apply(this, arguments); }

function SvgFaceMask(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2f({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2c || (_path$2c = /*#__PURE__*/React.createElement("path", {
    d: "M13.49 7.596a1 1 0 010 1.414l-4.243 4.243a1 1 0 11-1.414-1.414l4.242-4.243a1 1 0 011.415 0zm2.828 2.829a1 1 0 010 1.414l-4.243 4.243a1 1 0 01-1.414-1.415l4.243-4.242a1 1 0 011.414 0z"
  })), _path2$1t || (_path2$1t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.485 2.293a1 1 0 011.415 0l.777.778a5.002 5.002 0 016.93 6.93l.07.07a2 2 0 010 2.829L12.9 20.677a2 2 0 01-2.829 0l-.07-.071a5.002 5.002 0 01-6.93-6.93l-.778-.777a1 1 0 010-1.415l9.192-9.192zm-6.95 12.849a3.001 3.001 0 004.001 4l-4.001-4zm-.12-2.95l7.777-7.778 7.072 7.071-7.779 7.779-7.07-7.072zm14.728-3.656a3.001 3.001 0 00-4.002-4.001l4.002 4.001z",
    clipRule: "evenodd"
  })));
}

var _path$2b, _path2$1s;

function _extends$2e() { _extends$2e = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2e.apply(this, arguments); }

function SvgFile(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2e({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2b || (_path$2b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 4a1 1 0 00-1 1v14a1 1 0 001 1h12a1 1 0 001-1V9.8a1 1 0 00-.276-.69l-4.571-4.8a1 1 0 00-.724-.31H6zM3 5a3 3 0 013-3h7.429a3 3 0 012.172.931l4.571 4.8A3 3 0 0121 9.8V19a3 3 0 01-3 3H6a3 3 0 01-3-3V5z",
    clipRule: "evenodd"
  })), _path2$1s || (_path2$1s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13 3a1 1 0 011 1v5h5a1 1 0 110 2h-6a1 1 0 01-1-1V4a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$2a;

function _extends$2d() { _extends$2d = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2d.apply(this, arguments); }

function SvgFilter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2d({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2a || (_path$2a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4a2 2 0 012-2h16a2 2 0 012 2v1.54a2 2 0 01-.698 1.519L15 12.459v6.737c0 1.57-1.728 2.528-3.06 1.696l-2-1.25A2 2 0 019 17.946V12.46L2.698 7.059A2 2 0 012 5.54V4zm18 0H4v1.54l6.302 5.401A2 2 0 0111 12.46v5.486l2 1.25V12.46a2 2 0 01.698-1.519L20 5.541V4z",
    clipRule: "evenodd"
  })));
}

var _path$29, _path2$1r;

function _extends$2c() { _extends$2c = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2c.apply(this, arguments); }

function SvgFingerprintOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2c({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$29 || (_path$29 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a6.48 6.48 0 00-2.417.464 1 1 0 11-.744-1.857A8.48 8.48 0 0112 2c.783 0 1.543.106 2.266.306a1 1 0 01-.532 1.928A6.506 6.506 0 0012 4zm3.7-.101a1 1 0 011.4-.2 8.489 8.489 0 013.4 6.801V14a1 1 0 11-2 0v-3.5a6.488 6.488 0 00-2.6-5.201 1 1 0 01-.2-1.4zM12 7c-.111 0-.221.005-.329.015a1 1 0 01-.185-1.991A5.5 5.5 0 0117.5 10.5a1 1 0 11-2 0A3.5 3.5 0 0012 7zm-6.476-.416A1 1 0 016.04 7.9a6.475 6.475 0 00-.54 2.6V14a1 1 0 11-2 0v-3.5c0-1.207.252-2.358.708-3.4a1 1 0 011.316-.516zm3.008.88a1 1 0 01.396 1.357A3.48 3.48 0 008.5 10.5v4.31a1 1 0 11-2 0V10.5c0-.955.244-1.855.674-2.64a1 1 0 011.358-.396zM10.5 10.5a1 1 0 011 1V15a5 5 0 00.212 1.441 1 1 0 01-1.915.576A6.996 6.996 0 019.5 15v-3.5a1 1 0 011-1zm3 2.5a1 1 0 011 1v1c0 .592.256 1.123.667 1.491a1 1 0 11-1.334 1.49A3.993 3.993 0 0112.5 15v-1a1 1 0 011-1zm-5.832 3.793a1 1 0 011.275.61 7.794 7.794 0 001.367 2.407l.458.55a1 1 0 11-1.536 1.28l-.458-.55a9.81 9.81 0 01-1.717-3.022 1 1 0 01.61-1.275zM12.1 19.2a1 1 0 011.4-.2l1.6 1.2a1 1 0 01-1.2 1.6l-1.6-1.2a1 1 0 01-.2-1.4z",
    clipRule: "evenodd"
  })), _path2$1r || (_path2$1r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414l-18-18a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$28;

function _extends$2b() { _extends$2b = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2b.apply(this, arguments); }

function SvgFingerprint(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2b({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$28 || (_path$28 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a6.472 6.472 0 00-4.333 1.655 1 1 0 11-1.334-1.49A8.472 8.472 0 0112 2c.783 0 1.543.106 2.266.306a1 1 0 01-.532 1.928A6.506 6.506 0 0012 4zm3.7-.101a1 1 0 011.4-.2 8.489 8.489 0 013.4 6.801V15a3.999 3.999 0 01-2 3.464 1 1 0 01-1-1.731c.6-.347 1-.994 1-1.733v-4.5a6.488 6.488 0 00-2.6-5.201 1 1 0 01-.2-1.4zM12 7a3.5 3.5 0 00-3.5 3.5v4.31a1 1 0 11-2 0V10.5a5.5 5.5 0 1111 0 1 1 0 11-2 0A3.5 3.5 0 0012 7zm-6.476-.416A1 1 0 016.04 7.9a6.475 6.475 0 00-.54 2.6V14a1 1 0 11-2 0v-3.5c0-1.207.252-2.358.708-3.4a1 1 0 011.316-.516zM11 9a1 1 0 011-1 2.5 2.5 0 012.5 2.5V15c0 .592.256 1.123.667 1.491a1 1 0 11-1.334 1.49A3.993 3.993 0 0112.5 15v-4.5a.5.5 0 00-.5-.5 1 1 0 01-1-1zm-.5 1.5a1 1 0 011 1V15a5 5 0 00.212 1.441 1 1 0 01-1.915.576A6.996 6.996 0 019.5 15v-3.5a1 1 0 011-1zm6 1.75a1 1 0 011 1V15a1 1 0 11-2 0v-1.75a1 1 0 011-1zm-8.832 4.543a1 1 0 011.275.61 7.794 7.794 0 001.367 2.407l.458.55a1 1 0 11-1.536 1.28l-.458-.55a9.81 9.81 0 01-1.717-3.022 1 1 0 01.61-1.275zM12.1 19.2a1 1 0 011.4-.2l1.6 1.2a1 1 0 01-1.2 1.6l-1.6-1.2a1 1 0 01-.2-1.4z",
    clipRule: "evenodd"
  })));
}

var _path$27, _path2$1q;

function _extends$2a() { _extends$2a = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2a.apply(this, arguments); }

function SvgFlag(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2a({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$27 || (_path$27 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3.903l-.56-.828a1 1 0 001.114 1.66l.008-.004c.011-.008.034-.021.067-.04a3.87 3.87 0 01.322-.159 6.124 6.124 0 011.356-.407c1.194-.221 3-.237 5.317.704 2.683 1.09 4.877 1.107 6.433.82.349-.065.664-.145.943-.23v8.866a6.29 6.29 0 01-1.3.37c-1.2.214-3.008.23-5.324-.679-2.682-1.123-4.879-1.14-6.438-.842a7.95 7.95 0 00-1.806.564 5.713 5.713 0 00-.637.34l-.043.028-.015.01-.006.005-.002.001h-.001s-.001.002.572.821l-.573-.82a1 1 0 001.14 1.644l.007-.005a3.71 3.71 0 01.388-.204c.296-.135.75-.305 1.35-.42 1.189-.227 2.989-.244 5.301.727l.021.009c2.68 1.052 4.867 1.067 6.417.79a8.292 8.292 0 001.795-.525 5.847 5.847 0 00.633-.317l.044-.026.015-.01.005-.003.003-.002s.002-.001-.546-.838l.548.837a1 1 0 00.452-.837v-11a1 1 0 00-1.554-.833l-.008.005c-.011.008-.034.021-.066.04a3.8 3.8 0 01-.323.159 6.123 6.123 0 01-1.356.407c-1.194.221-3 .237-5.317-.704-2.683-1.09-4.877-1.107-6.433-.82a8.118 8.118 0 00-1.8.546 5.779 5.779 0 00-.635.328l-.044.027-.014.01-.006.004-.003.002s-.002.001.559.829z",
    clipRule: "evenodd"
  })), _path2$1q || (_path2$1q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 2.903a1 1 0 011 1v17a1 1 0 11-2 0v-17a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$26;

function _extends$29() { _extends$29 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$29.apply(this, arguments); }

function SvgFolder(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$29({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$26 || (_path$26 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 5a1 1 0 00-1 1v12a1 1 0 001 1h14a1 1 0 001-1V9a1 1 0 00-1-1h-5.644a3 3 0 01-2.496-1.336l-.813-1.219A1 1 0 009.215 5H5zM2 6a3 3 0 013-3h4.215a3 3 0 012.496 1.336l.813 1.219a1 1 0 00.832.445H19a3 3 0 013 3v9a3 3 0 01-3 3H5a3 3 0 01-3-3V6z",
    clipRule: "evenodd"
  })));
}

var _path$25, _path2$1p, _path3$y;

function _extends$28() { _extends$28 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$28.apply(this, arguments); }

function SvgFrown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$28({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$25 || (_path$25 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$1p || (_path2$1p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.837 16.943a1 1 0 001.277-.61 2.001 2.001 0 013.772 0 1 1 0 101.886-.666 4.001 4.001 0 00-7.544 0 1 1 0 00.61 1.276z",
    clipRule: "evenodd"
  })), _path3$y || (_path3$y = /*#__PURE__*/React.createElement("path", {
    d: "M10 9a1 1 0 11-2 0 1 1 0 012 0zm6 0a1 1 0 11-2 0 1 1 0 012 0z"
  })));
}

var _path$24, _path2$1o, _path3$x, _path4$6, _path5$1;

function _extends$27() { _extends$27 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$27.apply(this, arguments); }

function SvgGift(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$27({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$24 || (_path$24 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 12a1 1 0 011-1h14a1 1 0 011 1v7a3 3 0 01-3 3H7a3 3 0 01-3-3v-7zm2 1v6a1 1 0 001 1h10a1 1 0 001-1v-6H6z",
    clipRule: "evenodd"
  })), _path2$1o || (_path2$1o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 9a2 2 0 012-2h16a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V9zm18 0H4v2h16V9z",
    clipRule: "evenodd"
  })), _path3$x || (_path3$x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 6a1 1 0 011 1v14a1 1 0 01-2 0V7a1 1 0 011-1z",
    clipRule: "evenodd"
  })), _path4$6 || (_path4$6 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.009 8.13L12 8l.991-.131v-.005l-.002-.01-.004-.031a7.896 7.896 0 00-.086-.492 12.929 12.929 0 00-.315-1.25c-.292-.951-.81-2.23-1.715-3.135a3.697 3.697 0 10-5.23 5.23 1 1 0 00.076.067l.654.532 1.262-1.55-.609-.497A1.698 1.698 0 019.455 4.36c.552.553.953 1.448 1.216 2.306a10.917 10.917 0 01.335 1.446l.002.017v.002z",
    clipRule: "evenodd"
  })), _path5$1 || (_path5$1 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.685 8.13L11.693 8l-.99-.131v-.005l.001-.01.005-.031.017-.11a12.904 12.904 0 01.384-1.633c.291-.95.81-2.229 1.715-3.134a3.698 3.698 0 015.229 5.23c-.024.023-.05.046-.076.067l-.653.532-1.263-1.55.61-.497a1.698 1.698 0 00-2.433-2.368c-.553.553-.954 1.448-1.217 2.306a10.922 10.922 0 00-.334 1.446l-.003.017v.002z",
    clipRule: "evenodd"
  })));
}

var _path$23;

function _extends$26() { _extends$26 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$26.apply(this, arguments); }

function SvgGlasses(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$26({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$23 || (_path$23 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.826 6a1 1 0 00-.995.91l-.561 6.166A3.986 3.986 0 017 12c1.253 0 2.37.576 3.104 1.477A3.985 3.985 0 0112 13c.685 0 1.331.173 1.896.477A3.992 3.992 0 0117 12a3.99 3.99 0 012.73 1.076l-.56-6.167A1 1 0 0018.174 6H17a1 1 0 110-2h1.174a3 3 0 012.987 2.728l.654 7.19a2.817 2.817 0 01-.819 2.256 4 4 0 11-7.936-.87A1.988 1.988 0 0012 15c-.39 0-.753.111-1.06.304a4 4 0 11-7.936.87 2.817 2.817 0 01-.819-2.256l.654-7.19A3 3 0 015.826 4H7a1 1 0 110 2H5.826zM19 16a2 2 0 10-4 0 2 2 0 004 0zM7 14a2 2 0 100 4 2 2 0 000-4z",
    clipRule: "evenodd"
  })));
}

var _path$22;

function _extends$25() { _extends$25 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$25.apply(this, arguments); }

function SvgGlobe(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$25({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$22 || (_path$22 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.942 2h.116a.99.99 0 01.053 0C17.583 2.06 22 6.515 22 12c0 5.523-4.477 10-10 10S2 17.523 2 12c0-5.486 4.417-9.94 9.889-10a.99.99 0 01.053 0zM9 11.883c0-.642.055-1.271.16-1.883h5.68a11.102 11.102 0 01-.043 4H9.203A11.083 11.083 0 019 11.883zM7.171 14a13.11 13.11 0 01-.036-4H4.252A8.014 8.014 0 004 12c0 .69.088 1.36.252 2h2.92zm-2.1 2h2.594a12.976 12.976 0 001.88 3.616A8.023 8.023 0 015.07 16zm4.724 0h4.41A11.01 11.01 0 0112 19.44 11.008 11.008 0 019.795 16zm6.54 0a12.974 12.974 0 01-1.88 3.616A8.022 8.022 0 0018.93 16h-2.595zm3.413-2A8 8 0 0020 12a8 8 0 00-.252-2h-2.883a13.119 13.119 0 01-.036 4h2.919zm-5.451-6H9.703A11.004 11.004 0 0112 4.326 11.004 11.004 0 0114.297 8zm2.114 0h2.519a8.025 8.025 0 00-4.253-3.541A12.965 12.965 0 0116.411 8zM5.071 8h2.518a12.965 12.965 0 011.734-3.541A8.026 8.026 0 005.07 8z",
    clipRule: "evenodd"
  })));
}

var _path$21;

function _extends$24() { _extends$24 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$24.apply(this, arguments); }

function SvgGrid(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$24({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$21 || (_path$21 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 013-3h3a3 3 0 013 3v3a3 3 0 01-3 3H5a3 3 0 01-3-3V5zm3-1a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V5a1 1 0 00-1-1H5zM2 16a3 3 0 013-3h3a3 3 0 013 3v3a3 3 0 01-3 3H5a3 3 0 01-3-3v-3zm3-1a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1v-3a1 1 0 00-1-1H5zm8-10a3 3 0 013-3h3a3 3 0 013 3v3a3 3 0 01-3 3h-3a3 3 0 01-3-3V5zm3-1a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V5a1 1 0 00-1-1h-3zm-3 12a3 3 0 013-3h3a3 3 0 013 3v3a3 3 0 01-3 3h-3a3 3 0 01-3-3v-3zm3-1a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1v-3a1 1 0 00-1-1h-3z",
    clipRule: "evenodd"
  })));
}

var _path$20, _path2$1n;

function _extends$23() { _extends$23 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$23.apply(this, arguments); }

function SvgHash(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$23({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$20 || (_path$20 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 8a1 1 0 011-1h18a1 1 0 110 2H3a1 1 0 01-1-1zm0 8a1 1 0 011-1h18a1 1 0 110 2H3a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path2$1n || (_path2$1n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.164 2.014a1 1 0 01.822 1.15l-3 18a1 1 0 01-1.972-.328l3-18a1 1 0 011.15-.822zm7 0a1 1 0 01.822 1.15l-3 18a1 1 0 01-1.972-.328l3-18a1 1 0 011.15-.822z",
    clipRule: "evenodd"
  })));
}

var _path$1$;

function _extends$22() { _extends$22 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$22.apply(this, arguments); }

function SvgHeadphones(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$22({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1$ || (_path$1$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10v7a3 3 0 01-3 3h-1.625a3 3 0 01-3-3v-4a3 3 0 013-3h2.5c.042 0 .084.003.125.008V12a8 8 0 10-16 0v.008c.04-.005.083-.008.125-.008h2.5a3 3 0 013 3v4a3 3 0 01-3 3H5a3 3 0 01-3-3v-7zm2 1.992V19a1 1 0 001 1h1.625a1 1 0 001-1v-4a1 1 0 00-1-1h-2.5c-.042 0-.084-.003-.125-.008zm16 0a1.01 1.01 0 01-.125.008h-2.5a1 1 0 00-1 1v4a1 1 0 001 1H19a1 1 0 001-1v-5.008z",
    clipRule: "evenodd"
  })));
}

var _path$1_;

function _extends$21() { _extends$21 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$21.apply(this, arguments); }

function SvgHealthPass(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$21({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1_ || (_path$1_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a2 2 0 00-2 2v3a1 1 0 01-1 1H6a2 2 0 100 4h3a1 1 0 011 1v3a2 2 0 104 0v-3a1 1 0 011-1h3a2 2 0 100-4h-3a1 1 0 01-1-1V6a2 2 0 00-2-2zM8 6a4 4 0 118 0v2h2a4 4 0 010 8h-2v2a4 4 0 01-8 0v-2H6a4 4 0 010-8h2V6z",
    clipRule: "evenodd"
  })));
}

var _path$1Z, _path2$1m;

function _extends$20() { _extends$20 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$20.apply(this, arguments); }

function SvgHealth(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$20({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1Z || (_path$1Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 6a2 2 0 00-2 2v1H8a2 2 0 00-2 2v2a2 2 0 002 2h1v1a2 2 0 002 2h2a2 2 0 002-2v-1h1a2 2 0 002-2v-2a2 2 0 00-2-2h-1V8a2 2 0 00-2-2h-2zm-1 5a1 1 0 001-1V8h2v2a1 1 0 001 1h2v2h-2a1 1 0 00-1 1v2h-2v-2a1 1 0 00-1-1H8v-2h2z",
    clipRule: "evenodd"
  })), _path2$1m || (_path2$1m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zM4 12a8 8 0 1116 0 8 8 0 01-16 0z",
    clipRule: "evenodd"
  })));
}

var _path$1Y;

function _extends$1$() { _extends$1$ = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1$.apply(this, arguments); }

function SvgHeart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1$({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1Y || (_path$1Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.06 3.446a5.66 5.66 0 014.404 0 5.74 5.74 0 011.862 1.268 5.833 5.833 0 011.24 1.89 5.905 5.905 0 010 4.449 5.834 5.834 0 01-1.24 1.89L12.713 20.7a1 1 0 01-1.427 0l-7.612-7.757A5.875 5.875 0 012 8.828c0-1.54.6-3.02 1.674-4.114A5.693 5.693 0 017.737 3c1.528 0 2.99.62 4.064 1.714l.199.203.199-.203a5.733 5.733 0 011.861-1.268zm4.838 2.669a3.73 3.73 0 00-1.212-.826 3.659 3.659 0 00-2.848 0 3.73 3.73 0 00-1.211.826l-.914.93a1 1 0 01-1.427 0l-.913-.93A3.693 3.693 0 007.737 5c-.985 0-1.933.4-2.636 1.115A3.875 3.875 0 004 8.828c0 1.022.398 1.997 1.101 2.714L12 18.572l6.898-7.03c.348-.355.625-.777.815-1.243a3.906 3.906 0 000-2.942 3.837 3.837 0 00-.815-1.242z",
    clipRule: "evenodd"
  })));
}

var _path$1X, _path2$1l, _path3$w;

function _extends$1_() { _extends$1_ = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1_.apply(this, arguments); }

function SvgHelpCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1_({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1X || (_path$1X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$1l || (_path2$1l = /*#__PURE__*/React.createElement("path", {
    d: "M13 16a1 1 0 11-2 0 1 1 0 012 0z"
  })), _path3$w || (_path3$w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 9a1 1 0 00-1 1 1 1 0 11-2 0 3 3 0 116 0c0 1.224-.815 2.045-1.37 2.495a9.277 9.277 0 01-.63.461v.294a1 1 0 11-2 0v-.75c0-.336.159-.567.222-.652a1.42 1.42 0 01.21-.222 3.65 3.65 0 01.314-.235c.035-.024.07-.049.106-.072.172-.118.343-.235.517-.376.446-.363.631-.667.631-.943a1 1 0 00-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$1W;

function _extends$1Z() { _extends$1Z = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1Z.apply(this, arguments); }

function SvgHexagon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1W || (_path$1W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.5 2.711a3 3 0 013 0l5.794 3.346a3 3 0 011.5 2.598v6.69a3 3 0 01-1.5 2.598L13.5 21.29a3 3 0 01-3 0l-5.794-3.346a3 3 0 01-1.5-2.598v-6.69a3 3 0 011.5-2.598L10.5 2.71zm2 1.732a1 1 0 00-1 0L5.706 7.79a1 1 0 00-.5.866v6.69a1 1 0 00.5.866l5.794 3.346a1 1 0 001 0l5.794-3.346a1 1 0 00.5-.866v-6.69a1 1 0 00-.5-.866L12.5 4.443z",
    clipRule: "evenodd"
  })));
}

var _path$1V, _path2$1k;

function _extends$1Y() { _extends$1Y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1Y.apply(this, arguments); }

function SvgHome(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1V || (_path$1V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.664 3.928a1 1 0 00-1.328 0l-7 6.223a1 1 0 00-.336.747V19a1 1 0 001 1h14a1 1 0 001-1v-8.102a1 1 0 00-.336-.747l-7-6.223zm-2.657-1.494a3 3 0 013.986 0l7 6.222A3 3 0 0122 10.898V19a3 3 0 01-3 3H5a3 3 0 01-3-3v-8.102a3 3 0 011.007-2.242l7-6.222z",
    clipRule: "evenodd"
  })), _path2$1k || (_path2$1k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 13a1 1 0 011-1h6a1 1 0 011 1v8a1 1 0 01-1 1H9a1 1 0 01-1-1v-8zm2 1v6h4v-6h-4z",
    clipRule: "evenodd"
  })));
}

var _path$1U;

function _extends$1X() { _extends$1X = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1X.apply(this, arguments); }

function SvgIconlogo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1X({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1U || (_path$1U = /*#__PURE__*/React.createElement("path", {
    d: "M8.91 2.562c.67-.216 1.39.148 1.608.814a1.264 1.264 0 01-.82 1.596 1.279 1.279 0 01-1.608-.813c-.218-.666.15-1.38.82-1.597zm6.18 0c.67.217 1.038.931.82 1.597a1.279 1.279 0 01-1.609.813 1.265 1.265 0 01-.82-1.596 1.279 1.279 0 011.61-.814zM12 7.355c.705 0 1.277-.567 1.277-1.267S12.705 4.821 12 4.821s-1.277.568-1.277 1.267c0 .7.572 1.267 1.277 1.267zm0 11.824c.705 0 1.277-.568 1.277-1.267 0-.7-.572-1.267-1.277-1.267s-1.277.567-1.277 1.267.572 1.267 1.277 1.267zM9.775 7.217c0 .7-.572 1.267-1.277 1.267a1.272 1.272 0 01-1.276-1.267c0-.7.571-1.267 1.276-1.267.705 0 1.277.568 1.277 1.267zm5.727 10.833c.705 0 1.276-.568 1.276-1.267 0-.7-.571-1.267-1.276-1.267-.705 0-1.277.567-1.277 1.267s.572 1.266 1.277 1.266zM7.61 10.173c0 .7-.57 1.267-1.276 1.267a1.272 1.272 0 01-1.276-1.267c0-.7.571-1.267 1.276-1.267.705 0 1.277.568 1.277 1.267zm10.135 4.921c.705 0 1.276-.567 1.276-1.267s-.571-1.267-1.276-1.267c-.705 0-1.277.567-1.277 1.267s.572 1.267 1.277 1.267zM22 12c0 .7-.572 1.267-1.277 1.267A1.272 1.272 0 0119.447 12c0-.7.571-1.267 1.276-1.267.705 0 1.277.567 1.277 1.267zM3.277 13.267c.705 0 1.276-.567 1.276-1.267s-.571-1.267-1.276-1.267C2.572 10.733 2 11.3 2 12s.572 1.267 1.277 1.267zm16.813-7.1a1.26 1.26 0 01-.282 1.77 1.283 1.283 0 01-1.783-.28 1.26 1.26 0 01.282-1.77 1.283 1.283 0 011.783.28zM5.693 18.113a1.26 1.26 0 00.282-1.77 1.283 1.283 0 00-1.783-.28 1.26 1.26 0 00-.282 1.77 1.283 1.283 0 001.783.28zm4.825 2.511a1.265 1.265 0 00-.82-1.596 1.279 1.279 0 00-1.608.813c-.218.666.15 1.38.82 1.597.67.216 1.39-.148 1.608-.814zm5.392-.783a1.279 1.279 0 00-1.609-.813c-.67.216-1.037.93-.82 1.596a1.28 1.28 0 001.61.814 1.265 1.265 0 00.819-1.597zm-12-13.674a1.283 1.283 0 011.783-.28 1.26 1.26 0 01.282 1.77 1.283 1.283 0 01-1.783.28 1.26 1.26 0 01-.282-1.77zm15.898 9.896a1.283 1.283 0 00-1.783.28 1.26 1.26 0 00.282 1.77c.57.411 1.369.286 1.783-.28a1.26 1.26 0 00-.282-1.77zM7.61 13.827c0 .7-.57 1.267-1.276 1.267a1.272 1.272 0 01-1.276-1.267c0-.7.571-1.267 1.276-1.267.705 0 1.277.567 1.277 1.267zm10.056-2.387c.705 0 1.276-.567 1.276-1.267s-.571-1.267-1.276-1.267c-.705 0-1.277.568-1.277 1.267 0 .7.572 1.267 1.277 1.267zm-7.891 5.343c0 .7-.572 1.267-1.277 1.267a1.272 1.272 0 01-1.276-1.267c0-.7.571-1.267 1.276-1.267.705 0 1.277.567 1.277 1.267zm5.727-8.299c.705 0 1.276-.567 1.276-1.267s-.571-1.267-1.276-1.267c-.705 0-1.277.568-1.277 1.267 0 .7.572 1.267 1.277 1.267z"
  })));
}

var _path$1T, _path2$1j;

function _extends$1W() { _extends$1W = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1W.apply(this, arguments); }

function SvgIdCardBack(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1W({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1T || (_path$1T = /*#__PURE__*/React.createElement("path", {
    d: "M6 15a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1-7a1 1 0 00-1 1v2a1 1 0 001 1h10a1 1 0 001-1V9a1 1 0 00-1-1H7z"
  })), _path2$1j || (_path2$1j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 7a3 3 0 013-3h14a3 3 0 013 3v10a3 3 0 01-3 3H5a3 3 0 01-3-3V7zm3-1a1 1 0 00-1 1v10a1 1 0 001 1h14a1 1 0 001-1V7a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })));
}

var _path$1S, _path2$1i, _path3$v;

function _extends$1V() { _extends$1V = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1V.apply(this, arguments); }

function SvgIdCard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1V({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1S || (_path$1S = /*#__PURE__*/React.createElement("path", {
    d: "M14 13a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1zm1-4a1 1 0 100 2h3a1 1 0 100-2h-3z"
  })), _path2$1i || (_path2$1i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 10.25a3 3 0 115.051 2.19A3.001 3.001 0 0113 15.25v.5a1 1 0 11-2 0v-.5a1 1 0 00-1-1H8a1 1 0 00-1 1v.5a1 1 0 11-2 0v-.5c0-1.287.81-2.385 1.949-2.81A2.992 2.992 0 016 10.25zm3-1a1 1 0 100 2 1 1 0 000-2z",
    clipRule: "evenodd"
  })), _path3$v || (_path3$v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 7a3 3 0 013-3h14a3 3 0 013 3v10a3 3 0 01-3 3H5a3 3 0 01-3-3V7zm3-1a1 1 0 00-1 1v10a1 1 0 001 1h14a1 1 0 001-1V7a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })));
}

var _path$1R, _path2$1h;

function _extends$1U() { _extends$1U = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1U.apply(this, arguments); }

function SvgIdInFrame(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1U({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1R || (_path$1R = /*#__PURE__*/React.createElement("path", {
    d: "M3 8a1 1 0 001-1V5a1 1 0 011-1h2a1 1 0 000-2H5a3 3 0 00-3 3v2a1 1 0 001 1zm1 9a1 1 0 10-2 0v2a3 3 0 003 3h2a1 1 0 100-2H5a1 1 0 01-1-1v-2zm16 0a1 1 0 112 0v2a3 3 0 01-3 3h-2a1 1 0 110-2h2a1 1 0 001-1v-2zm1-9a1 1 0 01-1-1V5a1 1 0 00-1-1h-2a1 1 0 110-2h2a3 3 0 013 3v2a1 1 0 01-1 1zm-7 3c0 .439-.141.844-.38 1.174A2.25 2.25 0 0115 14.25a.75.75 0 01-.75.75h-4.5a.75.75 0 01-.75-.75c0-.934.57-1.736 1.38-2.076A2 2 0 1114 11z"
  })), _path2$1h || (_path2$1h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 6a3 3 0 00-3 3v6a3 3 0 003 3h8a3 3 0 003-3V9a3 3 0 00-3-3H8zM7 9a1 1 0 011-1h8a1 1 0 011 1v6a1 1 0 01-1 1H8a1 1 0 01-1-1V9z",
    clipRule: "evenodd"
  })));
}

var _path$1Q, _path2$1g, _path3$u, _path4$5;

function _extends$1T() { _extends$1T = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1T.apply(this, arguments); }

function SvgIdInReview(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1T({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1Q || (_path$1Q = /*#__PURE__*/React.createElement("path", {
    d: "M5 4a3 3 0 00-3 3v10a3 3 0 003 3h5a1 1 0 100-2H5a1 1 0 01-1-1V7a1 1 0 011-1h14a1 1 0 011 1v4a1 1 0 102 0V7a3 3 0 00-3-3H5z"
  })), _path2$1g || (_path2$1g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.4 11.8a3 3 0 10-4.8 0A3.994 3.994 0 005 15v1a1 1 0 102 0v-1a2 2 0 013.54-1.276 1 1 0 001.54-1.277 4.02 4.02 0 00-.68-.647zM8 10a1 1 0 112 0 1 1 0 01-2 0z",
    clipRule: "evenodd"
  })), _path3$u || (_path3$u = /*#__PURE__*/React.createElement("path", {
    d: "M13 10a1 1 0 011-1h3a1 1 0 110 2h-3a1 1 0 01-1-1z"
  })), _path4$5 || (_path4$5 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13 17a4 4 0 117.446 2.032l1.261 1.26a1 1 0 01-1.414 1.415l-1.261-1.26A4 4 0 0113 17zm4-2a2 2 0 100 4 2 2 0 000-4z",
    clipRule: "evenodd"
  })));
}

var _path$1P, _path2$1f;

function _extends$1S() { _extends$1S = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1S.apply(this, arguments); }

function SvgImage(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1S({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1P || (_path$1P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 6a3 3 0 100 6 3 3 0 000-6zM8 9a1 1 0 112 0 1 1 0 01-2 0z",
    clipRule: "evenodd"
  })), _path2$1f || (_path2$1f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19 22a3 3 0 003-3V5a3 3 0 00-3-3H5a3 3 0 00-3 3v14a3 3 0 003 3h14zM5 4a1 1 0 00-1 1v14a1 1 0 001 1h1.55l7.703-8.664a1 1 0 011.387-.104L20 14.865V5a1 1 0 00-1-1H5zm15 15v-1.532l-4.896-4.08L9.227 20H19a1 1 0 001-1z",
    clipRule: "evenodd"
  })));
}

var _path$1O, _path2$1e;

function _extends$1R() { _extends$1R = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1R.apply(this, arguments); }

function SvgInbox(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1R({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1O || (_path$1O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.558 4.782A3 3 0 018.3 3h7.4a3 3 0 012.742 1.782l3.3 7.424A3 3 0 0122 13.424V18a3 3 0 01-3 3H5a3 3 0 01-3-3v-4.576a3 3 0 01.259-1.218l3.3-7.424zM8.3 5a1 1 0 00-.914.594l-3.3 7.424a1 1 0 00-.086.406V18a1 1 0 001 1h14a1 1 0 001-1v-4.576a1 1 0 00-.086-.406l-3.3-7.424A1 1 0 0015.7 5H8.3z",
    clipRule: "evenodd"
  })), _path2$1e || (_path2$1e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 13a1 1 0 011-1h5a1 1 0 01.832.445L10.535 15h3.43l1.703-2.555A1 1 0 0116.5 12H21a1 1 0 110 2h-3.965l-1.703 2.555A1 1 0 0114.5 17H10a1 1 0 01-.832-.445L7.465 14H3a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$1N, _path2$1d, _path3$t;

function _extends$1Q() { _extends$1Q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1Q.apply(this, arguments); }

function SvgInfo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1N || (_path$1N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$1d || (_path2$1d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 11a1 1 0 00-1 1v5a1 1 0 102 0v-5a1 1 0 00-1-1z",
    clipRule: "evenodd"
  })), _path3$t || (_path3$t = /*#__PURE__*/React.createElement("path", {
    d: "M13 8a1 1 0 10-2 0 1 1 0 002 0z"
  })));
}

var _path$1M, _path2$1c, _path3$s;

function _extends$1P() { _extends$1P = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1P.apply(this, arguments); }

function SvgKey(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1P({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1M || (_path$1M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 12a4 4 0 100 8 4 4 0 000-8zm-6 4a6 6 0 1112 0 6 6 0 01-12 0z",
    clipRule: "evenodd"
  })), _path2$1c || (_path2$1c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.707 2.293a1 1 0 010 1.414l-9 9a1 1 0 01-1.414-1.414l9-9a1 1 0 011.414 0z",
    clipRule: "evenodd"
  })), _path3$s || (_path3$s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.293 7.293a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414zm3-3a1 1 0 011.414 0l2 2a1 1 0 01-1.414 1.414l-2-2a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$1L;

function _extends$1O() { _extends$1O = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1O.apply(this, arguments); }

function SvgLab(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1O({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1L || (_path$1L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 8.697V4a1 1 0 010-2h8a1 1 0 110 2v4.697l5.76 8.639C23.087 19.33 21.658 22 19.262 22H4.737c-2.396 0-3.825-2.67-2.496-4.664L8 8.697zM14 4h-4v5a1 1 0 01-.168.555L7.535 13h8.93l-1.339-2.008A1.01 1.01 0 0115 11h-2a1 1 0 110-2h1V7h-1a1 1 0 110-2h1V4zM3.905 18.445L6.202 15h11.596l2.297 3.445A1 1 0 0119.263 20H4.737a1 1 0 01-.832-1.555z",
    clipRule: "evenodd"
  })));
}

var _path$1K, _path2$1b;

function _extends$1N() { _extends$1N = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1N.apply(this, arguments); }

function SvgLaptopComputer(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1N({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1K || (_path$1K = /*#__PURE__*/React.createElement("path", {
    d: "M10 17a1 1 0 100 2h4a1 1 0 100-2h-4z"
  })), _path2$1b || (_path2$1b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4a2 2 0 012-2h12a2 2 0 012 2v9.86l1.597 5.59A2 2 0 0119.674 22H4.326a2 2 0 01-1.923-2.55L4 13.86V4zm14 0v9H6V4h12zm1.674 16H4.326l1.428-5h12.492l1.428 5z",
    clipRule: "evenodd"
  })));
}

var _path$1J, _path2$1a;

function _extends$1M() { _extends$1M = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1M.apply(this, arguments); }

function SvgLink(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1M({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1J || (_path$1J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.073 9.497a1.011 1.011 0 011.43 0 5.056 5.056 0 010 7.15L10.63 20.52a5.056 5.056 0 01-7.15-7.15l.715-.715a1.011 1.011 0 011.43 1.43l-.715.715a3.034 3.034 0 104.29 4.29l3.872-3.871a3.034 3.034 0 000-4.29 1.011 1.011 0 010-1.43z",
    clipRule: "evenodd"
  })), _path2$1a || (_path2$1a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.927 14.503a1.011 1.011 0 01-1.43 0 5.056 5.056 0 010-7.15L13.37 3.48a5.056 5.056 0 017.15 7.15l-.715.715a1.011 1.011 0 01-1.43-1.43l.715-.715a3.034 3.034 0 00-4.29-4.29l-3.872 3.871a3.034 3.034 0 000 4.29 1.011 1.011 0 010 1.43z",
    clipRule: "evenodd"
  })));
}

var _path$1I;

function _extends$1L() { _extends$1L = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1L.apply(this, arguments); }

function SvgLoader(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1L({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1I || (_path$1I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 16a1 1 0 011 1v4a1 1 0 11-2 0v-4a1 1 0 011-1zm0-14a1 1 0 011 1v4a1 1 0 11-2 0V3a1 1 0 011-1zm4 10a1 1 0 011-1h4a1 1 0 110 2h-4a1 1 0 01-1-1zM2 12a1 1 0 011-1h4a1 1 0 110 2H3a1 1 0 01-1-1zm7.172 2.828a1 1 0 010 1.415L6.343 19.07a1 1 0 01-1.414-1.414l2.828-2.829a1 1 0 011.415 0zm9.899-9.899a1 1 0 010 1.414l-2.828 2.829a1 1 0 11-1.415-1.415l2.829-2.828a1 1 0 011.414 0zm-4.243 9.899a1 1 0 011.415 0l2.828 2.829a1 1 0 01-1.414 1.414l-2.829-2.828a1 1 0 010-1.415zM4.929 4.929a1 1 0 011.414 0l2.829 2.828a1 1 0 11-1.415 1.415L4.93 6.343a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$1H, _path2$19;

function _extends$1K() { _extends$1K = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1K.apply(this, arguments); }

function SvgLock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1K({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1H || (_path$1H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 13a3 3 0 013-3h12a3 3 0 013 3v6a3 3 0 01-3 3H6a3 3 0 01-3-3v-6zm3-1a1 1 0 00-1 1v6a1 1 0 001 1h12a1 1 0 001-1v-6a1 1 0 00-1-1H6z",
    clipRule: "evenodd"
  })), _path2$19 || (_path2$19 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 7a5 5 0 0110 0v3a1 1 0 11-2 0V7a3 3 0 10-6 0v3a1 1 0 11-2 0V7z",
    clipRule: "evenodd"
  })));
}

var _path$1G, _path2$18, _path3$r;

function _extends$1J() { _extends$1J = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1J.apply(this, arguments); }

function SvgLogIn(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1J({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1G || (_path$1G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.293 5.293a1 1 0 000 1.414L14.586 12l-5.293 5.293a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414l-6-6a1 1 0 00-1.414 0z",
    clipRule: "evenodd"
  })), _path2$18 || (_path2$18 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17 12a1 1 0 00-1-1H3a1 1 0 100 2h13a1 1 0 001-1z",
    clipRule: "evenodd"
  })), _path3$r || (_path3$r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15 21a1 1 0 011-1h3a1 1 0 001-1V5a1 1 0 00-1-1h-3a1 1 0 110-2h3a3 3 0 013 3v14a3 3 0 01-3 3h-3a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$1F, _path2$17, _path3$q;

function _extends$1I() { _extends$1I = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1I.apply(this, arguments); }

function SvgLogOut(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1I({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1F || (_path$1F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.293 5.293a1 1 0 000 1.414L19.586 12l-5.293 5.293a1 1 0 001.414 1.414l6-6a1 1 0 000-1.414l-6-6a1 1 0 00-1.414 0z",
    clipRule: "evenodd"
  })), _path2$17 || (_path2$17 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 12a1 1 0 00-1-1H8a1 1 0 100 2h13a1 1 0 001-1z",
    clipRule: "evenodd"
  })), _path3$q || (_path3$q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 21a1 1 0 00-1-1H5a1 1 0 01-1-1V5a1 1 0 011-1h3a1 1 0 000-2H5a3 3 0 00-3 3v14a3 3 0 003 3h3a1 1 0 001-1z",
    clipRule: "evenodd"
  })));
}

var _path$1E, _path2$16;

function _extends$1H() { _extends$1H = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1H.apply(this, arguments); }

function SvgLuggage(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1H({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1E || (_path$1E = /*#__PURE__*/React.createElement("path", {
    d: "M10 9a1 1 0 011 1v6a1 1 0 11-2 0v-6a1 1 0 011-1zm4 0a1 1 0 011 1v6a1 1 0 11-2 0v-6a1 1 0 011-1z"
  })), _path2$16 || (_path2$16 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2h1a3 3 0 013 3v8a3 3 0 01-3 3v1a1 1 0 11-2 0v-1h-4v1a1 1 0 11-2 0v-1a3 3 0 01-3-3V9a3 3 0 013-3h1zM8 8a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1V9a1 1 0 00-1-1H8zm5-2V4h-2v2h2z",
    clipRule: "evenodd"
  })));
}

var _path$1D, _path2$15;

function _extends$1G() { _extends$1G = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1G.apply(this, arguments); }

function SvgMail(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1G({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1D || (_path$1D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 7a3 3 0 013-3h14a3 3 0 013 3v10a3 3 0 01-3 3H5a3 3 0 01-3-3V7zm3-1a1 1 0 00-1 1v10a1 1 0 001 1h14a1 1 0 001-1V7a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })), _path2$15 || (_path2$15 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.168 7.445a1 1 0 011.387-.277L12 12.798l8.445-5.63a1 1 0 011.11 1.664l-9 6a1 1 0 01-1.11 0l-9-6a1 1 0 01-.277-1.387z",
    clipRule: "evenodd"
  })));
}

var _path$1C;

function _extends$1F() { _extends$1F = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1F.apply(this, arguments); }

function SvgMaintenance(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1F({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1C || (_path$1C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17.907 2.701A7.197 7.197 0 009.08 4.837a1 1 0 00-.16-.134L4.676 1.875A1 1 0 003.414 2L2 3.414a1 1 0 00-.125 1.262l2.828 4.243a1 1 0 001.54.152l.707-.707.663.663c-.02.881.12 1.765.421 2.604L2.897 16.77a3.064 3.064 0 004.335 4.334l4.814-4.814 4.803 4.803a3 3 0 104.243-4.243l-1.929-1.928A7.197 7.197 0 0021.3 6.093a1 1 0 00-1.61-.276l-3.402 3.402a.031.031 0 01-.044-.001l-1.46-1.46a.032.032 0 01-.001-.046l3.401-3.401a1 1 0 00-.276-1.61zm-6.775 2.82a5.188 5.188 0 014.472-1.46l-2.237 2.237a2.032 2.032 0 00.001 2.874l1.46 1.46a2.031 2.031 0 002.873 0l2.238-2.236a5.196 5.196 0 01-7.103 5.608 1.527 1.527 0 00-1.659.325l-5.359 5.36a1.064 1.064 0 01-1.507-1.506l5.362-5.362a1.524 1.524 0 00.324-1.657 5.197 5.197 0 011.135-5.643zm6.211 10.408a7.206 7.206 0 01-2.37.458l3.29 3.29a1 1 0 101.415-1.414l-2.335-2.334zM4.25 3.994l-.255.255L5.69 6.794l1.103-1.103L4.25 3.994z",
    clipRule: "evenodd"
  })));
}

var _path$1B, _path2$14, _path3$p;

function _extends$1E() { _extends$1E = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1E.apply(this, arguments); }

function SvgMapPinOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1E({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1B || (_path$1B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.35 21.76L12 21l.65.76a1 1 0 01-1.3 0zm.65-2.112a32.263 32.263 0 01-2.728-2.909c-.848-1.03-1.677-2.189-2.29-3.371C6.363 12.177 6 11.028 6 10a5.97 5.97 0 01.991-3.305 1 1 0 00-1.668-1.103A7.967 7.967 0 004 10c0 1.472.511 2.948 1.206 4.288.7 1.35 1.62 2.628 2.522 3.723a34.237 34.237 0 003.596 3.727l.018.015.005.004.001.002L12 21l.65.76.004-.003.006-.006.022-.019.08-.07c.07-.062.17-.15.294-.265.25-.23.602-.56 1.016-.973a32.4 32.4 0 002.917-3.321 1 1 0 10-1.596-1.206 30.418 30.418 0 01-2.734 3.111c-.247.247-.47.462-.659.64zm5.511-5.04a1 1 0 01-.44-1.343C17.657 12.109 18 10.997 18 10a6 6 0 00-9.14-5.114 1 1 0 01-1.05-1.703A8 8 0 0120 10c0 1.43-.482 2.862-1.145 4.17a1 1 0 01-1.344.439z",
    clipRule: "evenodd"
  })), _path2$14 || (_path2$14 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 8a2 2 0 011.95 2.449 1 1 0 101.95.446 4 4 0 00-4.781-4.797 1 1 0 00.439 1.95c.141-.03.289-.048.442-.048zM9 9a1 1 0 00-1 1 4 4 0 004 4 1 1 0 100-2 2 2 0 01-2-2 1 1 0 00-1-1z",
    clipRule: "evenodd"
  })), _path3$p || (_path3$p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414l-18-18a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$1A, _path2$13;

function _extends$1D() { _extends$1D = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1D.apply(this, arguments); }

function SvgMapPin(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1D({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1A || (_path$1A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.65 21.76c-.282-.33-1.3 0-1.3 0a1 1 0 001.3 0zM12 19.647a32.268 32.268 0 002.728-2.909c.848-1.03 1.677-2.189 2.29-3.371.618-1.191.982-2.34.982-3.368a6 6 0 00-12 0c0 1.028.364 2.177.981 3.368.614 1.182 1.443 2.341 2.29 3.371A32.263 32.263 0 0012 19.65zm.65 2.111c-.282-.33-1.3 0-1.3 0l-.003-.002-.005-.004-.018-.015a20.69 20.69 0 01-.297-.265 34.237 34.237 0 01-3.299-3.462c-.902-1.095-1.823-2.374-2.522-3.723C4.511 12.948 4 11.472 4 10a8 8 0 1116 0c0 1.472-.511 2.948-1.206 4.288-.7 1.35-1.62 2.628-2.522 3.723a34.235 34.235 0 01-3.596 3.727l-.018.015-.005.004-.002.002z",
    clipRule: "evenodd"
  })), _path2$13 || (_path2$13 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 8a2 2 0 110 4 2 2 0 010-4zm4 2a4 4 0 10-8 0 4 4 0 008 0z",
    clipRule: "evenodd"
  })));
}

var _path$1z, _path2$12;

function _extends$1C() { _extends$1C = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1C.apply(this, arguments); }

function SvgMap(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1C({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1z || (_path$1z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.585 3.189A1 1 0 0122 4v15a1 1 0 01-.684.949l-6 2a1 1 0 01-.632 0L9 20.054 3.316 21.95A1 1 0 012 21V6a1 1 0 01.684-.949l6-2a1 1 0 01.632 0L15 4.946l5.684-1.895a1 1 0 01.9.138zM4 6.72v12.892l4.684-1.562a1 1 0 01.632 0L15 19.946l5-1.667V5.387L15.316 6.95a1 1 0 01-.632 0L9 5.054 4 6.721z",
    clipRule: "evenodd"
  })), _path2$12 || (_path2$12 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 3.5a1 1 0 011 1v14a1 1 0 11-2 0v-14a1 1 0 011-1zm6 2a1 1 0 011 1v14a1 1 0 11-2 0v-14a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$1y, _path2$11, _path3$o;

function _extends$1B() { _extends$1B = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1B.apply(this, arguments); }

function SvgMaximize2(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1B({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1y || (_path$1y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 14a1 1 0 011 1v5h5a1 1 0 110 2H3a1 1 0 01-1-1v-6a1 1 0 011-1z",
    clipRule: "evenodd"
  })), _path2$11 || (_path2$11 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.707 13.293a1 1 0 010 1.414l-6.9 6.9a1 1 0 01-1.414-1.415l6.9-6.9a1 1 0 011.414 0zM21 10a1 1 0 01-1-1V4h-5a1 1 0 110-2h6a1 1 0 011 1v6a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })), _path3$o || (_path3$o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.293 10.707a1 1 0 010-1.414l6.9-6.9a1 1 0 111.414 1.415l-6.9 6.9a1 1 0 01-1.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$1x, _path2$10;

function _extends$1A() { _extends$1A = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1A.apply(this, arguments); }

function SvgMaximize(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1A({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1x || (_path$1x = /*#__PURE__*/React.createElement("path", {
    d: "M3 17v2a2 2 0 002 2h2M3 7V5a2 2 0 012-2h2m14 14v2a2 2 0 01-2 2h-2m4-14V5a2 2 0 00-2-2h-2"
  })), _path2$10 || (_path2$10 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 16a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 110 2H5a3 3 0 01-3-3v-2a1 1 0 011-1zm0-8a1 1 0 001-1V5a1 1 0 011-1h2a1 1 0 000-2H5a3 3 0 00-3 3v2a1 1 0 001 1zm18 8a1 1 0 00-1 1v2a1 1 0 01-1 1h-2a1 1 0 100 2h2a3 3 0 003-3v-2a1 1 0 00-1-1zm0-8a1 1 0 01-1-1V5a1 1 0 00-1-1h-2a1 1 0 110-2h2a3 3 0 013 3v2a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$1w, _path2$$, _path3$n;

function _extends$1z() { _extends$1z = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1z.apply(this, arguments); }

function SvgMeh(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1z({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1w || (_path$1w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$$ || (_path2$$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 15a1 1 0 011-1h6a1 1 0 110 2H9a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path3$n || (_path3$n = /*#__PURE__*/React.createElement("path", {
    d: "M10 9a1 1 0 11-2 0 1 1 0 012 0zm6 0a1 1 0 11-2 0 1 1 0 012 0z"
  })));
}

var _path$1v;

function _extends$1y() { _extends$1y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1y.apply(this, arguments); }

function SvgMenu(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1y({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1v || (_path$1v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 7a1 1 0 011-1h16a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h16a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h16a1 1 0 110 2H4a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$1u;

function _extends$1x() { _extends$1x = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1x.apply(this, arguments); }

function SvgMessage(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1x({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1u || (_path$1u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 013-3h14a3 3 0 013 3v9a3 3 0 01-3 3H8.39a1 1 0 00-.743.331l-2.16 2.401C4.26 21.094 2 20.227 2 18.394V5zm3-1a1 1 0 00-1 1v13.394l2.16-2.4A3 3 0 018.39 15H19a1 1 0 001-1V5a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })));
}

var _path$1t;

function _extends$1w() { _extends$1w = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1w.apply(this, arguments); }

function SvgMessageCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1w({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1t || (_path$1t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.5 4a7.5 7.5 0 00-6.882 10.488c.19.434.243.95.084 1.45-.393 1.235-.802 2.49-1.125 3.475l-.001.003a.036.036 0 00.005.006.037.037 0 00.008.006h.004c1-.323 2.28-.73 3.552-1.115a2.063 2.063 0 011.409.087c.903.386 1.898.6 2.946.6a7.5 7.5 0 000-15zM3 11.5a9.5 9.5 0 115.768 8.738.127.127 0 00-.04-.01h-.005c-1.255.38-2.523.782-3.516 1.103-1.558.502-3.043-.978-2.53-2.541.322-.982.728-2.23 1.118-3.457v-.007a.127.127 0 00-.01-.04A9.472 9.472 0 013 11.5z",
    clipRule: "evenodd"
  })));
}

var _path$1s, _path2$_;

function _extends$1v() { _extends$1v = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1v.apply(this, arguments); }

function SvgMic(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1v({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1s || (_path$1s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a4 4 0 00-4 4v4a4 4 0 008 0V6a4 4 0 00-4-4zm-2 4a2 2 0 014 0v4a2 2 0 01-4 0V6z",
    clipRule: "evenodd"
  })), _path2$_ || (_path2$_ = /*#__PURE__*/React.createElement("path", {
    d: "M6.248 11.715a1 1 0 10-1.917.57 8.008 8.008 0 006.67 5.653L11 18v2H8a1 1 0 100 2h8a1 1 0 000-2h-3v-2c0-.02 0-.041-.002-.062a8.008 8.008 0 006.67-5.653 1 1 0 10-1.916-.57 6.003 6.003 0 01-11.504 0z"
  })));
}

var _path$1r, _path2$Z, _path3$m;

function _extends$1u() { _extends$1u = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1u.apply(this, arguments); }

function SvgMinimize2(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1u({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1r || (_path$1r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 21a1 1 0 01-1-1v-5H4a1 1 0 110-2h6a1 1 0 011 1v6a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })), _path2$Z || (_path2$Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 21.707a1 1 0 010-1.414l6.9-6.9a1 1 0 011.414 1.415l-6.9 6.9a1 1 0 01-1.414 0zM14 3a1 1 0 011 1v5h5a1 1 0 110 2h-6a1 1 0 01-1-1V4a1 1 0 011-1z",
    clipRule: "evenodd"
  })), _path3$m || (_path3$m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.707 2.293a1 1 0 010 1.414l-6.9 6.9a1 1 0 01-1.414-1.415l6.9-6.9a1 1 0 011.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$1q, _path2$Y;

function _extends$1t() { _extends$1t = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1t.apply(this, arguments); }

function SvgMinimize(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1t({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1q || (_path$1q = /*#__PURE__*/React.createElement("path", {
    d: "M17 3v2a2 2 0 002 2h2M7 3v2a2 2 0 01-2 2H3m14 14v-2a2 2 0 012-2h2M7 21v-2a2 2 0 00-2-2H3"
  })), _path2$Y || (_path2$Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17 2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 110 2h-2a3 3 0 01-3-3V3a1 1 0 011-1zM7 2a1 1 0 00-1 1v2a1 1 0 01-1 1H3a1 1 0 000 2h2a3 3 0 003-3V3a1 1 0 00-1-1zm10 20a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 100-2h-2a3 3 0 00-3 3v2a1 1 0 001 1zM7 22a1 1 0 01-1-1v-2a1 1 0 00-1-1H3a1 1 0 110-2h2a3 3 0 013 3v2a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$1p, _path2$X;

function _extends$1s() { _extends$1s = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1s.apply(this, arguments); }

function SvgMinusCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1s({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1p || (_path$1p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$X || (_path2$X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 12a1 1 0 011-1h8a1 1 0 110 2H8a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$1o, _path2$W;

function _extends$1r() { _extends$1r = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1r.apply(this, arguments); }

function SvgMinusSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1r({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1o || (_path$1o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 013-3h14a3 3 0 013 3v14a3 3 0 01-3 3H5a3 3 0 01-3-3V5zm3-1a1 1 0 00-1 1v14a1 1 0 001 1h14a1 1 0 001-1V5a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })), _path2$W || (_path2$W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 12a1 1 0 011-1h8a1 1 0 110 2H8a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$1n;

function _extends$1q() { _extends$1q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1q.apply(this, arguments); }

function SvgMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1q({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1n || (_path$1n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 12a1 1 0 011-1h14a1 1 0 110 2H5a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$1m, _path2$V;

function _extends$1p() { _extends$1p = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1p.apply(this, arguments); }

function SvgMobilePhone(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1p({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1m || (_path$1m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.714 4C7.365 4 7 4.312 7 4.8v14.4c0 .488.365.8.714.8h8.572c.349 0 .714-.312.714-.8V4.8c0-.488-.365-.8-.714-.8H7.714zM5 4.8C5 3.3 6.17 2 7.714 2h8.572C17.83 2 19 3.3 19 4.8v14.4c0 1.5-1.17 2.8-2.714 2.8H7.714C6.17 22 5 20.7 5 19.2V4.8z",
    clipRule: "evenodd"
  })), _path2$V || (_path2$V = /*#__PURE__*/React.createElement("path", {
    d: "M13 17a1 1 0 11-2 0 1 1 0 012 0z"
  })));
}

var _path$1l;

function _extends$1o() { _extends$1o = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1o.apply(this, arguments); }

function SvgMoon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1o({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1l || (_path$1l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.23 4.037A8 8 0 1019.602 14.5 7 7 0 0111.23 4.036zM2 12C2 6.477 6.477 2 12 2c.567 0 1.125.047 1.668.139a1 1 0 01.418 1.798 5 5 0 106.237 7.8 1 1 0 011.663.8C21.706 17.81 17.343 22 12 22 6.477 22 2 17.523 2 12z",
    clipRule: "evenodd"
  })));
}

var _path$1k;

function _extends$1n() { _extends$1n = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1n.apply(this, arguments); }

function SvgMoreHorizontal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1n({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1k || (_path$1k = /*#__PURE__*/React.createElement("path", {
    d: "M14 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0z"
  })));
}

var _path$1j;

function _extends$1m() { _extends$1m = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1m.apply(this, arguments); }

function SvgMoreVertical(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1m({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1j || (_path$1j = /*#__PURE__*/React.createElement("path", {
    d: "M6 10a2 2 0 110 4 2 2 0 010-4zm6 0a2 2 0 110 4 2 2 0 010-4zm6 0a2 2 0 110 4 2 2 0 010-4z"
  })));
}

var _path$1i, _path2$U, _path3$l;

function _extends$1l() { _extends$1l = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1l.apply(this, arguments); }

function SvgMove(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1l({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1i || (_path$1i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.707 8.293a1 1 0 010 1.414L4.414 12l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm10.586 0a1 1 0 000 1.414L19.586 12l-2.293 2.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 00-1.414 0z",
    clipRule: "evenodd"
  })), _path2$U || (_path2$U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 12a1 1 0 011-1h18a1 1 0 010 2H3a1 1 0 01-1-1zm6.293 5.293a1 1 0 011.414 0L12 19.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414zm0-10.586a1 1 0 001.414 0L12 4.414l2.293 2.293a1 1 0 101.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 000 1.414z",
    clipRule: "evenodd"
  })), _path3$l || (_path3$l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 22a1 1 0 01-1-1V3a1 1 0 112 0v18a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$1h;

function _extends$1k() { _extends$1k = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1k.apply(this, arguments); }

function SvgNavigation2(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1k({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1h || (_path$1h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a1 1 0 01.916.6l7 16a1 1 0 01-1.31 1.32L12 18.091l-6.607 2.827a1 1 0 01-1.31-1.32l7-16A1 1 0 0112 3zm0 3.495L6.928 18.087l4.679-2.002a1 1 0 01.787 0l4.677 2.002L12 6.495z",
    clipRule: "evenodd"
  })));
}

var _path$1g;

function _extends$1j() { _extends$1j = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1j.apply(this, arguments); }

function SvgNavigation(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1j({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1g || (_path$1g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.97 3.293a1 1 0 00-1.07-.224L3.635 9.433a1 1 0 00.038 1.876l6.9 2.38 2.38 6.9a1 1 0 001.877.039l6.364-16.264a1 1 0 00-.224-1.071zM18.5 5.764l-4.541 11.604-1.652-4.792a1 1 0 00-.62-.62l-4.791-1.651 11.603-4.54z",
    clipRule: "evenodd"
  })));
}

var _path$1f;

function _extends$1i() { _extends$1i = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1i.apply(this, arguments); }

function SvgNfc(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1i({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1f || (_path$1f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.071 3.293a1 1 0 011.414 0c4.687 4.686 4.687 12.284 0 16.97a1 1 0 01-1.414-1.414c3.905-3.905 3.905-10.237 0-14.142a1 1 0 010-1.414zm-3.121 2a1 1 0 011.414 0 9 9 0 010 12.728 1 1 0 01-1.414-1.414 7 7 0 000-9.9 1 1 0 010-1.414zm-3.122 2a1 1 0 011.415 0 6 6 0 010 8.485 1 1 0 01-1.415-1.414 4 4 0 000-5.657 1 1 0 010-1.414zm-3.12 2a1 1 0 011.413 0 3 3 0 010 4.243 1 1 0 11-1.414-1.415 1 1 0 000-1.414 1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$1e, _path2$T;

function _extends$1h() { _extends$1h = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1h.apply(this, arguments); }

function SvgNumberOne(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1h({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1e || (_path$1e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$T || (_path2$T = /*#__PURE__*/React.createElement("path", {
    d: "M11.222 17H13V7h-1.387C11.397 8.407 10.32 8.95 9 8.992v1.225h2.222V17z"
  })));
}

var _path$1d;

function _extends$1g() { _extends$1g = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1g.apply(this, arguments); }

function SvgOctagon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1g({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1d || (_path$1d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.98 2.879A3 3 0 019.1 2h5.8a3 3 0 012.12.879l4.101 4.1A3 3 0 0122 9.101v5.798a3 3 0 01-.879 2.122l-4.1 4.1a3 3 0 01-2.122.879H9.101a3 3 0 01-2.122-.879l-4.1-4.1A3 3 0 012 14.899V9.101a3 3 0 01.879-2.122l4.1-4.1zM9.1 4a1 1 0 00-.707.293l-4.1 4.1A1 1 0 004 9.101v5.798a1 1 0 00.293.708l4.1 4.1a1 1 0 00.708.293h5.798a1 1 0 00.708-.293l4.1-4.1A1 1 0 0020 14.9V9.1a1 1 0 00-.293-.707l-4.1-4.1A1 1 0 0014.9 4H9.1z",
    clipRule: "evenodd"
  })));
}

var _path$1c;

function _extends$1f() { _extends$1f = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1f.apply(this, arguments); }

function SvgPaperclip(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1f({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1c || (_path$1c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17.1 4.746a2.547 2.547 0 00-3.603 0l-8.231 8.232a4.32 4.32 0 006.11 6.11l8.594-8.594a1 1 0 011.414 1.414l-8.594 8.595a6.32 6.32 0 01-8.939-8.94l8.232-8.231a4.547 4.547 0 116.43 6.43l-8.287 8.288a2.774 2.774 0 01-3.922-3.922l7.73-7.73a1 1 0 111.414 1.414l-7.73 7.73a.774.774 0 101.094 1.094l8.287-8.288a2.547 2.547 0 000-3.602z",
    clipRule: "evenodd"
  })));
}

var _path$1b, _path2$S, _path3$k;

function _extends$1e() { _extends$1e = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1e.apply(this, arguments); }

function SvgPassport(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1e({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1b || (_path$1b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.5 10.5a3.5 3.5 0 117 0 3.5 3.5 0 01-7 0zM12 9a1.5 1.5 0 100 3 1.5 1.5 0 000-3z",
    clipRule: "evenodd"
  })), _path2$S || (_path2$S = /*#__PURE__*/React.createElement("path", {
    d: "M9 16a1 1 0 100 2h6a1 1 0 100-2H9z"
  })), _path3$k || (_path3$k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.23 4.362a.999.999 0 00-.23.656V21a1 1 0 001 1h14a1 1 0 001-1V5a1 1 0 00-1-1h-2.5V3a1 1 0 00-1.187-.982L4.83 4.014a.993.993 0 00-.601.348zM6 6v14h12V6H6z",
    clipRule: "evenodd"
  })));
}

var _path$1a;

function _extends$1d() { _extends$1d = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1d.apply(this, arguments); }

function SvgPhone(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1d({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1a || (_path$1a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.078 5.103C1.97 3.376 3.378 2 5.042 2h2.572c1.242 0 2.397.774 2.786 2.012.203.645.433 1.454.572 2.198.07.371.12.753.129 1.104.007.32-.016.752-.207 1.133-.42.842-.927 1.225-1.377 1.565-.067.05-.134.1-.198.152.753 1.087 1.346 1.823 1.997 2.426.642.594 1.374 1.09 2.436 1.657C14.133 13.787 14.846 13 16 13c.178 0 .437.041.642.076.243.041.546.099.875.163.66.13 1.45.297 2.126.442A2.986 2.986 0 0122 16.604V19c0 1.616-1.331 3.083-3.085 2.934-8.777-.745-15.805-7.615-16.796-16.32-.016-.145-.03-.324-.041-.51zM5.042 4c-.577 0-1 .468-.968.979.01.175.022.314.033.408.883 7.76 7.153 13.889 14.978 14.553.445.038.915-.35.915-.941v-2.395a.986.986 0 00-.776-.968c-.674-.144-1.45-.307-2.093-.434a32.773 32.773 0 00-.823-.154A7.595 7.595 0 0016 15a.482.482 0 00-.3.112c-.135.098-.275.249-.46.474a1.884 1.884 0 01-2.35.468c-1.196-.633-2.107-1.233-2.932-1.996-.82-.759-1.518-1.646-2.32-2.81-.535-.775-.478-1.909.34-2.573.173-.14.313-.248.428-.337.363-.28.493-.38.685-.756.002-.015.014-.08.01-.222a5.103 5.103 0 00-.095-.783c-.117-.628-.32-1.35-.514-1.965C8.382 4.259 8.04 4 7.614 4H5.042z",
    clipRule: "evenodd"
  })));
}

var _path$19, _path2$R;

function _extends$1c() { _extends$1c = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1c.apply(this, arguments); }

function SvgPieChart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1c({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$19 || (_path$19 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.864 3.502a1 1 0 01-.445 1.342 8 8 0 1010.75 10.71 1 1 0 111.791.891A10 10 0 0112 22C6.477 22 2 17.523 2 12a10 10 0 015.522-8.944 1 1 0 011.342.446z",
    clipRule: "evenodd"
  })), _path2$R || (_path2$R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 2.293A1 1 0 0112 2c5.523 0 10 4.477 10 10a1 1 0 01-1 1h-9a1 1 0 01-1-1V3a1 1 0 01.293-.707zM13 4.062V11h6.938A8.004 8.004 0 0013 4.062z",
    clipRule: "evenodd"
  })));
}

var _path$18, _path2$Q;

function _extends$1b() { _extends$1b = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1b.apply(this, arguments); }

function SvgPlaneBoard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1b({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$18 || (_path$18 = /*#__PURE__*/React.createElement("path", {
    d: "M16.604 9.666a1.078 1.078 0 111.078 1.867l-6.203 3.581-2.421-1.868a.337.337 0 01.037-.559 1.35 1.35 0 011.294-.03l.547.282 1.4-.809-1.945-.85a.466.466 0 01-.044-.832 1.864 1.864 0 011.302-.205l2.908.605 2.046-1.182z"
  })), _path2$Q || (_path2$Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 6a1 1 0 00-1 1v.997a.179.179 0 00.037.054.674.674 0 00.272.168 4.001 4.001 0 010 7.562.674.674 0 00-.272.168.178.178 0 00-.037.054V17a1 1 0 001 1h14a1 1 0 001-1V7a1 1 0 00-1-1H5zM2 7a3 3 0 013-3h14a3 3 0 013 3v10a3 3 0 01-3 3H5a3 3 0 01-3-3v-1c0-1.152.924-1.856 1.655-2.11a2.001 2.001 0 000-3.78C2.924 9.855 2 9.152 2 8V7z",
    clipRule: "evenodd"
  })));
}

var _path$17;

function _extends$1a() { _extends$1a = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1a.apply(this, arguments); }

function SvgPlugIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1a({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$17 || (_path$17 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17 2a1 1 0 00-1 1v2h-2V3a1 1 0 10-2 0v2h-1a2 2 0 00-2 2v2a6.002 6.002 0 005 5.917v3.333a1.75 1.75 0 11-3.5 0v-.808a3.75 3.75 0 10-7.5 0v.943a1 1 0 102 0v-.943a1.75 1.75 0 113.5 0v.808a3.75 3.75 0 107.5 0v-3.333A6.002 6.002 0 0021 9V7a2 2 0 00-2-2h-1V3a1 1 0 00-1-1zm2 5v2a4 4 0 01-8 0V7h8z",
    clipRule: "evenodd"
  })));
}

var _path$16;

function _extends$19() { _extends$19 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$19.apply(this, arguments); }

function SvgPlugOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$19({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$16 || (_path$16 = /*#__PURE__*/React.createElement("path", {
    d: "M3.707 2.293a1 1 0 00-1.414 1.414L14 15.414v2.836a1.75 1.75 0 11-3.5 0v-.808a3.75 3.75 0 10-7.5 0v.943a1 1 0 102 0v-.943a1.75 1.75 0 113.5 0v.808a3.75 3.75 0 007.5 0v-.836l4.293 4.293a1 1 0 001.414-1.414l-5.99-5.99-.02-.02-11.99-11.99zM16 3a1 1 0 112 0v2h1a2 2 0 012 2v2a5.988 5.988 0 01-2.056 4.522 1 1 0 11-1.315-1.507A3.988 3.988 0 0019 9V7h-7.25a1 1 0 110-2H12V3a1 1 0 112 0v2h2V3z"
  })));
}

var _path$15, _path2$P, _path3$j;

function _extends$18() { _extends$18 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$18.apply(this, arguments); }

function SvgPlusCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$18({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$15 || (_path$15 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$P || (_path2$P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 12a1 1 0 011-1h8a1 1 0 110 2H8a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path3$j || (_path3$j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 17a1 1 0 01-1-1V8a1 1 0 112 0v8a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$14, _path2$O, _path3$i;

function _extends$17() { _extends$17 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$17.apply(this, arguments); }

function SvgPlusSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$17({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$14 || (_path$14 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 013-3h14a3 3 0 013 3v14a3 3 0 01-3 3H5a3 3 0 01-3-3V5zm3-1a1 1 0 00-1 1v14a1 1 0 001 1h14a1 1 0 001-1V5a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })), _path2$O || (_path2$O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 12a1 1 0 011-1h8a1 1 0 110 2H8a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path3$i || (_path3$i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 17a1 1 0 01-1-1V8a1 1 0 112 0v8a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$13, _path2$N;

function _extends$16() { _extends$16 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$16.apply(this, arguments); }

function SvgPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$16({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$13 || (_path$13 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 11a1 1 0 011-1h14a1 1 0 110 2H5a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path2$N || (_path2$N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 19a1 1 0 01-1-1V4a1 1 0 112 0v14a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$12, _path2$M;

function _extends$15() { _extends$15 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$15.apply(this, arguments); }

function SvgPower(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$15({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$12 || (_path$12 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.866 5.57A1 1 0 018.5 6.936a7 7 0 106.999 0 1 1 0 011-1.731 9 9 0 11-9.001 0 1 1 0 011.367.365z",
    clipRule: "evenodd"
  })), _path2$M || (_path2$M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 011 1v6a1 1 0 11-2 0V3a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$11;

function _extends$14() { _extends$14 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$14.apply(this, arguments); }

function SvgProfile(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$14({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$11 || (_path$11 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a3 3 0 100 6 3 3 0 000-6zM7 7a5 5 0 1110 0A5 5 0 017 7zM3 19a5 5 0 015-5h8a5 5 0 015 5v2a1 1 0 11-2 0v-2a3 3 0 00-3-3H8a3 3 0 00-3 3v2a1 1 0 11-2 0v-2z",
    clipRule: "evenodd"
  })));
}

var _path$10, _path2$L, _path3$h, _path4$4;

function _extends$13() { _extends$13 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$13.apply(this, arguments); }

function SvgQrCodePhone(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$13({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$10 || (_path$10 = /*#__PURE__*/React.createElement("path", {
    d: "M10 7a1 1 0 00-2 0v3a1 1 0 102 0V7zm-2 6a1 1 0 011-1h6a1 1 0 110 2H9a1 1 0 01-1-1z"
  })), _path2$L || (_path2$L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 6a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V7a1 1 0 00-1-1h-3zm1 3V8h1v1h-1z",
    clipRule: "evenodd"
  })), _path3$h || (_path3$h = /*#__PURE__*/React.createElement("path", {
    d: "M12 15a1 1 0 011 1h1a1 1 0 112 0v1a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a1 1 0 011-1zm-2 1a1 1 0 10-2 0v1a1 1 0 102 0v-1z"
  })), _path4$4 || (_path4$4 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 2a3 3 0 00-3 3v14a3 3 0 003 3h8a3 3 0 003-3V5a3 3 0 00-3-3H8zM7 5a1 1 0 011-1h8a1 1 0 011 1v14a1 1 0 01-1 1H8a1 1 0 01-1-1V5z",
    clipRule: "evenodd"
  })));
}

var _path$$, _path2$K, _path3$g, _path4$3;

function _extends$12() { _extends$12 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$12.apply(this, arguments); }

function SvgQrCodeScanOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$12({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$$ || (_path$$ = /*#__PURE__*/React.createElement("path", {
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414A2.99 2.99 0 0119 22h-2a1 1 0 110-2h1.586l-1-1h-.336a.25.25 0 01-.25-.25v-.336L14.586 16h-.336a.25.25 0 01-.25-.25v-.336l-1-1v2.336a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25v-2.5a.25.25 0 01.25-.25h1.336l-1-1H9.25a.25.25 0 01-.25-.25v-1.5a.25.25 0 01.25-.25h.336l-1-1H6a1 1 0 01-1-1V6.414l-1-1V7a1 1 0 11-2 0V5c0-.462.105-.901.293-1.293a1 1 0 010-1.414zM20 7a1 1 0 102 0V5a3 3 0 00-3-3h-2a1 1 0 100 2h2a1 1 0 011 1v2zM2 19a3 3 0 003 3h2a1 1 0 100-2H5a1 1 0 01-1-1v-2a1 1 0 10-2 0v2z"
  })), _path2$K || (_path2$K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 15a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H6a1 1 0 01-1-1v-3zm2 1v1h1v-1H7z",
    clipRule: "evenodd"
  })), _path3$g || (_path3$g = /*#__PURE__*/React.createElement("path", {
    d: "M5.25 11a.25.25 0 00-.25.25v1.5c0 .138.112.25.25.25h1.5a.25.25 0 00.25-.25v-1.5a.25.25 0 00-.25-.25h-1.5zM11 5.25a.25.25 0 01.25-.25h1.5a.25.25 0 01.25.25v1.5a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25v-1.5zM13.25 17a.25.25 0 00-.25.25v1.5c0 .138.112.25.25.25h1.5a.25.25 0 00.25-.25v-1.5a.25.25 0 00-.25-.25h-1.5zM17 11.25a.25.25 0 01.25-.25h1.5a.25.25 0 01.25.25v1.5a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25v-1.5z"
  })), _path4$3 || (_path4$3 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.1 5A1.1 1.1 0 0014 6.1v2.8a1.1 1.1 0 001.1 1.1h2.8A1.1 1.1 0 0019 8.9V6.1A1.1 1.1 0 0017.9 5h-2.8zm.9 3V7h1v1h-1z",
    clipRule: "evenodd"
  })));
}

var _path$_, _path2$J, _path3$f, _path4$2;

function _extends$11() { _extends$11 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$11.apply(this, arguments); }

function SvgQrCodeScan(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$11({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$_ || (_path$_ = /*#__PURE__*/React.createElement("path", {
    d: "M20 17a1 1 0 112 0v2a3 3 0 01-3 3h-2a1 1 0 110-2h2a1 1 0 001-1v-2zM3 8a1 1 0 001-1V5a1 1 0 011-1h2a1 1 0 000-2H5a3 3 0 00-3 3v2a1 1 0 001 1zm0 8a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 110 2H5a3 3 0 01-3-3v-2a1 1 0 011-1zm18-8a1 1 0 01-1-1V5a1 1 0 00-1-1h-2a1 1 0 110-2h2a3 3 0 013 3v2a1 1 0 01-1 1z"
  })), _path2$J || (_path2$J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 5a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V6a1 1 0 00-1-1H6zm1 3V7h1v1H7zm-2 7a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H6a1 1 0 01-1-1v-3zm2 1v1h1v-1H7z",
    clipRule: "evenodd"
  })), _path3$f || (_path3$f = /*#__PURE__*/React.createElement("path", {
    d: "M5.25 11a.25.25 0 00-.25.25v1.5c0 .138.112.25.25.25h1.5a.25.25 0 00.25-.25v-1.5a.25.25 0 00-.25-.25h-1.5zM11 5.25a.25.25 0 01.25-.25h1.5a.25.25 0 01.25.25v2.5a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25v-2.5zm8 9v4.5a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25V16h-2.75a.25.25 0 01-.25-.25v-1.5a.25.25 0 01.25-.25h4.5a.25.25 0 01.25.25zM13.25 17a.25.25 0 00-.25.25v1.5c0 .138.112.25.25.25h1.5a.25.25 0 00.25-.25v-1.5a.25.25 0 00-.25-.25h-1.5zM16 11.25a.25.25 0 01.25-.25h2.5a.25.25 0 01.25.25v1.5a.25.25 0 01-.25.25h-2.5a.25.25 0 01-.25-.25v-1.5zM9.25 11a.25.25 0 00-.25.25v1.5c0 .138.112.25.25.25h4.5a.25.25 0 00.25-.25v-1.5a.25.25 0 00-.25-.25h-4.5zM11 14.25a.25.25 0 01.25-.25h1.5a.25.25 0 01.25.25v2.5a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25v-2.5z"
  })), _path4$2 || (_path4$2 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.1 5A1.1 1.1 0 0014 6.1v2.8a1.1 1.1 0 001.1 1.1h2.8A1.1 1.1 0 0019 8.9V6.1A1.1 1.1 0 0017.9 5h-2.8zm.9 3V7h1v1h-1z",
    clipRule: "evenodd"
  })));
}

var _path$Z, _path2$I, _path3$e, _path4$1, _path5, _path6, _path7;

function _extends$10() { _extends$10 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$10.apply(this, arguments); }

function SvgQrCode(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$10({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$Z || (_path$Z = /*#__PURE__*/React.createElement("path", {
    d: "M5.25 5a.25.25 0 00-.25.25v.5c0 .138.112.25.25.25h.5A.25.25 0 006 5.75v-.5A.25.25 0 005.75 5h-.5z"
  })), _path2$I || (_path2$I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 3.25C2 2.56 2.56 2 3.25 2h4.5C8.44 2 9 2.56 9 3.25v4.5C9 8.44 8.44 9 7.75 9h-4.5C2.56 9 2 8.44 2 7.75v-4.5zM4 4v3h3V4H4z",
    clipRule: "evenodd"
  })), _path3$e || (_path3$e = /*#__PURE__*/React.createElement("path", {
    d: "M5 18.25a.25.25 0 01.25-.25h.5a.25.25 0 01.25.25v.5a.25.25 0 01-.25.25h-.5a.25.25 0 01-.25-.25v-.5z"
  })), _path4$1 || (_path4$1 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 16.25c0-.69.56-1.25 1.25-1.25h4.5c.69 0 1.25.56 1.25 1.25v4.5C9 21.44 8.44 22 7.75 22h-4.5C2.56 22 2 21.44 2 20.75v-4.5zM4 17v3h3v-3H4z",
    clipRule: "evenodd"
  })), _path5 || (_path5 = /*#__PURE__*/React.createElement("path", {
    d: "M18 5.25a.25.25 0 01.25-.25h.5a.25.25 0 01.25.25v.5a.25.25 0 01-.25.25h-.5a.25.25 0 01-.25-.25v-.5z"
  })), _path6 || (_path6 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.25 2C15.56 2 15 2.56 15 3.25v4.5c0 .69.56 1.25 1.25 1.25h4.5C21.44 9 22 8.44 22 7.75v-4.5C22 2.56 21.44 2 20.75 2h-4.5zM17 7V4h3v3h-3z",
    clipRule: "evenodd"
  })), _path7 || (_path7 = /*#__PURE__*/React.createElement("path", {
    d: "M13.25 12a.25.25 0 00-.25.25v1.5c0 .138.112.25.25.25h4.5a.25.25 0 00.25-.25v-1.5a.25.25 0 00-.25-.25h-4.5zM6 10.25a.25.25 0 01.25-.25h6.5a.25.25 0 01.25.25v1.5a.25.25 0 01-.25.25H11v1.75a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25V12H6.25a.25.25 0 01-.25-.25v-1.5zM2.25 10a.25.25 0 00-.25.25v1.5c0 .138.112.25.25.25h1.5a.25.25 0 00.25-.25v-1.5a.25.25 0 00-.25-.25h-1.5zm15.75.25a.25.25 0 01.25-.25h1.5a.25.25 0 01.25.25v1.5a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25v-1.5zM15.25 17a.25.25 0 00-.25.25v1.5c0 .138.112.25.25.25H18v2.75c0 .138.112.25.25.25h3.5a.25.25 0 00.25-.25v-1.5a.25.25 0 00-.25-.25H20v-2.75a.25.25 0 00-.25-.25h-4.5zm5-5a.25.25 0 00-.25.25v3.5c0 .138.112.25.25.25h1.5a.25.25 0 00.25-.25v-3.5a.25.25 0 00-.25-.25h-1.5zM12 2.25a.25.25 0 01.25-.25h1.5a.25.25 0 01.25.25v1.5a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25v-1.5zm0 2V7h1.75a.25.25 0 01.25.25v1.5a.25.25 0 01-.25.25h-3.5a.25.25 0 01-.25-.25v-4.5a.25.25 0 01.25-.25h1.5a.25.25 0 01.25.25zM4.25 12a.25.25 0 00-.25.25v1.5c0 .138.112.25.25.25h1.5a.25.25 0 00.25-.25v-1.5a.25.25 0 00-.25-.25h-1.5zM13 20.25a.25.25 0 01.25-.25h1.5a.25.25 0 01.25.25v1.5a.25.25 0 01-.25.25h-1.5a.25.25 0 01-.25-.25v-1.5zM11.25 14a.25.25 0 00-.25.25v5.5c0 .138.112.25.25.25h1.5a.25.25 0 00.25-.25v-5.5a.25.25 0 00-.25-.25h-1.5z"
  })));
}

var _path$Y, _path2$H;

function _extends$$() { _extends$$ = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$$.apply(this, arguments); }

function SvgQuiz(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$$({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$Y || (_path$Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 7a1 1 0 011-1h13a1 1 0 110 2H8a1 1 0 01-1-1zm0 5a1 1 0 011-1h13a1 1 0 110 2H8a1 1 0 01-1-1zm0 5a1 1 0 011-1h13a1 1 0 110 2H8a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path2$H || (_path2$H = /*#__PURE__*/React.createElement("path", {
    d: "M4 7a1 1 0 11-2 0 1 1 0 012 0zm0 5a1 1 0 11-2 0 1 1 0 012 0zm0 5a1 1 0 11-2 0 1 1 0 012 0z"
  })));
}

var _path$X, _path2$G, _path3$d;

function _extends$_() { _extends$_ = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$_.apply(this, arguments); }

function SvgRadio(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$_({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$X || (_path$X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 11a1 1 0 100 2 1 1 0 000-2zm-3 1a3 3 0 116 0 3 3 0 01-6 0zm-.542-4.945a1 1 0 01.002 1.414A4.981 4.981 0 007 12a4.98 4.98 0 001.46 3.531 1 1 0 11-1.416 1.413A6.981 6.981 0 015 12c0-1.93.782-3.678 2.044-4.944a1 1 0 011.414-.001z",
    clipRule: "evenodd"
  })), _path2$G || (_path2$G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.559 4.286A1 1 0 015.573 5.7 8.968 8.968 0 003 12c0 2.453.98 4.676 2.573 6.3a1 1 0 11-1.429 1.4A10.967 10.967 0 011 12c0-2.998 1.2-5.717 3.144-7.7a1 1 0 011.415-.014zm9.735 2.769a1 1 0 00-.002 1.414A4.98 4.98 0 0116.752 12a4.981 4.981 0 01-1.46 3.531 1 1 0 001.416 1.413A6.982 6.982 0 0018.752 12c0-1.93-.782-3.678-2.044-4.944a1 1 0 00-1.414-.001z",
    clipRule: "evenodd"
  })), _path3$d || (_path3$d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.194 4.286A1 1 0 0018.18 5.7a8.968 8.968 0 012.572 6.3c0 2.453-.98 4.676-2.572 6.3a1 1 0 101.428 1.4 10.968 10.968 0 003.144-7.7c0-2.998-1.2-5.717-3.144-7.7a1 1 0 00-1.415-.014z",
    clipRule: "evenodd"
  })));
}

var _path$W, _path2$F;

function _extends$Z() { _extends$Z = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$Z.apply(this, arguments); }

function SvgRefresh(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$W || (_path$W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.707 2.293a1 1 0 010 1.414c-.978.978-2.171 2.286-3.115 3.758C4.64 8.948 4 10.505 4 12a8 8 0 108-8 1 1 0 110-2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12c0-2.04.86-3.981 1.908-5.615 1.056-1.645 2.363-3.07 3.385-4.092a1 1 0 011.414 0z",
    clipRule: "evenodd"
  })), _path2$F || (_path2$F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 3a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-2 0V4H3a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$V, _path2$E, _path3$c;

function _extends$Y() { _extends$Y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$Y.apply(this, arguments); }

function SvgRepeat(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$V || (_path$V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.707 14.293a1 1 0 010 1.414L4.414 18l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z",
    clipRule: "evenodd"
  })), _path2$E || (_path2$E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 18a1 1 0 011-1h14a3 3 0 003-3v-2a1 1 0 112 0v2a5 5 0 01-5 5H3a1 1 0 01-1-1zm15.293-8.293a1 1 0 010-1.414L19.586 6l-2.293-2.293a1 1 0 011.414-1.414l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414 0z",
    clipRule: "evenodd"
  })), _path3$c || (_path3$c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 6a1 1 0 01-1 1H7a3 3 0 00-3 3v2a1 1 0 11-2 0v-2a5 5 0 015-5h14a1 1 0 011 1z",
    clipRule: "evenodd"
  })));
}

var _path$U, _path2$D, _path3$b;

function _extends$X() { _extends$X = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$X.apply(this, arguments); }

function SvgRss(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$X({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$U || (_path$U = /*#__PURE__*/React.createElement("path", {
    d: "M5 20.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"
  })), _path2$D || (_path2$D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 11a1 1 0 011-1c6.075 0 11 4.925 11 11a1 1 0 11-2 0 9 9 0 00-9-9 1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path3$b || (_path3$b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 3a1 1 0 011-1c10.493 0 19 8.507 19 19a1 1 0 11-2 0c0-9.389-7.611-17-17-17a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$T;

function _extends$W() { _extends$W = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$W.apply(this, arguments); }

function SvgSave(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$W({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$T || (_path$T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 22a3 3 0 01-3-3V5a3 3 0 013-3h9.172a3 3 0 012.12.879l4.83 4.828A3 3 0 0122 9.828V19a3 3 0 01-3 3H5zM5 4a1 1 0 00-1 1v14a1 1 0 001 1h1v-6a1 1 0 011-1h10a1 1 0 011 1v6h1a1 1 0 001-1V9.828a1 1 0 00-.293-.707L15 4.414V8a1 1 0 01-1 1H8a1 1 0 01-1-1V4H5zm11 16v-5H8v5h8zM13 7V4H9v3h4z",
    clipRule: "evenodd"
  })));
}

var _path$S, _path2$C;

function _extends$V() { _extends$V = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$V.apply(this, arguments); }

function SvgSearch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$V({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$S || (_path$S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 100 14 7 7 0 000-14zm-9 7a9 9 0 1118 0 9 9 0 01-18 0z",
    clipRule: "evenodd"
  })), _path2$C || (_path2$C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.293 16.293a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$R, _path2$B;

function _extends$U() { _extends$U = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$U.apply(this, arguments); }

function SvgSend(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$U({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$R || (_path$R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.707 2.293a1 1 0 01.242 1.023l-6 18a1 1 0 01-1.882.043l-3.174-8.252L2.64 9.933a1 1 0 01.043-1.882l18-6a1 1 0 011.023.242zM5.961 9.067l6.065 2.333a1 1 0 01.574.574l2.333 6.065 4.486-13.458L5.96 9.067z",
    clipRule: "evenodd"
  })), _path2$B || (_path2$B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.207 2.793a1 1 0 010 1.414l-8.5 8.5a1 1 0 01-1.414-1.414l8.5-8.5a1 1 0 011.414 0z",
    clipRule: "evenodd"
  })));
}

var _path$Q, _path2$A;

function _extends$T() { _extends$T = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$T.apply(this, arguments); }

function SvgSettings1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$T({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$Q || (_path$Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 10.068a2 2 0 100 4 2 2 0 000-4zm-4 2a4 4 0 118 0 4 4 0 01-8 0z",
    clipRule: "evenodd"
  })), _path2$A || (_path2$A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.353 4.15c.674-2.776 4.62-2.776 5.294 0a.724.724 0 001.08.447c2.44-1.486 5.23 1.304 3.744 3.743a.724.724 0 00.448 1.08c2.775.674 2.775 4.621 0 5.295a.724.724 0 00-.448 1.08c1.486 2.439-1.305 5.23-3.744 3.744a.724.724 0 00-1.08.447c-.674 2.776-4.62 2.776-5.294 0a.724.724 0 00-1.08-.447c-2.44 1.486-5.23-1.305-3.744-3.744a.724.724 0 00-.448-1.08c-2.775-.674-2.775-4.62 0-5.294a.724.724 0 00.448-1.08C3.043 5.9 5.834 3.11 8.272 4.596a.724.724 0 001.08-.448zm3.35.471c-.178-.738-1.227-.738-1.407 0a2.724 2.724 0 01-4.064 1.684c-.648-.395-1.39.346-.995.995a2.724 2.724 0 01-1.684 4.064c-.737.18-.737 1.228 0 1.407a2.724 2.724 0 011.684 4.065c-.395.648.347 1.39.995.995a2.724 2.724 0 014.064 1.684c.18.737 1.229.737 1.408 0a2.724 2.724 0 014.064-1.684c.648.395 1.39-.347.995-.995a2.724 2.724 0 011.684-4.065c.737-.179.737-1.228 0-1.407A2.724 2.724 0 0117.763 7.3c.395-.649-.347-1.39-.995-.995a2.723 2.723 0 01-4.064-1.684z",
    clipRule: "evenodd"
  })));
}

var _path$P;

function _extends$S() { _extends$S = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$S.apply(this, arguments); }

function SvgSex(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$S({
    width: 24,
    height: 24,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$P || (_path$P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M18.5 3a1 1 0 100 2h.586l-1.829 1.828A5 5 0 009.5 11a3 3 0 11-1.547-2.626 1 1 0 00.97-1.748A5 5 0 105.5 15.9V17H5a1 1 0 100 2h.5v1a1 1 0 102 0v-1H8a1 1 0 100-2h-.5v-1.1a5.002 5.002 0 004-4.9 3 3 0 111.547 2.626 1 1 0 00-.97 1.748 5 5 0 006.595-7.132L20.5 6.415V7a1 1 0 102 0V4a1 1 0 00-1-1h-3z",
    fill: "#242423"
  })));
}

var _path$O, _path2$z;

function _extends$R() { _extends$R = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$R.apply(this, arguments); }

function SvgShareAndroid(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$R({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$O || (_path$O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18 4a2 2 0 100 4 2 2 0 000-4zm-4 2a4 4 0 118 0 4 4 0 01-8 0zm4 10a2 2 0 100 4 2 2 0 000-4zm-4 2a4 4 0 118 0 4 4 0 01-8 0zm-8-8a2 2 0 100 4 2 2 0 000-4zm-4 2a4 4 0 118 0 4 4 0 01-8 0z",
    clipRule: "evenodd"
  })), _path2$z || (_path2$z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.923 7.115a1 1 0 01-.538 1.308l-6 2.5a1 1 0 01-.77-1.846l6-2.5a1 1 0 011.308.538zm0 9.77a1 1 0 00-.538-1.308l-6-2.5a1 1 0 00-.77 1.846l6 2.5a1 1 0 001.308-.538z",
    clipRule: "evenodd"
  })));
}

var _path$N, _path2$y, _path3$a;

function _extends$Q() { _extends$Q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$Q.apply(this, arguments); }

function SvgShare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$N || (_path$N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.707 7.707a1 1 0 01-1.414 0L12 4.414 8.707 7.707a1 1 0 11-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })), _path2$y || (_path2$y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 011 1v12a1 1 0 11-2 0V3a1 1 0 011-1z",
    clipRule: "evenodd"
  })), _path3$a || (_path3$a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 11a1 1 0 011 1v7a1 1 0 001 1h12a1 1 0 001-1v-7a1 1 0 112 0v7a3 3 0 01-3 3H6a3 3 0 01-3-3v-7a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$M, _path2$x;

function _extends$P() { _extends$P = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$P.apply(this, arguments); }

function SvgShieldOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$P({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$M || (_path$M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.606 2.08a1 1 0 01.788 0l7 3A1 1 0 0120 6v6c0 .463-.05.915-.142 1.353a1 1 0 01-1.958-.408c.066-.313.1-.629.1-.945V6.66l-6-2.572-1.94.831a1 1 0 01-.787-1.838l2.333-1zM6 6.612A1 1 0 004.958 4.93l-.352.15A1 1 0 004 6v6c0 2.944 2.032 5.448 3.82 7.108a22.738 22.738 0 003.656 2.744l.02.011.005.003.002.002L12 21l-.496.868a1 1 0 00.992 0L12 21l.496.868h.002l.003-.002.008-.005.028-.017a14.122 14.122 0 00.466-.286c.307-.196.736-.482 1.23-.846.985-.723 2.257-1.775 3.352-3.064a1 1 0 00-1.524-1.296c-.959 1.13-2.099 2.077-3.012 2.75a21.58 21.58 0 01-1.049.723 20.736 20.736 0 01-2.82-2.183C7.468 16.052 6 14.056 6 12V6.611z",
    clipRule: "evenodd"
  })), _path2$x || (_path2$x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414l-18-18a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$L;

function _extends$O() { _extends$O = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$O.apply(this, arguments); }

function SvgShield(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$O({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$L || (_path$L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.606 2.08a1 1 0 01.788 0l7 3A1 1 0 0120 6v6c0 2.944-2.032 5.448-3.82 7.108a22.743 22.743 0 01-3.656 2.744l-.02.011-.005.003-.002.002L12 21l-.497.868-.002-.002-.006-.003-.019-.01a9.404 9.404 0 01-.305-.186 22.738 22.738 0 01-3.351-2.56C6.032 17.449 4 14.945 4 12V6a1 1 0 01.606-.92l7-3zM6 6.66V12c0 2.056 1.468 4.052 3.18 5.642A20.736 20.736 0 0012 19.825a20.732 20.732 0 002.82-2.183C16.532 16.052 18 14.056 18 12V6.66l-6-2.572-6 2.571zM12 21l-.497.868a1 1 0 00.993 0L12 21z",
    clipRule: "evenodd"
  })));
}

var _path$K, _path2$w;

function _extends$N() { _extends$N = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$N.apply(this, arguments); }

function SvgShoppingCart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$N({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$K || (_path$K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 2a1 1 0 000 2h1.62a1 1 0 01.98.798l1.42 6.904c.018.086.047.168.085.244.148.448.398.846.72 1.17l-.272.542C6.555 15.653 8.006 18 10.236 18H18a1 1 0 100-2h-7.764a1 1 0 01-.894-1.447l.285-.57c.107.011.215.017.325.017h7.311a3 3 0 002.852-2.069l.98-3A3 3 0 0018.243 5H7.683l-.125-.605A3 3 0 004.62 2H3zm5.2 5l.768 4.18a1 1 0 00.984.82h7.311a1 1 0 00.951-.69l.98-3A1 1 0 0018.243 7H8.2z",
    clipRule: "evenodd"
  })), _path2$w || (_path2$w = /*#__PURE__*/React.createElement("path", {
    d: "M12 20.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm4.5 1.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
  })));
}

var _path$J, _path2$v;

function _extends$M() { _extends$M = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$M.apply(this, arguments); }

function SvgSidebar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$M({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$J || (_path$J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 013-3h14a3 3 0 013 3v14a3 3 0 01-3 3H5a3 3 0 01-3-3V5zm3-1a1 1 0 00-1 1v14a1 1 0 001 1h14a1 1 0 001-1V5a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })), _path2$v || (_path2$v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 2a1 1 0 011 1v18a1 1 0 11-2 0V3a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$I, _path2$u;

function _extends$L() { _extends$L = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$L.apply(this, arguments); }

function SvgSlash(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$L({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$I || (_path$I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 110 16 8 8 0 010-16zm10 8c0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10 10-4.477 10-10z",
    clipRule: "evenodd"
  })), _path2$u || (_path2$u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.293 5.293a1 1 0 011.414 0l12 12a1 1 0 01-1.414 1.414l-12-12a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$H, _path2$t;

function _extends$K() { _extends$K = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$K.apply(this, arguments); }

function SvgSliders(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$K({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$H || (_path$H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 2a1 1 0 011 1v7a1 1 0 11-2 0V3a1 1 0 011-1zm0 11.5a1 1 0 011 1V21a1 1 0 11-2 0v-6.5a1 1 0 011-1zm7-4a1 1 0 011 1V21a1 1 0 11-2 0V10.5a1 1 0 011-1zM12 2a1 1 0 011 1v3a1 1 0 11-2 0V3a1 1 0 011-1zm7 0a1 1 0 011 1v11a1 1 0 11-2 0V3a1 1 0 011-1zm0 15a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1z",
    clipRule: "evenodd"
  })), _path2$t || (_path2$t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16 18a1 1 0 011-1h4a1 1 0 110 2h-4a1 1 0 01-1-1zm-7-8a1 1 0 011-1h4a1 1 0 110 2h-4a1 1 0 01-1-1zm-7 4a1 1 0 011-1h4a1 1 0 110 2H3a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$G, _path2$s, _circle, _circle2;

function _extends$J() { _extends$J = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$J.apply(this, arguments); }

function SvgSmile(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$J({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$G || (_path$G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$s || (_path2$s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.837 13.057a1 1 0 011.277.61 2.001 2.001 0 003.772 0 1 1 0 111.886.666 4.001 4.001 0 01-7.544 0 1 1 0 01.61-1.276z",
    clipRule: "evenodd"
  })), _circle || (_circle = /*#__PURE__*/React.createElement("circle", {
    cx: 9,
    cy: 9,
    r: 1
  })), _circle2 || (_circle2 = /*#__PURE__*/React.createElement("circle", {
    cx: 15,
    cy: 9,
    r: 1
  })));
}

var _path$F;

function _extends$I() { _extends$I = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$I.apply(this, arguments); }

function SvgSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$I({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$F || (_path$F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 013-3h14a3 3 0 013 3v14a3 3 0 01-3 3H5a3 3 0 01-3-3V5zm3-1a1 1 0 00-1 1v14a1 1 0 001 1h14a1 1 0 001-1V5a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })));
}

var _path$E;

function _extends$H() { _extends$H = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$H.apply(this, arguments); }

function SvgStar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$H({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$E || (_path$E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 01.905.575l2.555 5.442 5.691.87a1 1 0 01.565 1.687l-4.148 4.25.981 6.015a1 1 0 01-1.47 1.036L12 19.068l-5.079 2.807a1 1 0 01-1.47-1.036l.98-6.014-4.147-4.251a1 1 0 01.565-1.687l5.691-.87 2.555-5.442A1 1 0 0112 2zm0 3.354L10.124 9.35a1 1 0 01-.754.564l-4.295.656 3.14 3.22a1 1 0 01.272.859l-.73 4.48 3.76-2.079a1 1 0 01.967 0l3.76 2.078-.731-4.48a1 1 0 01.271-.859l3.141-3.219-4.295-.656a1 1 0 01-.754-.564L12 5.354z",
    clipRule: "evenodd"
  })));
}

var _path$D;

function _extends$G() { _extends$G = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$G.apply(this, arguments); }

function SvgSun(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$G({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$D || (_path$D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 9a3 3 0 100 6 3 3 0 000-6zm-5 3a5 5 0 1110 0 5 5 0 01-10 0zm5 6.5a1 1 0 00-1 1V21a1 1 0 102 0v-1.5a1 1 0 00-1-1zM12 2a1 1 0 00-1 1v1.5a1 1 0 102 0V3a1 1 0 00-1-1zm6.5 10a1 1 0 001 1H21a1 1 0 100-2h-1.5a1 1 0 00-1 1zM2 12a1 1 0 001 1h1.5a1 1 0 100-2H3a1 1 0 00-1 1zm14.596-4.596a1 1 0 001.414 0l1.061-1.06a1 1 0 00-1.414-1.415l-1.06 1.06a1 1 0 000 1.415zM4.929 19.071a1 1 0 001.414 0l1.06-1.06a1 1 0 10-1.413-1.415l-1.061 1.06a1 1 0 000 1.415zM7.404 7.404a1 1 0 000-1.414l-1.06-1.061a1 1 0 00-1.415 1.414l1.06 1.06a1 1 0 001.415 0zm11.667 11.667a1 1 0 000-1.414l-1.06-1.06a1 1 0 00-1.415 1.413l1.06 1.061a1 1 0 001.415 0z",
    clipRule: "evenodd"
  })));
}

var _path$C, _path2$r;

function _extends$F() { _extends$F = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$F.apply(this, arguments); }

function SvgSunrise(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$F({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$C || (_path$C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 14a3 3 0 00-3 3 1 1 0 11-2 0 5 5 0 0110 0 1 1 0 11-2 0 3 3 0 00-3-3zm6.5 3a1 1 0 001 1H21a1 1 0 100-2h-1.5a1 1 0 00-1 1zM2 17a1 1 0 001 1h1.5a1 1 0 100-2H3a1 1 0 00-1 1zm0 4a1 1 0 001 1h18a1 1 0 100-2H3a1 1 0 00-1 1zm14.596-8.596a1 1 0 001.414 0l1.061-1.06a1 1 0 10-1.414-1.415l-1.06 1.06a1 1 0 000 1.415zm-9.192 0a1 1 0 000-1.414l-1.06-1.061a1 1 0 00-1.415 1.414l1.06 1.06a1 1 0 001.415 0zm8.303-5.697a1 1 0 01-1.414 0L12 4.414 9.707 6.707a1 1 0 11-1.414-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })), _path2$r || (_path2$r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 011 1v6a1 1 0 01-2 0V3a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$B, _path2$q;

function _extends$E() { _extends$E = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$E.apply(this, arguments); }

function SvgSunset(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$E({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$B || (_path$B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 14a3 3 0 00-3 3 1 1 0 11-2 0 5 5 0 0110 0 1 1 0 11-2 0 3 3 0 00-3-3zm6.5 3a1 1 0 001 1H21a1 1 0 100-2h-1.5a1 1 0 00-1 1zM2 17a1 1 0 001 1h1.5a1 1 0 100-2H3a1 1 0 00-1 1zm0 4a1 1 0 001 1h18a1 1 0 100-2H3a1 1 0 00-1 1zm14.596-8.596a1 1 0 001.414 0l1.061-1.06a1 1 0 10-1.414-1.415l-1.06 1.06a1 1 0 000 1.415zm-9.192 0a1 1 0 000-1.414l-1.06-1.061a1 1 0 00-1.415 1.414l1.06 1.06a1 1 0 001.415 0zm8.303-7.111a1 1 0 00-1.414 0L12 7.586 9.707 5.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 000-1.414z",
    clipRule: "evenodd"
  })), _path2$q || (_path2$q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 10a1 1 0 001-1V3a1 1 0 00-2 0v6a1 1 0 001 1z",
    clipRule: "evenodd"
  })));
}

var _path$A, _path2$p, _path3$9;

function _extends$D() { _extends$D = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$D.apply(this, arguments); }

function SvgSync(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$D({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$A || (_path$A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 00-8 8 1 1 0 11-2 0C2 6.477 6.477 2 12 2c3.936 0 7.264 2.272 8.895 5.555a1 1 0 11-1.79.89C17.79 5.801 15.132 4 12 4z",
    clipRule: "evenodd"
  })), _path2$p || (_path2$p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20 2a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 110-2h4V3a1 1 0 011-1zm-8 18a8 8 0 008-8 1 1 0 112 0c0 5.523-4.477 10-10 10-3.936 0-7.264-2.272-8.896-5.555a1 1 0 111.792-.89C6.21 18.199 8.868 20 12 20z",
    clipRule: "evenodd"
  })), _path3$9 || (_path3$9 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 22a1 1 0 01-1-1v-5a1 1 0 011-1h5a1 1 0 110 2H5v4a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path$z;

function _extends$C() { _extends$C = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$C.apply(this, arguments); }

function SvgSyringe(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$C({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$z || (_path$z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.492 4.521l-.007-.006-.006-.007-2.115-2.115a1 1 0 00-1.414 1.415l1.414 1.414-1.414 1.414-1.414-1.414-.002-.002-.706-.705a1 1 0 10-1.414 1.414l-7.778 7.778a3 3 0 000 4.243l-2.121 2.12a1 1 0 101.414 1.414l2.121-2.121a3 3 0 004.243 0l2.12-2.12.001-.002c.001 0 .002 0 .002-.002l2.82-2.819.007-.007.007-.007 2.821-2.821a1 1 0 001.414-1.415l-.705-.705-.002-.002-1.414-1.414 1.414-1.414 1.415 1.414a1 1 0 101.414-1.414L19.492 4.52zm-7.785 10.6l1.414-1.414-.707-.707a1 1 0 011.415-1.414l.707.707 2.12-2.121-2.827-2.829-7.779 7.778a1 1 0 000 1.414l1.415 1.415a1 1 0 001.414 0l1.414-1.415-.707-.707A1 1 0 1111 14.414l.707.707z",
    clipRule: "evenodd"
  })));
}

var _path$y;

function _extends$B() { _extends$B = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$B.apply(this, arguments); }

function SvgTable(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$B({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$y || (_path$y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.729 4.029A2 2 0 016.477 3h11.046a2 2 0 011.748 1.029l2.778 5c.619 1.114.047 2.442-1.049 2.848V20a1 1 0 11-2 0v-8h-2v5a1 1 0 11-2 0v-5H9v5a1 1 0 11-2 0v-5H5v8a1 1 0 11-2 0v-8.123c-1.096-.406-1.668-1.734-1.049-2.848l2.778-5zM20.301 10l-2.778-5H6.477L3.7 10h16.6z",
    clipRule: "evenodd"
  })));
}

var _path$x, _path2$o;

function _extends$A() { _extends$A = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$A.apply(this, arguments); }

function SvgTag(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$A({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$x || (_path$x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.01 4A1.01 1.01 0 004 5.01v5.684c0 .267.106.524.296.713l8.297 8.297a1.01 1.01 0 001.427 0l5.684-5.684a1.01 1.01 0 000-1.427l-8.297-8.297A1.01 1.01 0 0010.694 4H5.01zM2 5.01A3.01 3.01 0 015.01 2h5.684a3.01 3.01 0 012.128.881l8.297 8.297a3.01 3.01 0 010 4.257l-5.684 5.684a3.01 3.01 0 01-4.257 0l-8.297-8.297A3.01 3.01 0 012 10.694V5.01z",
    clipRule: "evenodd"
  })), _path2$o || (_path2$o = /*#__PURE__*/React.createElement("path", {
    d: "M8.11 7.055a1.005 1.005 0 11-2.01 0 1.005 1.005 0 012.01 0z"
  })));
}

var _path$w, _path2$n;

function _extends$z() { _extends$z = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$z.apply(this, arguments); }

function SvgTemperatureOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$z({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$w || (_path$w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.707 2.293a1 1 0 00-1.414 1.414L6 7.414v4.084a.073.073 0 01-.006.015.246.246 0 01-.055.07 6 6 0 109.924 5.695l4.43 4.43a1 1 0 001.414-1.415l-18-18zM13.95 15.364L11 12.414v.757a3.001 3.001 0 11-2 0v-2.757l-1-1V11.5c0 .645-.31 1.19-.707 1.555a4 4 0 106.657 2.31zM9 16a1 1 0 112 0 1 1 0 01-2 0z",
    clipRule: "evenodd"
  })), _path2$n || (_path2$n = /*#__PURE__*/React.createElement("path", {
    d: "M9.2 4.166A2 2 0 0112 6v1.25a1 1 0 102 0V6a4 4 0 00-5.6-3.667 1 1 0 00.8 1.833zM16 5a1 1 0 100 2h3a1 1 0 100-2h-3zm0 6a1 1 0 110-2h3a1 1 0 110 2h-3z"
  })));
}

var _path$v, _path2$m, _path3$8;

function _extends$y() { _extends$y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$y.apply(this, arguments); }

function SvgTemperature(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$y({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$v || (_path$v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 13.17a3.001 3.001 0 102 0V8a1 1 0 10-2 0v5.17zM10 15a1 1 0 100 2 1 1 0 000-2z",
    clipRule: "evenodd"
  })), _path2$m || (_path2$m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 6a4 4 0 118 0v5.498a.08.08 0 00.006.015.247.247 0 00.055.07 6 6 0 11-8.122 0 .246.246 0 00.055-.07.073.073 0 00.006-.015V6zm4-2a2 2 0 00-2 2v5.5c0 .645-.31 1.19-.707 1.555a4 4 0 105.414 0C12.31 12.69 12 12.145 12 11.5V6a2 2 0 00-2-2z",
    clipRule: "evenodd"
  })), _path3$8 || (_path3$8 = /*#__PURE__*/React.createElement("path", {
    d: "M15 6a1 1 0 011-1h3a1 1 0 110 2h-3a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2h-3z"
  })));
}

var _path$u;

function _extends$x() { _extends$x = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$x.apply(this, arguments); }

function SvgTennis(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$x({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$u || (_path$u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12zm10-8a8.13 8.13 0 00-.519.017c.395 2.476.077 4.92-1.118 7.076-1.195 2.156-3.1 3.72-5.41 4.698a7.999 7.999 0 007.579 4.192c-.393-2.474-.074-4.914 1.12-7.067 1.194-2.154 3.095-3.718 5.4-4.695A7.999 7.999 0 0012 4zm7.77 6.089c-1.93.832-3.435 2.111-4.369 3.796-.934 1.685-1.222 3.64-.904 5.718a8.003 8.003 0 005.273-9.514zM4 12a8.003 8.003 0 015.516-7.607c.32 2.083.033 4.042-.902 5.73-.936 1.688-2.445 2.97-4.381 3.801A8.018 8.018 0 014 12z",
    clipRule: "evenodd"
  })));
}

var _path$t, _path2$l;

function _extends$w() { _extends$w = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$w.apply(this, arguments); }

function SvgThumbsDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$w({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$t || (_path$t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 11a3 3 0 01-3 3h-3a1 1 0 01-1-1V3a1 1 0 011-1h3a3 3 0 013 3v6zm-3 1a1 1 0 001-1V5a1 1 0 00-1-1h-2v8h2z",
    clipRule: "evenodd"
  })), _path2$l || (_path2$l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.677 21.376a1 1 0 01-.927.624c-2.195 0-3.465-.907-4.13-1.97A4.181 4.181 0 018 18.029V15H5.396a3 3 0 01-2.951-3.537l1.273-7A3 3 0 016.669 2H16a1 1 0 011 1v10c0 .129-.025.257-.073.376l-3.25 8zM10 17.991v.001l.003.048a2.181 2.181 0 00.313.93c.232.37.7.85 1.777.99L15 12.805V4H6.67a1 1 0 00-.985.821l-1.272 7A1 1 0 005.396 13H8a2 2 0 012 2v2.99z",
    clipRule: "evenodd"
  })));
}

var _path$s, _path2$k;

function _extends$v() { _extends$v = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$v.apply(this, arguments); }

function SvgThumbsUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$v({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$s || (_path$s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 13a3 3 0 013-3h3a1 1 0 011 1v10a1 1 0 01-1 1H5a3 3 0 01-3-3v-6zm3-1a1 1 0 00-1 1v6a1 1 0 001 1h2v-8H5z",
    clipRule: "evenodd"
  })), _path2$k || (_path2$k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.323 2.624A1 1 0 0111.25 2c2.195 0 3.465.907 4.13 1.97A4.179 4.179 0 0116 5.971V9h2.604a3 3 0 012.951 3.537l-1.273 7A3 3 0 0117.331 22H8a1 1 0 01-1-1V11a1 1 0 01.074-.376l3.25-8zM14 6.009v-.001l-.003-.048a2.18 2.18 0 00-.313-.93c-.232-.37-.7-.85-1.777-.99L9 11.195V20h8.33a1 1 0 00.985-.821l1.272-7A1 1 0 0018.604 11H16a2 2 0 01-2-2V6.01z",
    clipRule: "evenodd"
  })));
}

var _path$r, _path2$j, _path3$7, _path4;

function _extends$u() { _extends$u = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$u.apply(this, arguments); }

function SvgTicketGroup(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$u({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$r || (_path$r = /*#__PURE__*/React.createElement("path", {
    d: "M7.438 12a1 1 0 100 2h1.125a1 1 0 100-2H7.437z"
  })), _path2$j || (_path2$j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 21a3 3 0 01-3-3V6a3 3 0 013-3c.58 0 1.047.253 1.38.539.33.281.572.63.742.94a1 1 0 001.756 0c.17-.31.413-.659.741-.94C9.953 3.253 10.42 3 11 3a3 3 0 013 3v12a3 3 0 01-3 3c-.58 0-1.047-.253-1.38-.539a3.377 3.377 0 01-.742-.94 1 1 0 00-1.756 0c-.17.31-.413.659-.741.94-.334.286-.8.539-1.381.539zm-.006-2a.302.302 0 00.085-.057 1.39 1.39 0 00.288-.382 3 3 0 015.266 0c.095.174.199.305.288.382.046.04.074.053.085.057A1 1 0 0012 18v-4h-.063a1 1 0 110-2H12V6a1 1 0 00-.994-1 .302.302 0 00-.085.057 1.39 1.39 0 00-.288.382 3 3 0 01-5.266 0 1.403 1.403 0 00-.288-.382A.302.302 0 004.994 5 1 1 0 004 6v6h.063a1 1 0 110 2H4v4a1 1 0 00.994 1z",
    clipRule: "evenodd"
  })), _path3$7 || (_path3$7 = /*#__PURE__*/React.createElement("path", {
    d: "M17.514 5.363a5.087 5.087 0 01-.433.32A1 1 0 1116 4a3.23 3.23 0 00.26-.196l.053-.042c.095-.077.216-.175.337-.263.279-.203.746-.5 1.351-.5h1a3 3 0 013 3v12a3 3 0 01-3 3h-1c-.605 0-1.072-.297-1.351-.5a8.44 8.44 0 01-.337-.263l-.052-.042A3.229 3.229 0 0016 20a1 1 0 111.081-1.681 4.9 4.9 0 01.433.319l.067.054c.095.077.168.136.245.192a.92.92 0 00.192.117H19a1 1 0 001-1v-4.002a1 1 0 010-1.996V6a1 1 0 00-1-1h-.982a.92.92 0 00-.192.117c-.077.056-.15.115-.245.192l-.067.054z"
  })), _path4 || (_path4 = /*#__PURE__*/React.createElement("path", {
    d: "M16 13a1 1 0 011-1h.438a1 1 0 110 2H17a1 1 0 01-1-1z"
  })));
}

var _path$q, _path2$i;

function _extends$t() { _extends$t = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$t.apply(this, arguments); }

function SvgTicket(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$t({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$q || (_path$q = /*#__PURE__*/React.createElement("path", {
    d: "M11.602 11.602a1 1 0 10-1.414 1.415l.796.795a1 1 0 001.414-1.414l-.796-.796z"
  })), _path2$i || (_path2$i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.808 11.293a3 3 0 000 4.243l.707.707c.41.41.92.562 1.357.595.431.033.85-.041 1.189-.14a1 1 0 011.242 1.241c-.1.34-.174.758-.141 1.189.033.438.185.947.595 1.357l.707.707a3 3 0 004.243 0l8.485-8.485a3 3 0 000-4.243l-.707-.707c-.41-.41-.92-.562-1.357-.595a3.376 3.376 0 00-1.189.14 1 1 0 01-1.242-1.241c.1-.34.174-.758.141-1.189-.033-.438-.185-.947-.595-1.357l-.707-.707a3 3 0 00-4.243 0l-8.485 8.485zm1.414 2.828a1 1 0 010-1.414l2.923-2.923c.044.164.13.32.259.448l.398.398a1 1 0 001.414-1.414l-.398-.398a.995.995 0 00-.448-.259l4.337-4.337a1 1 0 011.414 0l.703.703c.005.01.015.039.02.1.009.117-.01.284-.066.474A3 3 0 0018.5 9.222c.19-.055.357-.075.474-.066.061.005.09.015.1.02l.703.703a1 1 0 010 1.414L15.44 15.63a.996.996 0 00-.259-.448l-.398-.398a1 1 0 00-1.414 1.414l.398.398a.995.995 0 00.448.26l-2.923 2.922a1 1 0 01-1.414 0l-.703-.703a.302.302 0 01-.02-.1c-.009-.117.01-.284.066-.474A3 3 0 005.5 14.778c-.19.055-.356.075-.474.066a.298.298 0 01-.1-.02l-.703-.703zm10.6-9.201zM9.177 19.08c0-.002 0-.002-.002-.003l.003.003z",
    clipRule: "evenodd"
  })));
}

var _path$p;

function _extends$s() { _extends$s = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$s.apply(this, arguments); }

function SvgTool(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$s({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$p || (_path$p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.604 4.061a5.197 5.197 0 00-5.608 7.103 1.524 1.524 0 01-.323 1.657l-5.362 5.362a1.064 1.064 0 001.507 1.505l5.36-5.36a1.527 1.527 0 011.658-.324 5.196 5.196 0 007.103-5.608L17.7 10.633a2.031 2.031 0 01-2.873-.001l-1.46-1.46a2.032 2.032 0 010-2.874l2.236-2.237zm-5.886.046a7.197 7.197 0 018.19-1.406 1 1 0 01.275 1.61l-3.401 3.401a.032.032 0 000 .046l1.46 1.46a.031.031 0 00.045 0l3.402-3.401a1 1 0 011.61.276 7.198 7.198 0 01-8.93 9.873l-5.137 5.136a3.064 3.064 0 01-4.335-4.333l5.137-5.138a7.195 7.195 0 011.684-7.524z",
    clipRule: "evenodd"
  })));
}

var _path$o, _path2$h, _path3$6;

function _extends$r() { _extends$r = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$r.apply(this, arguments); }

function SvgTrash2(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$r({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$o || (_path$o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 7a1 1 0 011-1h12a1 1 0 011 1v12a3 3 0 01-3 3H8a3 3 0 01-3-3V7zm2 1v11a1 1 0 001 1h8a1 1 0 001-1V8H7z",
    clipRule: "evenodd"
  })), _path2$h || (_path2$h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 7a1 1 0 001 1h8a1 1 0 001-1V5a3 3 0 00-3-3h-4a3 3 0 00-3 3v2zm2-1V5a1 1 0 011-1h4a1 1 0 011 1v1H9z",
    clipRule: "evenodd"
  })), _path3$6 || (_path3$6 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 7a1 1 0 011-1h16a1 1 0 110 2H4a1 1 0 01-1-1zm7 3a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1zm4 0a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$n, _path2$g;

function _extends$q() { _extends$q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$q.apply(this, arguments); }

function SvgTrendingDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$q({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$n || (_path$n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14 17a1 1 0 011-1h5v-5a1 1 0 112 0v6a1 1 0 01-1 1h-6a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path2$g || (_path2$g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 6.293a1 1 0 011.414 0L9 11.586l3.293-3.293a1 1 0 011.414 0l7.9 7.9a1 1 0 01-1.415 1.414L13 10.414l-3.293 3.293a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$m, _path2$f;

function _extends$p() { _extends$p = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$p.apply(this, arguments); }

function SvgTrendingUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$p({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$m || (_path$m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14 7a1 1 0 001 1h5v5a1 1 0 102 0V7a1 1 0 00-1-1h-6a1 1 0 00-1 1z",
    clipRule: "evenodd"
  })), _path2$f || (_path2$f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 17.707a1 1 0 001.414 0L9 12.414l3.293 3.293a1 1 0 001.414 0l7.9-7.9a1 1 0 00-1.415-1.414L13 13.586l-3.293-3.293a1 1 0 00-1.414 0l-6 6a1 1 0 000 1.414z",
    clipRule: "evenodd"
  })));
}

var _path$l;

function _extends$o() { _extends$o = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$o.apply(this, arguments); }

function SvgTriangle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$o({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$l || (_path$l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.674 4.883c1.48-2.51 5.163-2.51 6.643 0l6.15 10.435C22.974 17.872 21.07 21 18.148 21H5.845c-2.923 0-4.827-3.128-3.322-5.682L8.674 4.883zM13.595 5.9c-.707-1.199-2.492-1.199-3.199 0l-6.15 10.435C3.563 17.491 4.393 19 5.844 19h12.301c1.451 0 2.282-1.51 1.6-2.666L13.595 5.9z",
    clipRule: "evenodd"
  })));
}

var _path$k, _path2$e;

function _extends$n() { _extends$n = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$n.apply(this, arguments); }

function SvgUnlock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$n({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$k || (_path$k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 13a3 3 0 013-3h12a3 3 0 013 3v6a3 3 0 01-3 3H6a3 3 0 01-3-3v-6zm3-1a1 1 0 00-1 1v6a1 1 0 001 1h12a1 1 0 001-1v-6a1 1 0 00-1-1H6z",
    clipRule: "evenodd"
  })), _path2$e || (_path2$e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 7a5 5 0 0110 0 1 1 0 11-2 0 3 3 0 10-6 0v3a1 1 0 11-2 0V7z",
    clipRule: "evenodd"
  })));
}

var _path$j, _path2$d, _path3$5;

function _extends$m() { _extends$m = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$m.apply(this, arguments); }

function SvgUploadCloud(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$m({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$j || (_path$j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 4a6 6 0 00-4.659 9.782 1 1 0 11-1.551 1.261 8 8 0 1113.973-6.985 5.001 5.001 0 012.755 8.495 1 1 0 01-1.407-1.421A3 3 0 0017 10c-.544 0-.99-.4-1.07-.919A6.002 6.002 0 0010 4z",
    clipRule: "evenodd"
  })), _path2$d || (_path2$d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.707 16.707a1 1 0 01-1.414 0L12 13.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })), _path3$5 || (_path3$5 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 11a1 1 0 011 1v9a1 1 0 01-2 0v-9a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$i, _path2$c, _path3$4;

function _extends$l() { _extends$l = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$l.apply(this, arguments); }

function SvgUpload(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$l({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$i || (_path$i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.707 9.707a1 1 0 01-1.414 0L12 4.414 6.707 9.707a1 1 0 01-1.414-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 010 1.414z",
    clipRule: "evenodd"
  })), _path2$c || (_path2$c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 011 1v13a1 1 0 11-2 0V3a1 1 0 011-1z",
    clipRule: "evenodd"
  })), _path3$4 || (_path3$4 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 15a1 1 0 011 1v3a1 1 0 001 1h14a1 1 0 001-1v-3a1 1 0 112 0v3a3 3 0 01-3 3H5a3 3 0 01-3-3v-3a1 1 0 011-1z",
    clipRule: "evenodd"
  })));
}

var _path$h;

function _extends$k() { _extends$k = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$k.apply(this, arguments); }

function SvgUserCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$k({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$h || (_path$h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.707 9.293a1 1 0 00-1.414 0L17 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 000-1.414zM9 4a3 3 0 100 6 3 3 0 000-6zM4 7a5 5 0 1110 0A5 5 0 014 7zM2 19a5 5 0 015-5h4a5 5 0 015 5v2a1 1 0 11-2 0v-2a3 3 0 00-3-3H7a3 3 0 00-3 3v2a1 1 0 11-2 0v-2z",
    clipRule: "evenodd"
  })));
}

var _path$g;

function _extends$j() { _extends$j = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$j.apply(this, arguments); }

function SvgUserMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$j({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$g || (_path$g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14 12a1 1 0 001 1h6a1 1 0 100-2h-6a1 1 0 00-1 1zM9 4a3 3 0 100 6 3 3 0 000-6zM4 7a5 5 0 1110 0A5 5 0 014 7zM2 19a5 5 0 015-5h4a5 5 0 015 5v2a1 1 0 11-2 0v-2a3 3 0 00-3-3H7a3 3 0 00-3 3v2a1 1 0 11-2 0v-2z",
    clipRule: "evenodd"
  })));
}

var _path$f, _path2$b;

function _extends$i() { _extends$i = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$i.apply(this, arguments); }

function SvgUserPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$i({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$f || (_path$f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18 16a1 1 0 001-1V9a1 1 0 10-2 0v6a1 1 0 001 1z",
    clipRule: "evenodd"
  })), _path2$b || (_path2$b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14 12a1 1 0 001 1h6a1 1 0 100-2h-6a1 1 0 00-1 1zM9 4a3 3 0 100 6 3 3 0 000-6zM4 7a5 5 0 1110 0A5 5 0 014 7zM2 19a5 5 0 015-5h4a5 5 0 015 5v2a1 1 0 11-2 0v-2a3 3 0 00-3-3H7a3 3 0 00-3 3v2a1 1 0 11-2 0v-2z",
    clipRule: "evenodd"
  })));
}

var _path$e, _path2$a, _path3$3;

function _extends$h() { _extends$h = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$h.apply(this, arguments); }

function SvgUserPrivacy(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$h({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$e || (_path$e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 4a3 3 0 100 6 3 3 0 000-6zM3 7a5 5 0 1110 0A5 5 0 013 7zM2 19a5 5 0 015-5h1a1 1 0 110 2H7a3 3 0 00-3 3v2a1 1 0 11-2 0v-2zm8-2a3 3 0 013-3h6a3 3 0 013 3v2a3 3 0 01-3 3h-6a3 3 0 01-3-3v-2zm3-1a1 1 0 00-1 1v2a1 1 0 001 1h6a1 1 0 001-1v-2a1 1 0 00-1-1h-6z",
    clipRule: "evenodd"
  })), _path2$a || (_path2$a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 13a3 3 0 013-3h2a3 3 0 013 3v2a1 1 0 11-2 0v-2a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 11-2 0v-2z",
    clipRule: "evenodd"
  })), _path3$3 || (_path3$3 = /*#__PURE__*/React.createElement("path", {
    d: "M17 18a1 1 0 11-2 0 1 1 0 012 0z"
  })));
}

var _path$d, _path2$9;

function _extends$g() { _extends$g = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$g.apply(this, arguments); }

function SvgUserTime(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$g({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$d || (_path$d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16 12a4 4 0 100 8 4 4 0 000-8zm-6 4a6 6 0 1112 0 6 6 0 01-12 0z",
    clipRule: "evenodd"
  })), _path2$9 || (_path2$9 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16 13a1 1 0 011 1v1h1a1 1 0 110 2h-2a1 1 0 01-1-1v-2a1 1 0 011-1zM8 4a3 3 0 100 6 3 3 0 000-6zM3 7a5 5 0 1110 0A5 5 0 013 7zM2 19a5 5 0 015-5h1a1 1 0 110 2H7a3 3 0 00-3 3v2a1 1 0 11-2 0v-2z",
    clipRule: "evenodd"
  })));
}

var _path$c;

function _extends$f() { _extends$f = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$f.apply(this, arguments); }

function SvgUserX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$f({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$c || (_path$c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.192 9.293a1 1 0 010 1.414l-1.535 1.536 1.293 1.293a1 1 0 01-1.415 1.414l-1.292-1.293-1.293 1.293a1 1 0 01-1.414-1.414l1.292-1.293-1.535-1.536a1 1 0 011.414-1.414l1.536 1.535 1.535-1.535a1 1 0 011.414 0zM9 4a3 3 0 100 6 3 3 0 000-6zM4 7a5 5 0 1110 0A5 5 0 014 7zM2 19a5 5 0 015-5h4a5 5 0 015 5v2a1 1 0 11-2 0v-2a3 3 0 00-3-3H7a3 3 0 00-3 3v2a1 1 0 11-2 0v-2z",
    clipRule: "evenodd"
  })));
}

var _path$b;

function _extends$e() { _extends$e = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$e.apply(this, arguments); }

function SvgUsers(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$e({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$b || (_path$b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 4a3 3 0 100 6 3 3 0 000-6zM4 7a5 5 0 1110 0A5 5 0 014 7zm11.134-3.966a1 1 0 011.367-.364A4.998 4.998 0 0119 7a4.998 4.998 0 01-2.5 4.33 1 1 0 11-1-1.73 3.003 3.003 0 000-5.2 1 1 0 01-.366-1.366zM2 19a5 5 0 015-5h4a5 5 0 015 5v2a1 1 0 11-2 0v-2a3 3 0 00-3-3H7a3 3 0 00-3 3v2a1 1 0 11-2 0v-2zm14-4a1 1 0 011-1 5 5 0 015 5v2a1 1 0 11-2 0v-2a3 3 0 00-3-3 1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var _path$a, _path2$8, _path3$2;

function _extends$d() { _extends$d = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$d.apply(this, arguments); }

function SvgWalking(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$d({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$a || (_path$a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.787 8.542a1 1 0 01.67 1.245l-1.478 4.929-.49 3.425a.999.999 0 01-.157.414l-2 3a1 1 0 01-1.664-1.11l1.874-2.811.468-3.275a.998.998 0 01.032-.146l1.5-5a1 1 0 011.245-.67z",
    clipRule: "evenodd"
  })), _path2$8 || (_path2$8 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.793 7.793a1 1 0 01.95-.263l2 .5a1 1 0 01.718.695l.866 3.029 2.044.818a1 1 0 01-.742 1.857l-2.5-1a1 1 0 01-.59-.654l-.842-2.945-.89-.223-1.933 1.933-.925 2.776a1 1 0 01-1.898-.632l1-3a1 1 0 01.242-.391l2.5-2.5z",
    clipRule: "evenodd"
  })), _path3$2 || (_path3$2 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.86 13.732a1 1 0 011.408.128l2.5 3a1 1 0 01.193.365l1 3.5a1 1 0 01-1.923.55l-.941-3.297-2.365-2.838a1 1 0 01.128-1.408zM12.5 7a2 2 0 100-4 2 2 0 000 4z",
    clipRule: "evenodd"
  })));
}

var _path$9, _path2$7, _path3$1;

function _extends$c() { _extends$c = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$c.apply(this, arguments); }

function SvgWifiOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$c({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$9 || (_path$9 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.977 10.001a1 1 0 01-.764 1.19 8.357 8.357 0 00-3.573 1.745 1 1 0 01-1.28-1.537 10.357 10.357 0 014.427-2.162 1 1 0 011.19.764zm4.385.571a1 1 0 011.346-.434c.677.346 1.317.768 1.91 1.261a1 1 0 01-1.28 1.537 8.354 8.354 0 00-1.542-1.018 1 1 0 01-.434-1.346zM12 6c-.404 0-.807.02-1.206.058a1 1 0 01-.192-1.991 14.612 14.612 0 0111.06 3.583 1 1 0 01-1.323 1.5A12.61 12.61 0 0012 6zm-4.996-.09a1 1 0 01-.468 1.335A12.608 12.608 0 003.66 9.151a1 1 0 11-1.322-1.5 14.607 14.607 0 013.33-2.208 1 1 0 011.335.468zM11.956 16c-.852 0-1.682.265-2.377.758a1 1 0 01-1.158-1.63 6.104 6.104 0 017.07 0 1 1 0 11-1.158 1.63A4.104 4.104 0 0011.956 16z",
    clipRule: "evenodd"
  })), _path2$7 || (_path2$7 = /*#__PURE__*/React.createElement("path", {
    d: "M13 20a1 1 0 11-2 0 1 1 0 012 0z"
  })), _path3$1 || (_path3$1 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414l-18-18a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$8, _path2$6;

function _extends$b() { _extends$b = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b.apply(this, arguments); }

function SvgWifi(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$8 || (_path$8 = /*#__PURE__*/React.createElement("path", {
    d: "M3.661 9.15a12.61 12.61 0 0116.678 0 1 1 0 001.322-1.5 14.61 14.61 0 00-19.322 0 1 1 0 101.322 1.5z"
  })), _path2$6 || (_path2$6 = /*#__PURE__*/React.createElement("path", {
    d: "M6.64 12.936a8.357 8.357 0 0110.697 0 1 1 0 001.28-1.537 10.357 10.357 0 00-13.257 0 1 1 0 001.28 1.537zM11.956 16c-.852 0-1.682.265-2.377.758a1 1 0 01-1.158-1.63 6.104 6.104 0 017.07 0 1 1 0 11-1.158 1.63A4.104 4.104 0 0011.956 16zM12 21a1 1 0 100-2 1 1 0 000 2z"
  })));
}

var _path$7, _path2$5;

function _extends$a() { _extends$a = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a.apply(this, arguments); }

function SvgXCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$7 || (_path$7 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 100 16 8 8 0 000-16zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z",
    clipRule: "evenodd"
  })), _path2$5 || (_path2$5 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.463 8.464a1 1 0 011.414 0l2.121 2.122 2.122-2.122a1 1 0 011.414 1.415L13.413 12l2.121 2.121a1 1 0 01-1.414 1.415l-2.122-2.122-2.12 2.122a1 1 0 11-1.415-1.415L10.584 12 8.463 9.879a1 1 0 010-1.415z",
    clipRule: "evenodd"
  })));
}

var _path$6, _path2$4;

function _extends$9() { _extends$9 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9.apply(this, arguments); }

function SvgXOctagon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$6 || (_path$6 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.98 2.879A3 3 0 019.1 2h5.8a3 3 0 012.12.879l4.101 4.1A3 3 0 0122 9.101v5.798a3 3 0 01-.879 2.122l-4.1 4.1a3 3 0 01-2.122.879H9.101a3 3 0 01-2.122-.879l-4.1-4.1A3 3 0 012 14.899V9.101a3 3 0 01.879-2.122l4.1-4.1zM9.1 4a1 1 0 00-.707.293l-4.1 4.1A1 1 0 004 9.101v5.798a1 1 0 00.293.708l4.1 4.1a1 1 0 00.708.293h5.798a1 1 0 00.708-.293l4.1-4.1A1 1 0 0020 14.9V9.1a1 1 0 00-.293-.707l-4.1-4.1A1 1 0 0014.9 4H9.1z",
    clipRule: "evenodd"
  })), _path2$4 || (_path2$4 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.464 8.464a1 1 0 011.415 0L12 10.586l2.121-2.122a1 1 0 111.415 1.415L13.414 12l2.122 2.121a1 1 0 11-1.415 1.415L12 13.414l-2.121 2.122a1 1 0 11-1.415-1.415L10.586 12 8.464 9.879a1 1 0 010-1.415z",
    clipRule: "evenodd"
  })));
}

var _path$5, _path2$3;

function _extends$8() { _extends$8 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8.apply(this, arguments); }

function SvgXSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$5 || (_path$5 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 013-3h14a3 3 0 013 3v14a3 3 0 01-3 3H5a3 3 0 01-3-3V5zm3-1a1 1 0 00-1 1v14a1 1 0 001 1h14a1 1 0 001-1V5a1 1 0 00-1-1H5z",
    clipRule: "evenodd"
  })), _path2$3 || (_path2$3 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.464 8.464a1 1 0 011.415 0L12 10.586l2.121-2.122a1 1 0 111.415 1.415L13.414 12l2.122 2.121a1 1 0 11-1.415 1.415L12 13.414l-2.121 2.122a1 1 0 11-1.415-1.415L10.586 12 8.464 9.879a1 1 0 010-1.415z",
    clipRule: "evenodd"
  })));
}

var _path$4;

function _extends$7() { _extends$7 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }

function SvgX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$4 || (_path$4 = /*#__PURE__*/React.createElement("path", {
    d: "M16.243 17.657a1 1 0 001.414-1.414L13.414 12l4.243-4.243a1 1 0 10-1.414-1.414L12 10.586 7.757 6.343a1 1 0 10-1.414 1.414L10.586 12l-4.243 4.243a1 1 0 101.414 1.414L12 13.414l4.243 4.243z"
  })));
}

var _path$3, _path2$2;

function _extends$6() { _extends$6 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }

function SvgZapOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.426 2.095a1 1 0 01.564 1.046l-.658 4.603a1 1 0 01-1.98-.283l.198-1.379-.35.385a1 1 0 11-1.48-1.345l2.54-2.795a1 1 0 011.166-.232zM9.434 8.022a1 1 0 01.067 1.413L6.261 13H11a1 1 0 01.99 1.141l-.54 3.777 2.914-3.205a1 1 0 011.48 1.345l-5.104 5.615a1 1 0 01-1.73-.814L9.847 15H4a1 1 0 01-.74-1.673L8.022 8.09a1 1 0 011.412-.068zM14.7 10a1 1 0 011-1H20a1 1 0 01.74 1.673l-2.05 2.255a1 1 0 11-1.48-1.345l.53-.583H15.7a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path2$2 || (_path2$2 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 011.414 0l18 18a1 1 0 01-1.414 1.414l-18-18a1 1 0 010-1.414z",
    clipRule: "evenodd"
  })));
}

var _path$2;

function _extends$5() { _extends$5 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }

function SvgZap(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.426 2.095a1 1 0 01.564 1.046L14.153 9H20a1 1 0 01.74 1.673l-10 11a1 1 0 01-1.73-.814L9.847 15H4a1 1 0 01-.74-1.673l10-11a1 1 0 011.166-.232zM6.26 13H11a1 1 0 01.99 1.141l-.54 3.777L17.74 11H13a1 1 0 01-.99-1.141l.54-3.777L6.26 13z",
    clipRule: "evenodd"
  })));
}

var _path$1, _path2$1, _path3;

function _extends$4() { _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }

function SvgZoomIn(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 100 14 7 7 0 000-14zm-9 7a9 9 0 1118 0 9 9 0 01-18 0z",
    clipRule: "evenodd"
  })), _path2$1 || (_path2$1 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.293 16.293a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414zM7 11a1 1 0 011-1h6a1 1 0 110 2H8a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })), _path3 || (_path3 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 15a1 1 0 01-1-1V8a1 1 0 112 0v6a1 1 0 01-1 1z",
    clipRule: "evenodd"
  })));
}

var _path, _path2;

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

function SvgZoomOut(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    fill: "currentColor"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 100 14 7 7 0 000-14zm-9 7a9 9 0 1118 0 9 9 0 01-18 0z",
    clipRule: "evenodd"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.293 16.293a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414zM7 11a1 1 0 011-1h6a1 1 0 110 2H8a1 1 0 01-1-1z",
    clipRule: "evenodd"
  })));
}

var data$2 = {
    activity: SvgActivity,
    airplane: SvgAirplane,
    'alert-circle': SvgAlertCircle,
    'alert-octagon': SvgAlertOctagon,
    'alert-triangle': SvgAlertTriangle,
    'arrow-down-circle': SvgArrowDownCircle,
    'arrow-down-left': SvgArrowDownLeft,
    'arrow-down-right': SvgArrowDownRight,
    'arrow-down': SvgArrowDown,
    'arrow-left-circle': SvgArrowLeftCircle,
    'arrow-left': SvgArrowLeft,
    'arrow-right-circle': SvgArrowRightCircle,
    'arrow-right': SvgArrowRight,
    'arrow-up-circle': SvgArrowUpCircle,
    'arrow-up-left': SvgArrowUpLeft,
    'arrow-up-right': SvgArrowUpRight,
    'arrow-up': SvgArrowUp,
    award: SvgAward,
    'bar-chart': SvgBarChart,
    'bar-chart-2': SvgBarChart2,
    baseball: SvgBaseball,
    basketball: SvgBasketball,
    bed: SvgBed,
    'bell-off': SvgBellOff,
    bell: SvgBell,
    'book-open': SvgBookOpen,
    bookmark: SvgBookmark,
    briefcase: SvgBriefcase,
    calendar: SvgCalendar,
    'camera-off': SvgCameraOff,
    camera: SvgCamera,
    car: SvgCar,
    'cdc-in-frame': SvgCdcInFrame,
    'cdc-review': SvgCdcReview,
    check: SvgCheck,
    'checkmark-circle': SvgCheckmarkCircle,
    'chevron-down': SvgChevronDown,
    'chevron-left': SvgChevronLeft,
    'chevron-right': SvgChevronRight,
    'chevron-up': SvgChevronUp,
    'chevrons-down': SvgChevronsDown,
    'chevrons-left': SvgChevronsLeft,
    'chevrons-right': SvgChevronsRight,
    'chevrons-up': SvgChevronsUp,
    'circle-delete': SvgCircleDelete,
    circle: SvgCircle,
    clipboard: SvgClipboard,
    clock: SvgClock,
    'cloud-drizzle': SvgCloudDrizzle,
    'cloud-lightning': SvgCloudLightning,
    'cloud-off': SvgCloudOff,
    'cloud-rain': SvgCloudRain,
    'cloud-snow': SvgCloudSnow,
    cloud: SvgCloud,
    columns: SvgColumns,
    compass: SvgCompass,
    copy: SvgCopy,
    'corner-down-left': SvgCornerDownLeft,
    'corner-down-right': SvgCornerDownRight,
    'corner-left-down': SvgCornerLeftDown,
    'corner-left-up': SvgCornerLeftUp,
    'corner-right-down': SvgCornerRightDown,
    'corner-right-up': SvgCornerRightUp,
    'corner-up-left': SvgCornerUpLeft,
    'corner-up-right': SvgCornerUpRight,
    'credit-card': SvgCreditCard,
    crop: SvgCrop,
    crosshair: SvgCrosshair,
    delete: SvgDelete,
    'dollar-sign': SvgDollarSign,
    'download-cloud': SvgDownloadCloud,
    download: SvgDownload,
    'drop-down': SvgDropDown,
    'drop-left': SvgDropLeft,
    'drop-right': SvgDropRight,
    'drop-up': SvgDropUp,
    droplet: SvgDroplet,
    'edit-2': SvgEdit2,
    edit: SvgEdit,
    'external-link': SvgExternalLink,
    'eye-off': SvgEyeOff,
    eye: SvgEye,
    'face-in-frame': SvgFaceInFrame,
    'face-mask': SvgFaceMask,
    file: SvgFile,
    filter: SvgFilter,
    'fingerprint-off': SvgFingerprintOff,
    fingerprint: SvgFingerprint,
    flag: SvgFlag,
    folder: SvgFolder,
    frown: SvgFrown,
    gift: SvgGift,
    glasses: SvgGlasses,
    globe: SvgGlobe,
    grid: SvgGrid,
    hash: SvgHash,
    headphones: SvgHeadphones,
    'health-pass': SvgHealthPass,
    health: SvgHealth,
    heart: SvgHeart,
    'help-circle': SvgHelpCircle,
    hexagon: SvgHexagon,
    home: SvgHome,
    iconlogo: SvgIconlogo,
    'id-card-back': SvgIdCardBack,
    'id-card': SvgIdCard,
    'id-in-frame': SvgIdInFrame,
    'id-in-review': SvgIdInReview,
    image: SvgImage,
    inbox: SvgInbox,
    info: SvgInfo,
    key: SvgKey,
    lab: SvgLab,
    'laptop-computer': SvgLaptopComputer,
    link: SvgLink,
    loader: SvgLoader,
    lock: SvgLock,
    'log-in': SvgLogIn,
    'log-out': SvgLogOut,
    luggage: SvgLuggage,
    mail: SvgMail,
    maintenance: SvgMaintenance,
    'map-pin-off': SvgMapPinOff,
    'map-pin': SvgMapPin,
    map: SvgMap,
    'maximize-2': SvgMaximize2,
    maximize: SvgMaximize,
    meh: SvgMeh,
    menu: SvgMenu,
    message: SvgMessage,
    'message-circle': SvgMessageCircle,
    mic: SvgMic,
    'minimize-2': SvgMinimize2,
    minimize: SvgMinimize,
    'minus-circle': SvgMinusCircle,
    'minus-square': SvgMinusSquare,
    minus: SvgMinus,
    'mobile-phone': SvgMobilePhone,
    moon: SvgMoon,
    'more-horizontal': SvgMoreHorizontal,
    'more-vertical': SvgMoreVertical,
    move: SvgMove,
    'navigation-2': SvgNavigation2,
    navigation: SvgNavigation,
    nfc: SvgNfc,
    'number-one': SvgNumberOne,
    octagon: SvgOctagon,
    paperclip: SvgPaperclip,
    passport: SvgPassport,
    phone: SvgPhone,
    'pie-chart': SvgPieChart,
    'plane-board': SvgPlaneBoard,
    'plug-icon': SvgPlugIcon,
    'plug-off': SvgPlugOff,
    'plus-circle': SvgPlusCircle,
    'plus-square': SvgPlusSquare,
    plus: SvgPlus,
    power: SvgPower,
    profile: SvgProfile,
    'qr-code-phone': SvgQrCodePhone,
    'qr-code-scan-off': SvgQrCodeScanOff,
    'qr-code-scan': SvgQrCodeScan,
    'qr-code': SvgQrCode,
    quiz: SvgQuiz,
    radio: SvgRadio,
    refresh: SvgRefresh,
    repeat: SvgRepeat,
    rss: SvgRss,
    save: SvgSave,
    search: SvgSearch,
    send: SvgSend,
    'settings 1': SvgSettings1,
    sex: SvgSex,
    'share-android': SvgShareAndroid,
    share: SvgShare,
    'shield-off': SvgShieldOff,
    shield: SvgShield,
    'shopping-cart': SvgShoppingCart,
    sidebar: SvgSidebar,
    slash: SvgSlash,
    sliders: SvgSliders,
    smile: SvgSmile,
    square: SvgSquare,
    star: SvgStar,
    sun: SvgSun,
    sunrise: SvgSunrise,
    sunset: SvgSunset,
    sync: SvgSync,
    syringe: SvgSyringe,
    table: SvgTable,
    tag: SvgTag,
    'temperature-off': SvgTemperatureOff,
    temperature: SvgTemperature,
    tennis: SvgTennis,
    'thumbs-down': SvgThumbsDown,
    'thumbs-up': SvgThumbsUp,
    'ticket-group': SvgTicketGroup,
    ticket: SvgTicket,
    tool: SvgTool,
    'trash-2': SvgTrash2,
    'trending-down': SvgTrendingDown,
    'trending-up': SvgTrendingUp,
    triangle: SvgTriangle,
    unlock: SvgUnlock,
    'upload-cloud': SvgUploadCloud,
    upload: SvgUpload,
    'user-check': SvgUserCheck,
    'user-minus': SvgUserMinus,
    'user-plus': SvgUserPlus,
    'user-privacy': SvgUserPrivacy,
    'user-time': SvgUserTime,
    'user-x': SvgUserX,
    users: SvgUsers,
    walking: SvgWalking,
    'wifi-off': SvgWifiOff,
    wifi: SvgWifi,
    'x-circle': SvgXCircle,
    'x-octagon': SvgXOctagon,
    'x-square': SvgXSquare,
    x: SvgX,
    'zap-off': SvgZapOff,
    zap: SvgZap,
    'zoom-in': SvgZoomIn,
    'zoom-out': SvgZoomOut,
};

var SVG = function (_a) {
    var _b = _a.color, color = _b === void 0 ? 'neutralsTextBlack' : _b, name = _a.name, _c = _a.size, size = _c === void 0 ? '100%' : _c, props = __rest(_a, ["color", "name", "size"]);
    var fill = colors[color] || color;
    var Comp = data$2[name];
    return Comp ? (React__default.createElement(Comp, __assign({ fillRule: "evenodd", clipRule: "evenodd", width: size, height: size, viewBox: "0 0 24 24", preserveAspectRatio: "xMidYMid meet", xmlns: "http://www.w3.org/2000/svg", xmlnsXlink: "http://www.w3.org/1999/xlink", fill: fill }, props))) : (React__default.createElement(React__default.Fragment, null));
};
var Icon = function (_a) {
    var _b = _a.size, size = _b === void 0 ? '1.5rem' : _b, iconClick = _a.iconClick, role = _a.role, props = __rest(_a, ["size", "iconClick", "role"]);
    return (React__default.createElement(Box, __assign({ width: size, height: size, flexShrink: 0, onClick: iconClick, role: role, "aria-label": props.name, "data-testid": "test-icon-".concat(props.name) }, props),
        React__default.createElement(SVG, __assign({}, props, { "aria-hidden": true }))));
};
var getSVGUrl = function (props) {
    var url = encodeURIComponent(renderToStaticMarkup(React__default.createElement(SVG, __assign({}, props))));
    return "url(data:image/svg+xml;utf8,".concat(url, ")");
};

var Input = forwardRef(function (_a, ref) {
    var _b;
    var icon = _a.icon, color = _a.color, _c = _a.variant, variant = _c === void 0 ? 'default' : _c, props = __rest(_a, ["icon", "color", "variant"]);
    var px = 4;
    var py = 3;
    var css = {
        outline: 'none',
        ':disabled': {
            color: 'neutralsPlaceholderGray',
            borderColor: 'neutralsLightGray',
            bg: 'neutralsBackgroundGray',
        },
        '::placeholder': {
            color: 'neutralsPlaceholderGray',
        },
        ':disabled::placeholder': {
            color: 'neutralsBorderGray',
        },
    };
    var statusColors = ['success', 'error'];
    var hasBorder = variant !== 'borderless';
    if (hasBorder && color && inputColors[color]) {
        Object.assign(css, inputColors[color]);
    }
    else if (variant === 'default') {
        Object.assign(css, inputColors['default']);
    }
    var iconRole = '';
    if (icon) {
        iconRole = props.iconClick ? 'button' : 'img';
    }
    if (variant === 'borderless' && color && statusColors.includes(color)) {
        Object.assign(css, { color: (_b = inputColors[color]) === null || _b === void 0 ? void 0 : _b.borderColor });
    }
    return (React__default.createElement(Box, { display: "inline-flex", position: "relative" },
        React__default.createElement(Box, __assign({ as: "input", ref: ref, tx: "form.input", variant: variant, id: props.id || props.name, "aria-label": props.id || props.name, maxWidth: "inputMaxWidth", height: "inputHeight", pr: icon ? 7 : px }, props, { __css: css })),
        React__default.createElement(Icon, { cursor: props.iconClick ? 'pointer' : 'inherit', position: "absolute", right: ".75rem", top: py, name: icon, role: iconRole, iconClick: props.iconClick, color: props.disabled ? 'neutralsBorderGray' : 'neutralsTextGray' })));
});
Input.displayName = 'Input';
var InputGroup = forwardRef(function (_a, ref) {
    var variant = _a.variant, color = _a.color, label = _a.label, message = _a.message, icon = _a.icon, disabled = _a.disabled, containerProps = _a.containerProps, messageProps = _a.messageProps, error = _a.error, props = __rest(_a, ["variant", "color", "label", "message", "icon", "disabled", "containerProps", "messageProps", "error"]);
    var colorType = error ? 'error' : color;
    var messageText = error || message;
    return (React__default.createElement(Flex, __assign({ flexDirection: "column", p: 4, width: "100%" }, containerProps),
        React__default.createElement(Label$1, { variant: disabled ? 'disabled' : 'default', mb: 2, htmlFor: props.id || props.name, as: "label" }, label),
        React__default.createElement(Input, __assign({ ref: ref, variant: variant, color: error ? 'error' : color, disabled: disabled, icon: icon, maxWidth: "none", width: "100%", "aria-invalid": !!error }, props)),
        messageText && (React__default.createElement(Message, __assign({ role: "alert", variant: colorType, mt: 1 }, messageProps), messageText))));
});
InputGroup.displayName = 'InputGroup';

var Label$1 = forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'default' : _b, props = __rest(_a, ["variant"]);
    return (React__default.createElement(Box, __assign({ ref: ref, variant: variant, tx: "form.label", sx: {
            fontSize: 1,
        } }, props)));
});
Label$1.displayName = 'Label';

var Link = forwardRef(function (_a, ref) {
    var children = _a.children, href = _a.href, _b = _a.visitable, visitable = _b === void 0 ? false : _b, props = __rest(_a, ["children", "href", "visitable"]);
    return (React__default.createElement(Text, __assign({ as: "a", sx: {
            cursor: 'pointer',
        }, ref: ref, color: "utilityLinkBlue", __css: __assign(__assign({ textDecoration: 'none' }, (visitable && {
            '&:visited': {
                color: 'utilityLinkPurpleVisited',
            },
        })), { '&:active': {
                color: 'brandMidnightBlue',
            }, '&:hover': {
                textDecoration: 'underline',
            } }), href: href, variant: "medium" }, props), children));
});
Link.displayName = 'Link';

var ListIcon = function (_a) {
    var iconName = _a.iconName, _b = _a.iconType, iconType = _b === void 0 ? 'interactive' : _b, _c = _a.iconColor, iconColor = _c === void 0 ? 'brandMidnightBlue' : _c, _d = _a.iconContainerColor, iconContainerColor = _d === void 0 ? 'brandBlueWash' : _d;
    var isNonInteractive = iconType === 'nonInteractive';
    var colors = {
        iconContainerColor: iconContainerColor,
        iconColor: iconColor,
    };
    if (isNonInteractive) {
        colors = {
            iconContainerColor: 'neutralsWhite',
            iconColor: 'neutralsTextBlack',
        };
    }
    var IconComp = (React__default.createElement(Icon, { size: "1.5rem", name: iconName, color: colors.iconColor }));
    return isNonInteractive ? (IconComp) : (React__default.createElement(Flex, { alignItems: "center", justifyContent: "center", borderRadius: "50%", width: "3rem", minWidth: "3rem", height: "3rem", backgroundColor: colors.iconContainerColor }, IconComp));
};
var Label = function (_a) {
    var label = _a.label, _b = _a.labelColor, labelColor = _b === void 0 ? 'neutralsTextBlack' : _b, _c = _a.labelVariant, labelVariant = _c === void 0 ? 'medium' : _c, subLabel = _a.subLabel, _d = _a.subLabelColor, subLabelColor = _d === void 0 ? 'neutralsTextGray' : _d, isAfterIcon = _a.isAfterIcon;
    return (React__default.createElement(Flex, { pl: isAfterIcon ? '1rem' : '0', pr: "1rem", flexDirection: "column" },
        React__default.createElement(Text, { variant: labelVariant, color: labelColor }, label),
        subLabel && (React__default.createElement(Text, { variant: "regular", mt: 1, fontSize: 1, color: subLabelColor }, subLabel))));
};
var ActionButton = function (props) {
    return (React__default.createElement(Button, __assign({}, props, { "data-testid": "testid-list-row-button-".concat(props.id), icon: undefined, text: props.actionText, variant: "secondary", size: "small" })));
};
var ActionLink = function (props) {
    return (React__default.createElement(Link, __assign({}, props, { "data-testid": "testid-list-row-link-".concat(props.id), fontSize: "0.75rem" }), props.actionText));
};
var ActionIcon = function (_a) {
    var actionIcon = _a.actionIcon, actionIconColor = _a.actionIconColor;
    return (React__default.createElement(Flex, { alignItems: "center", justifyContent: "center" },
        React__default.createElement(Icon, { name: actionIcon, color: actionIconColor })));
};

var ListRow = function (props) {
    var variant = props.variant, icon = props.icon, iconColor = props.iconColor, iconContainerColor = props.iconContainerColor, label = props.label, labelColor = props.labelColor, labelVariant = props.labelVariant, subLabel = props.subLabel, subLabelColor = props.subLabelColor, actionIcon = props.actionIcon, actionIconColor = props.actionIconColor;
    var actionProps = {
        actionIcon: actionIcon || 'chevron-right',
        actionIconColor: actionIconColor || 'neutralsPlaceholderGray',
    };
    var actionType = {
        interactive: null,
        nonInteractive: null,
        actionIcon: React__default.createElement(ActionIcon, __assign({}, actionProps)),
        actionButton: React__default.createElement(ActionButton, __assign({}, props)),
        actionLink: (React__default.createElement(ActionLink, { actionText: props.actionText, id: props.id, href: props.href, label: props.label })),
    };
    var action = actionType[variant || 'interactive'];
    return (React__default.createElement(Flex, __assign({ alignItems: "center", justifyContent: "space-between" }, props),
        React__default.createElement(Flex, { alignItems: "center" },
            icon && (React__default.createElement(ListIcon, { iconType: variant, iconName: icon, iconColor: iconColor, iconContainerColor: iconContainerColor })),
            React__default.createElement(Label, { label: label, labelColor: labelColor, labelVariant: labelVariant, subLabel: subLabel, subLabelColor: subLabelColor, isAfterIcon: !!icon })),
        React__default.createElement(Flex, { alignItems: "center", justifyItems: "flex-end", minWidth: "fit-content" }, action)));
};

var _g$2, _defs;

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

function SvgDotsOnly(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    preserveAspectRatio: "xMidYMin meet",
    viewBox: "0 0 92 88",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$2 || (_g$2 = /*#__PURE__*/React.createElement("g", {
    clipPath: "url(#dots-only_svg__clip0_3773_34388)",
    fill: "#041A55"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M10.977 46.92a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99A2.983 2.983 0 008 43.93a2.983 2.983 0 002.977 2.99zM24.37 46.92a2.983 2.983 0 002.978-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM18.884 55.741a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM28.518 59.572a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM29.108 70.015a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM18.884 38.092a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM17.67 26.505a2.983 2.983 0 002.976-2.99 2.983 2.983 0 00-2.976-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM28.518 34.18a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM28.902 24.144a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM38.986 26.505a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM51.963 26.505a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM61.792 24.016a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM62.468 34.385a2.983 2.983 0 002.976-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.976 2.99 2.983 2.983 0 002.977 2.99zM72.103 38.092a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM72.103 55.741a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM62.468 59.601a2.983 2.983 0 002.976-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.976 2.99 2.983 2.983 0 002.977 2.99zM73.428 67.336a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM51.963 67.336a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM38.986 67.336a2.984 2.984 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM17.583 67.336a2.983 2.983 0 002.976-2.99 2.983 2.983 0 00-2.976-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM61.792 70.015a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM45.436 75.287a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM56.13 80.002a2.983 2.983 0 002.976-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.976 2.99 2.983 2.983 0 002.976 2.99zM34.88 80.002a2.983 2.983 0 002.976-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM66.704 46.92a2.983 2.983 0 002.976-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.976 2.99 2.983 2.983 0 002.977 2.99zM80.023 46.92A2.983 2.983 0 0083 43.93a2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM73.426 26.505a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM45.436 18.559a2.983 2.983 0 002.977-2.99 2.983 2.983 0 00-2.977-2.99 2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM34.796 13.98a2.983 2.983 0 002.977-2.99A2.983 2.983 0 0034.796 8a2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99zM56.16 13.98a2.983 2.983 0 002.976-2.99A2.983 2.983 0 0056.159 8a2.983 2.983 0 00-2.977 2.99 2.983 2.983 0 002.977 2.99z"
  }))), _defs || (_defs = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
    id: "dots-only_svg__clip0_3773_34388"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "#fff",
    transform: "translate(8 8)",
    d: "M0 0h75v72H0z"
  })))));
}

var _g$1;

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

function SvgHorizontalLogo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    preserveAspectRatio: "xMidYMin meet",
    viewBox: "0 0 281 88",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g$1 || (_g$1 = /*#__PURE__*/React.createElement("g", {
    fill: "#041A55"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M10.986 46.92a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.985-2.99A2.988 2.988 0 008 43.93a2.988 2.988 0 002.986 2.99zM24.42 46.92a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.985 2.99zM18.916 55.741a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.985 2.99zM28.578 59.572a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.985 2.99zM29.17 70.015a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.985-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM18.916 38.092a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.985 2.99zM17.698 26.505a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM28.578 34.18a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.985 2.99zM28.964 24.144a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM39.078 26.505a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.986 2.99zM52.094 26.505a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.985-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM61.952 24.016a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.985-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM62.629 34.385a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM72.293 38.092a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM72.293 55.741a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM62.629 59.601a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM73.622 67.336a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM52.094 67.336a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.985-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM39.078 67.336a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.986 2.99zM17.61 67.336a2.988 2.988 0 002.987-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM61.952 70.015a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.985-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM45.547 75.287a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.985 2.99zM56.272 80.002a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM34.959 80.002a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.985-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM66.877 46.92a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.985 2.99zM80.237 46.92a2.988 2.988 0 002.985-2.99 2.988 2.988 0 00-2.985-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM73.62 26.505a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM45.547 18.559a2.988 2.988 0 002.986-2.99 2.988 2.988 0 00-2.986-2.99 2.988 2.988 0 00-2.985 2.99 2.988 2.988 0 002.985 2.99zM34.876 13.98a2.988 2.988 0 002.986-2.99A2.988 2.988 0 0034.876 8a2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM56.302 13.98a2.988 2.988 0 002.986-2.99A2.988 2.988 0 0056.302 8a2.988 2.988 0 00-2.986 2.99 2.988 2.988 0 002.986 2.99zM112.72 44.001c0-8.541 6.493-15.029 14.988-15.029a14.973 14.973 0 0112.99 7.27l-4.972 3.13a8.693 8.693 0 00-3.281-3.6 8.67 8.67 0 00-4.699-1.268c-5.367 0-9.298 4.204-9.298 9.497 0 5.167 3.893 9.437 9.221 9.437a8.812 8.812 0 008.306-5.406l5.164 2.772a14.767 14.767 0 01-5.521 6.056 14.735 14.735 0 01-7.895 2.174c-8.821 0-14.989-6.73-14.989-15.026M151.604 29.38v29.255h19.194v-5.487h-13.504V29.38h-5.69zM181.913 29.38v29.255h19.68v-5.368h-14.028v-6.653h11.421v-5.332h-11.421v-6.573h14.028v-5.33h-19.68zM223.038 29.38l-11.057 29.255h5.805l1.917-5.331h12.22l1.916 5.331h5.81L228.592 29.38h-5.554zm2.805 7.163l4.206 11.642H221.6l4.243-11.642zM255.887 34.59v9.665h5.771c3.725 0 5.452-2.327 5.452-4.929 0-2.866-1.846-4.73-5.452-4.73l-5.771-.006zm-5.648-5.21h11.862c6.732 0 10.738 4.327 10.738 9.902a9.51 9.51 0 01-5.128 8.654l5.773 10.7h-6.335l-4.686-9.22h-6.576v9.22h-5.648V29.38z"
  }))));
}

var _g;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function SvgVerticalLogo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    viewBox: "0 0 167 137",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    fill: "#041A55"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M50.849 44.426a2.797 2.797 0 002.796-2.798 2.797 2.797 0 10-2.796 2.798zM63.43 44.426a2.797 2.797 0 002.797-2.798 2.797 2.797 0 10-2.797 2.798zM58.276 52.682a2.797 2.797 0 002.797-2.799 2.797 2.797 0 10-2.797 2.799zM67.328 56.267a2.797 2.797 0 002.796-2.799 2.797 2.797 0 10-2.796 2.799zM67.882 66.04a2.797 2.797 0 002.796-2.797 2.797 2.797 0 10-2.796 2.798zM58.276 36.163a2.797 2.797 0 002.797-2.798 2.797 2.797 0 10-2.797 2.798zM57.137 25.32a2.797 2.797 0 002.797-2.8 2.797 2.797 0 10-2.797 2.8zM67.328 32.503a2.797 2.797 0 002.796-2.799 2.797 2.797 0 10-2.796 2.799zM67.689 23.11a2.797 2.797 0 002.796-2.799 2.797 2.797 0 10-2.796 2.799zM77.16 25.32a2.797 2.797 0 002.797-2.8 2.797 2.797 0 10-2.796 2.8zM89.351 25.32a2.797 2.797 0 002.796-2.8 2.797 2.797 0 10-2.796 2.8zM98.584 22.99a2.797 2.797 0 002.796-2.799 2.797 2.797 0 10-2.796 2.799zM99.216 32.694a2.798 2.798 0 10-.001-5.595 2.798 2.798 0 00.001 5.595zM108.269 36.163a2.798 2.798 0 10-.001-5.595 2.798 2.798 0 00.001 5.595zM108.269 52.682a2.798 2.798 0 10-.001-5.595 2.798 2.798 0 00.001 5.595zM99.216 56.295a2.798 2.798 0 10-.001-5.595 2.798 2.798 0 00.001 5.595zM109.512 63.534a2.797 2.797 0 002.796-2.798 2.797 2.797 0 00-2.796-2.799 2.797 2.797 0 00-2.796 2.799 2.797 2.797 0 002.796 2.798zM89.351 63.534a2.797 2.797 0 002.796-2.798 2.797 2.797 0 10-2.796 2.798zM77.16 63.534a2.797 2.797 0 002.797-2.798 2.797 2.797 0 10-2.796 2.798zM57.056 63.534a2.797 2.797 0 002.796-2.798 2.797 2.797 0 10-2.796 2.798zM98.584 66.04a2.797 2.797 0 002.796-2.797 2.797 2.797 0 10-2.796 2.798zM83.218 70.975a2.797 2.797 0 002.796-2.798 2.797 2.797 0 10-2.796 2.798zM93.264 75.388a2.797 2.797 0 002.797-2.798 2.797 2.797 0 10-2.797 2.798zM73.303 75.388A2.797 2.797 0 0076.1 72.59a2.797 2.797 0 10-2.797 2.798zM103.195 44.426a2.797 2.797 0 100-5.594 2.797 2.797 0 000 5.594zM115.709 44.426a2.797 2.797 0 002.796-2.798 2.797 2.797 0 00-2.796-2.798 2.797 2.797 0 00-2.796 2.798 2.797 2.797 0 002.796 2.798zM109.512 25.32a2.797 2.797 0 002.796-2.8 2.797 2.797 0 10-2.796 2.8zM83.218 17.882a2.797 2.797 0 002.796-2.798 2.797 2.797 0 10-2.796 2.798zM73.225 13.597a2.797 2.797 0 002.797-2.799 2.797 2.797 0 10-2.797 2.799zM93.29 13.597a2.797 2.797 0 002.797-2.799 2.797 2.797 0 10-2.797 2.799zM8 113.934c0-7.994 6.08-14.063 14.036-14.063a14.036 14.036 0 0112.167 6.804l-4.662 2.92a8.14 8.14 0 00-7.476-4.557c-5.024 0-8.706 3.934-8.706 8.888.002 4.851 3.652 8.843 8.643 8.843a8.265 8.265 0 007.778-5.062l4.837 2.595a13.813 13.813 0 01-12.566 7.702C13.785 127.998 8 121.699 8 113.934zM44.418 100.25v27.383h17.977v-5.138H49.748V100.25h-5.33zM72.802 100.25v27.383h18.434v-5.026h-13.14v-6.228h10.699v-4.988H78.096v-6.151h13.14v-4.99H72.802zM111.317 100.25l-10.352 27.383h5.436l1.795-4.99h11.438l1.795 4.99h5.441l-10.367-27.383h-5.186zm2.629 6.706l3.939 10.894h-7.914l3.975-10.894zM142.079 105.128v9.04h5.405c3.488 0 5.106-2.178 5.106-4.615 0-2.682-1.729-4.425-5.106-4.425h-5.405zm-5.29-4.878h11.11c6.303 0 10.056 4.05 10.056 9.267a8.916 8.916 0 01-4.803 8.102l5.406 10.014h-5.932l-4.388-8.629h-6.159v8.629h-5.29V100.25z"
  }))));
}

var data$1 = {
    Vertical: SvgVerticalLogo,
    Dots: SvgDotsOnly,
    Horizontal: SvgHorizontalLogo,
};

var Logo = function (_a) {
    var name = _a.name, rest = __rest(_a, ["name"]);
    var Comp = data$1[name];
    return React__default.createElement(Comp, __assign({}, rest));
};

/* istanbul ignore next */
var MidnightBlueLoader = {
    v: '5.7.4',
    fr: 29.9700012207031,
    ip: 0,
    op: 80.0000032584668,
    w: 64,
    h: 64,
    nm: 'Loader 64px 2',
    ddd: 0,
    assets: [],
    layers: [
        {
            ddd: 0,
            ind: 1,
            ty: 4,
            nm: 'Shape Layer 2',
            sr: 1,
            ks: {
                o: { a: 0, k: 100, ix: 11 },
                r: {
                    a: 1,
                    k: [
                        {
                            i: { x: [0.833], y: [0.833] },
                            o: { x: [0.167], y: [0.167] },
                            t: 0,
                            s: [0],
                        },
                        { t: 80.0000032584668, s: [720] },
                    ],
                    ix: 10,
                },
                p: { a: 0, k: [32, 32, 0], ix: 2, l: 2 },
                a: { a: 0, k: [-0.98, 1.27, 0], ix: 1, l: 2 },
                s: { a: 0, k: [100, 100, 100], ix: 6, l: 2 },
            },
            ao: 0,
            shapes: [
                {
                    ty: 'gr',
                    it: [
                        {
                            d: 1,
                            ty: 'el',
                            s: { a: 0, k: [84.539, 84.539], ix: 2 },
                            p: { a: 0, k: [0, 0], ix: 3 },
                            nm: 'Ellipse Path 1',
                            mn: 'ADBE Vector Shape - Ellipse',
                            hd: false,
                        },
                        {
                            ty: 'st',
                            c: {
                                a: 0,
                                k: [0.015686275437, 0.101960785687, 0.333333343267, 1],
                                ix: 3,
                            },
                            o: { a: 0, k: 100, ix: 4 },
                            w: { a: 0, k: 5, ix: 5 },
                            lc: 2,
                            lj: 1,
                            ml: 4,
                            bm: 0,
                            nm: 'Stroke 1',
                            mn: 'ADBE Vector Graphic - Stroke',
                            hd: false,
                        },
                        {
                            ty: 'fl',
                            c: { a: 0, k: [1, 0, 0, 1], ix: 4 },
                            o: { a: 0, k: 0, ix: 5 },
                            r: 1,
                            bm: 0,
                            nm: 'Fill 1',
                            mn: 'ADBE Vector Graphic - Fill',
                            hd: false,
                        },
                        {
                            ty: 'tr',
                            p: { a: 0, k: [-0.98, 1.27], ix: 2 },
                            a: { a: 0, k: [0, 0], ix: 1 },
                            s: { a: 0, k: [68.52, 68.52], ix: 3 },
                            r: { a: 0, k: 0, ix: 6 },
                            o: { a: 0, k: 100, ix: 7 },
                            sk: { a: 0, k: 0, ix: 4 },
                            sa: { a: 0, k: 0, ix: 5 },
                            nm: 'Transform',
                        },
                    ],
                    nm: 'Ellipse 1',
                    np: 3,
                    cix: 2,
                    bm: 0,
                    ix: 1,
                    mn: 'ADBE Vector Group',
                    hd: false,
                },
                {
                    ty: 'tm',
                    s: {
                        a: 1,
                        k: [
                            {
                                i: { x: [0.833], y: [1] },
                                o: { x: [0.333], y: [0] },
                                t: 0,
                                s: [0],
                            },
                            { t: 40.0000016292334, s: [100] },
                        ],
                        ix: 1,
                    },
                    e: {
                        a: 1,
                        k: [
                            {
                                i: { x: [0.667], y: [1] },
                                o: { x: [0.333], y: [0] },
                                t: 0,
                                s: [1],
                            },
                            {
                                i: { x: [0.667], y: [1] },
                                o: { x: [0.333], y: [0] },
                                t: 30,
                                s: [40],
                            },
                            { t: 80.0000032584668, s: [100] },
                        ],
                        ix: 2,
                    },
                    o: { a: 0, k: 0, ix: 3 },
                    m: 1,
                    ix: 2,
                    nm: 'Trim Paths 1',
                    mn: 'ADBE Vector Filter - Trim',
                    hd: false,
                },
            ],
            ip: 0,
            op: 600.000024438501,
            st: 0,
            bm: 0,
        },
    ],
    markers: [],
};

/* istanbul ignore next */
var WhiteLoader = {
    v: '5.8.1',
    fr: 29.9700012207031,
    ip: 0,
    op: 60.0000024438501,
    w: 64,
    h: 64,
    nm: 'Loader 64px 2',
    ddd: 0,
    assets: [],
    layers: [
        {
            ddd: 0,
            ind: 1,
            ty: 4,
            nm: 'Shape Layer 2',
            sr: 1,
            ks: {
                o: { a: 0, k: 100, ix: 11 },
                r: {
                    a: 1,
                    k: [
                        {
                            i: { x: [0.833], y: [0.833] },
                            o: { x: [0.167], y: [0.167] },
                            t: 0,
                            s: [0],
                        },
                        { t: 60.0000024438501, s: [360] },
                    ],
                    ix: 10,
                },
                p: { a: 0, k: [32, 32, 0], ix: 2, l: 2 },
                a: { a: 0, k: [-0.98, 1.27, 0], ix: 1, l: 2 },
                s: { a: 0, k: [100, 100, 100], ix: 6, l: 2 },
            },
            ao: 0,
            shapes: [
                {
                    ty: 'gr',
                    it: [
                        {
                            d: 1,
                            ty: 'el',
                            s: { a: 0, k: [84.539, 84.539], ix: 2 },
                            p: { a: 0, k: [0, 0], ix: 3 },
                            nm: 'Ellipse Path 1',
                            mn: 'ADBE Vector Shape - Ellipse',
                            hd: false,
                        },
                        {
                            ty: 'st',
                            c: { a: 0, k: [1, 1, 1, 1], ix: 3 },
                            o: { a: 0, k: 100, ix: 4 },
                            w: { a: 0, k: 8, ix: 5 },
                            lc: 2,
                            lj: 1,
                            ml: 4,
                            bm: 0,
                            nm: 'Stroke 1',
                            mn: 'ADBE Vector Graphic - Stroke',
                            hd: false,
                        },
                        {
                            ty: 'fl',
                            c: { a: 0, k: [1, 0, 0, 1], ix: 4 },
                            o: { a: 0, k: 0, ix: 5 },
                            r: 1,
                            bm: 0,
                            nm: 'Fill 1',
                            mn: 'ADBE Vector Graphic - Fill',
                            hd: false,
                        },
                        {
                            ty: 'tr',
                            p: { a: 0, k: [-0.98, 1.27], ix: 2 },
                            a: { a: 0, k: [0, 0], ix: 1 },
                            s: { a: 0, k: [68.52, 68.52], ix: 3 },
                            r: { a: 0, k: 0, ix: 6 },
                            o: { a: 0, k: 100, ix: 7 },
                            sk: { a: 0, k: 0, ix: 4 },
                            sa: { a: 0, k: 0, ix: 5 },
                            nm: 'Transform',
                        },
                    ],
                    nm: 'Ellipse 1',
                    np: 3,
                    cix: 2,
                    bm: 0,
                    ix: 1,
                    mn: 'ADBE Vector Group',
                    hd: false,
                },
                {
                    ty: 'tm',
                    s: {
                        a: 1,
                        k: [
                            {
                                i: { x: [0.833], y: [1] },
                                o: { x: [0.333], y: [0] },
                                t: 0,
                                s: [0],
                            },
                            { t: 40.0000016292334, s: [100] },
                        ],
                        ix: 1,
                    },
                    e: {
                        a: 1,
                        k: [
                            {
                                i: { x: [0.667], y: [1] },
                                o: { x: [0.333], y: [0] },
                                t: 0,
                                s: [1],
                            },
                            {
                                i: { x: [0.667], y: [1] },
                                o: { x: [0.333], y: [0] },
                                t: 30,
                                s: [25],
                            },
                            { t: 60.0000024438501, s: [100] },
                        ],
                        ix: 2,
                    },
                    o: { a: 0, k: 0, ix: 3 },
                    m: 1,
                    ix: 2,
                    nm: 'Trim Paths 1',
                    mn: 'ADBE Vector Filter - Trim',
                    hd: false,
                },
            ],
            ip: 0,
            op: 600.000024438501,
            st: 0,
            bm: 0,
        },
    ],
    markers: [],
};

var DotsLoader = {
    assets: [
        {
            id: 'comp_Circle C_1459D2CB-91AE-41C5-AA50-7DB0CE6F9831',
            layers: [
                {
                    ty: 3.0,
                    ao: 0.0,
                    ddd: 0.0,
                    ind: 15.0,
                    ip: 0.0,
                    ks: {
                        a: {
                            a: 0.0,
                            k: [0.0, 0.0, 0.0],
                        },
                        o: {
                            a: 0.0,
                            k: 100.0,
                        },
                        p: {
                            s: true,
                            x: {
                                a: 0.0,
                                k: 64.0,
                            },
                            y: {
                                a: 0.0,
                                k: 0.0,
                            },
                        },
                        r: {
                            a: 0.0,
                            k: 0.0,
                        },
                        s: {
                            a: 0.0,
                            k: [100.0, 100.0],
                        },
                    },
                    nm: 'Circle C-null',
                    op: 31.0,
                    sr: 1.0,
                    st: 0.0,
                },
                {
                    ty: 4.0,
                    ao: 0.0,
                    ddd: 0.0,
                    ind: 14.0,
                    ip: 0.0,
                    ks: {
                        a: {
                            a: 0.0,
                            k: [0.0, 0.0],
                        },
                        o: {
                            a: 0.0,
                            k: 100.0,
                        },
                        p: {
                            a: 0.0,
                            k: [0.0, 0.0],
                        },
                        r: {
                            a: 0.0,
                            k: 0.0,
                        },
                        s: {
                            a: 0.0,
                            k: [100.0, 100.0],
                        },
                    },
                    nm: 'Circle C-content',
                    op: 31.0,
                    parent: 15.0,
                    shapes: [
                        {
                            ty: 'gr',
                            cix: 2.0,
                            it: [
                                {
                                    ty: 'sh',
                                    ind: 0.0,
                                    ks: {
                                        a: 0.0,
                                        k: {
                                            c: true,
                                            i: [
                                                [-4.4, 0.0],
                                                [0.0, 4.4],
                                                [4.4, 0.0],
                                                [0.0, -4.4],
                                            ],
                                            o: [
                                                [4.4, 0.0],
                                                [0.0, -4.4],
                                                [-4.4, 0.0],
                                                [0.0, 4.4],
                                            ],
                                            v: [
                                                [8.0, 16.0],
                                                [16.0, 8.0],
                                                [8.0, 0.0],
                                                [0.0, 8.0],
                                            ],
                                        },
                                    },
                                    nm: 'Circle C-path',
                                },
                                {
                                    ty: 'fl',
                                    c: {
                                        a: 1.0,
                                        k: [
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 0.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 9.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.671, 0.843, 0.937, 1.0],
                                                t: 18.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [0.671, 0.843, 0.937, 1.0],
                                                t: 18.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 27.0,
                                            },
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 30.0,
                                            },
                                        ],
                                    },
                                    nm: 'Fill 1',
                                    o: {
                                        a: 1.0,
                                        k: [
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 0.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 9.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 18.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 18.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 27.0,
                                            },
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 30.0,
                                            },
                                        ],
                                    },
                                    r: 1.0,
                                },
                                {
                                    ty: 'tr',
                                    a: {
                                        a: 0.0,
                                        k: [0.0, 0.0],
                                    },
                                    nm: 'Transform',
                                    o: {
                                        a: 0.0,
                                        k: 100.0,
                                    },
                                    p: {
                                        a: 0.0,
                                        k: [0.0, 0.0],
                                    },
                                    r: {
                                        a: 0.0,
                                        k: 0.0,
                                    },
                                    s: {
                                        a: 0.0,
                                        k: [100.0, 100.0],
                                    },
                                    sa: {
                                        a: 0.0,
                                        k: 0.0,
                                    },
                                    sk: {
                                        a: 0.0,
                                        k: 0.0,
                                    },
                                },
                            ],
                            nm: 'Circle C-content',
                            np: 3.0,
                        },
                    ],
                    sr: 1.0,
                    st: 0.0,
                },
            ],
        },
        {
            id: 'comp_Circle B_188021AC-B576-4181-AF9B-D21F72E8E2B9',
            layers: [
                {
                    ty: 3.0,
                    ao: 0.0,
                    ddd: 0.0,
                    ind: 25.0,
                    ip: 0.0,
                    ks: {
                        a: {
                            a: 0.0,
                            k: [0.0, 0.0, 0.0],
                        },
                        o: {
                            a: 0.0,
                            k: 100.0,
                        },
                        p: {
                            s: true,
                            x: {
                                a: 0.0,
                                k: 32.0,
                            },
                            y: {
                                a: 0.0,
                                k: -0.0,
                            },
                        },
                        r: {
                            a: 0.0,
                            k: 0.0,
                        },
                        s: {
                            a: 0.0,
                            k: [100.0, 100.0],
                        },
                    },
                    nm: 'Circle B-null',
                    op: 31.0,
                    sr: 1.0,
                    st: 0.0,
                },
                {
                    ty: 4.0,
                    ao: 0.0,
                    ddd: 0.0,
                    ind: 24.0,
                    ip: 0.0,
                    ks: {
                        a: {
                            a: 0.0,
                            k: [0.0, 0.0],
                        },
                        o: {
                            a: 0.0,
                            k: 100.0,
                        },
                        p: {
                            a: 0.0,
                            k: [0.0, 0.0],
                        },
                        r: {
                            a: 0.0,
                            k: 0.0,
                        },
                        s: {
                            a: 0.0,
                            k: [100.0, 100.0],
                        },
                    },
                    nm: 'Circle B-content',
                    op: 31.0,
                    parent: 25.0,
                    shapes: [
                        {
                            ty: 'gr',
                            cix: 2.0,
                            it: [
                                {
                                    ty: 'sh',
                                    ind: 0.0,
                                    ks: {
                                        a: 0.0,
                                        k: {
                                            c: true,
                                            i: [
                                                [-4.4, 0.0],
                                                [0.0, 4.4],
                                                [4.4, 0.0],
                                                [0.0, -4.4],
                                            ],
                                            o: [
                                                [4.4, 0.0],
                                                [0.0, -4.4],
                                                [-4.4, 0.0],
                                                [0.0, 4.4],
                                            ],
                                            v: [
                                                [8.0, 16.0],
                                                [16.0, 8.0],
                                                [8.0, 0.0],
                                                [0.0, 8.0],
                                            ],
                                        },
                                    },
                                    nm: 'Circle B-path',
                                },
                                {
                                    ty: 'fl',
                                    c: {
                                        a: 1.0,
                                        k: [
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 0.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 0.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.671, 0.843, 0.937, 1.0],
                                                t: 9.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [0.671, 0.843, 0.937, 1.0],
                                                t: 9.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 18.0,
                                            },
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 30.0,
                                            },
                                        ],
                                    },
                                    nm: 'Fill 1',
                                    o: {
                                        a: 1.0,
                                        k: [
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 0.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 0.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 9.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 9.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 18.0,
                                            },
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 30.0,
                                            },
                                        ],
                                    },
                                    r: 1.0,
                                },
                                {
                                    ty: 'tr',
                                    a: {
                                        a: 0.0,
                                        k: [0.0, 0.0],
                                    },
                                    nm: 'Transform',
                                    o: {
                                        a: 0.0,
                                        k: 100.0,
                                    },
                                    p: {
                                        a: 0.0,
                                        k: [0.0, 0.0],
                                    },
                                    r: {
                                        a: 0.0,
                                        k: 0.0,
                                    },
                                    s: {
                                        a: 0.0,
                                        k: [100.0, 100.0],
                                    },
                                    sa: {
                                        a: 0.0,
                                        k: 0.0,
                                    },
                                    sk: {
                                        a: 0.0,
                                        k: 0.0,
                                    },
                                },
                            ],
                            nm: 'Circle B-content',
                            np: 3.0,
                        },
                    ],
                    sr: 1.0,
                    st: 0.0,
                },
            ],
        },
        {
            id: 'comp_Circle A_0695107A-9986-4B35-A877-3F20B08E35B8',
            layers: [
                {
                    ty: 3.0,
                    ao: 0.0,
                    ddd: 0.0,
                    ind: 35.0,
                    ip: 0.0,
                    ks: {
                        a: {
                            a: 0.0,
                            k: [0.0, 0.0, 0.0],
                        },
                        o: {
                            a: 0.0,
                            k: 100.0,
                        },
                        p: {
                            s: true,
                            x: {
                                a: 0.0,
                                k: 0.0,
                            },
                            y: {
                                a: 0.0,
                                k: 0.0,
                            },
                        },
                        r: {
                            a: 0.0,
                            k: 0.0,
                        },
                        s: {
                            a: 0.0,
                            k: [100.0, 100.0],
                        },
                    },
                    nm: 'Circle A-null',
                    op: 31.0,
                    sr: 1.0,
                    st: 0.0,
                },
                {
                    ty: 4.0,
                    ao: 0.0,
                    ddd: 0.0,
                    ind: 34.0,
                    ip: 0.0,
                    ks: {
                        a: {
                            a: 0.0,
                            k: [0.0, 0.0],
                        },
                        o: {
                            a: 0.0,
                            k: 100.0,
                        },
                        p: {
                            a: 0.0,
                            k: [0.0, 0.0],
                        },
                        r: {
                            a: 0.0,
                            k: 0.0,
                        },
                        s: {
                            a: 0.0,
                            k: [100.0, 100.0],
                        },
                    },
                    nm: 'Circle A-content',
                    op: 31.0,
                    parent: 35.0,
                    shapes: [
                        {
                            ty: 'gr',
                            cix: 2.0,
                            it: [
                                {
                                    ty: 'sh',
                                    ind: 0.0,
                                    ks: {
                                        a: 0.0,
                                        k: {
                                            c: true,
                                            i: [
                                                [-4.4, 0.0],
                                                [0.0, 4.4],
                                                [4.4, 0.0],
                                                [0.0, -4.4],
                                            ],
                                            o: [
                                                [4.4, 0.0],
                                                [0.0, -4.4],
                                                [-4.4, 0.0],
                                                [0.0, 4.4],
                                            ],
                                            v: [
                                                [8.0, 16.0],
                                                [16.0, 8.0],
                                                [8.0, 0.0],
                                                [0.0, 8.0],
                                            ],
                                        },
                                    },
                                    nm: 'Circle A-path',
                                },
                                {
                                    ty: 'fl',
                                    c: {
                                        a: 1.0,
                                        k: [
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.671, 0.843, 0.937, 1.0],
                                                t: 0.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [0.671, 0.843, 0.937, 1.0],
                                                t: 0.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 9.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [0.937, 0.969, 0.984, 1.0],
                                                t: 18.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [0.671, 0.843, 0.937, 1.0],
                                                t: 27.0,
                                            },
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [0.671, 0.843, 0.937, 1.0],
                                                t: 30.0,
                                            },
                                        ],
                                    },
                                    nm: 'Fill 1',
                                    o: {
                                        a: 1.0,
                                        k: [
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 0.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 0.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 9.0,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 18.3,
                                            },
                                            {
                                                i: {
                                                    x: [0.5799999833106995],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.41999998688697815],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 27.0,
                                            },
                                            {
                                                i: {
                                                    x: [1.0],
                                                    y: [1.0],
                                                },
                                                o: {
                                                    x: [0.0],
                                                    y: [0.0],
                                                },
                                                s: [100.0],
                                                t: 30.0,
                                            },
                                        ],
                                    },
                                    r: 1.0,
                                },
                                {
                                    ty: 'tr',
                                    a: {
                                        a: 0.0,
                                        k: [0.0, 0.0],
                                    },
                                    nm: 'Transform',
                                    o: {
                                        a: 0.0,
                                        k: 100.0,
                                    },
                                    p: {
                                        a: 0.0,
                                        k: [0.0, 0.0],
                                    },
                                    r: {
                                        a: 0.0,
                                        k: 0.0,
                                    },
                                    s: {
                                        a: 0.0,
                                        k: [100.0, 100.0],
                                    },
                                    sa: {
                                        a: 0.0,
                                        k: 0.0,
                                    },
                                    sk: {
                                        a: 0.0,
                                        k: 0.0,
                                    },
                                },
                            ],
                            nm: 'Circle A-content',
                            np: 3.0,
                        },
                    ],
                    sr: 1.0,
                    st: 0.0,
                },
            ],
        },
    ],
    ddd: 0.0,
    fonts: {
        list: [],
    },
    fr: 30.0,
    h: 16.0,
    ip: 0.0,
    layers: [
        {
            ty: 0.0,
            ao: 0.0,
            ddd: 0.0,
            h: 16.0,
            ind: 30.0,
            ip: 0.0,
            ks: {
                a: {
                    a: 0.0,
                    k: [0.0, 0.0],
                },
                o: {
                    a: 0.0,
                    k: 100.0,
                },
                p: {
                    a: 0.0,
                    k: [0.0, 0.0],
                },
                r: {
                    a: 0.0,
                    k: 0.0,
                },
                s: {
                    a: 0.0,
                    k: [100.0, 100.0],
                },
            },
            nm: 'Circle A-composition',
            op: 31.0,
            refId: 'comp_Circle A_0695107A-9986-4B35-A877-3F20B08E35B8',
            sr: 1.0,
            st: 0.0,
            w: 80.0,
        },
        {
            ty: 0.0,
            ao: 0.0,
            ddd: 0.0,
            h: 16.0,
            ind: 20.0,
            ip: 0.0,
            ks: {
                a: {
                    a: 0.0,
                    k: [0.0, 0.0],
                },
                o: {
                    a: 0.0,
                    k: 100.0,
                },
                p: {
                    a: 0.0,
                    k: [0.0, 0.0],
                },
                r: {
                    a: 0.0,
                    k: 0.0,
                },
                s: {
                    a: 0.0,
                    k: [100.0, 100.0],
                },
            },
            nm: 'Circle B-composition',
            op: 31.0,
            refId: 'comp_Circle B_188021AC-B576-4181-AF9B-D21F72E8E2B9',
            sr: 1.0,
            st: 0.0,
            w: 80.0,
        },
        {
            ty: 0.0,
            ao: 0.0,
            ddd: 0.0,
            h: 16.0,
            ind: 10.0,
            ip: 0.0,
            ks: {
                a: {
                    a: 0.0,
                    k: [0.0, 0.0],
                },
                o: {
                    a: 0.0,
                    k: 100.0,
                },
                p: {
                    a: 0.0,
                    k: [0.0, 0.0],
                },
                r: {
                    a: 0.0,
                    k: 0.0,
                },
                s: {
                    a: 0.0,
                    k: [100.0, 100.0],
                },
            },
            nm: 'Circle C-composition',
            op: 31.0,
            refId: 'comp_Circle C_1459D2CB-91AE-41C5-AA50-7DB0CE6F9831',
            sr: 1.0,
            st: 0.0,
            w: 80.0,
        },
        {
            ty: 4.0,
            ao: 0.0,
            ddd: 0.0,
            ind: 2.0,
            ip: 0.0,
            ks: {
                a: {
                    a: 0.0,
                    k: [0.0, 0.0],
                },
                o: {
                    a: 0.0,
                    k: 100.0,
                },
                p: {
                    a: 0.0,
                    k: [0.0, 0.0],
                },
                r: {
                    a: 0.0,
                    k: 0.0,
                },
                s: {
                    a: 0.0,
                    k: [100.0, 100.0],
                },
            },
            nm: 'Dots Loader-background',
            op: 31.0,
            shapes: [
                {
                    ty: 'gr',
                    cix: 2.0,
                    it: [
                        {
                            ty: 'rc',
                            d: 1.0,
                            nm: 'Dots Loader-background',
                            p: {
                                a: 0.0,
                                k: [40.0, 8.0],
                            },
                            r: {
                                a: 0.0,
                                k: 0.0,
                            },
                            s: {
                                a: 0.0,
                                k: [80.0, 16.0],
                            },
                        },
                        {
                            ty: 'fl',
                            c: {
                                a: 0.0,
                                k: [1.0, 1.0, 1.0, 1.0],
                            },
                            nm: 'Fill 1',
                            o: {
                                a: 0.0,
                                k: 100.0,
                            },
                            r: 1.0,
                        },
                        {
                            ty: 'tr',
                            a: {
                                a: 0.0,
                                k: [0.0, 0.0],
                            },
                            nm: 'Transform',
                            o: {
                                a: 0.0,
                                k: 100.0,
                            },
                            p: {
                                a: 0.0,
                                k: [0.0, 0.0],
                            },
                            r: {
                                a: 0.0,
                                k: 0.0,
                            },
                            s: {
                                a: 0.0,
                                k: [100.0, 100.0],
                            },
                            sa: {
                                a: 0.0,
                                k: 0.0,
                            },
                            sk: {
                                a: 0.0,
                                k: 0.0,
                            },
                        },
                    ],
                    nm: 'Dots Loader-background',
                    np: 3.0,
                },
            ],
            sr: 1.0,
            st: 0.0,
        },
    ],
    meta: {
        a: '',
        d: 'Loader Dots',
        g: 'Flow 1.13.0',
        k: '',
        tc: '',
    },
    nm: 'Loader Dots',
    op: 31.0,
    v: '5.7.14',
    w: 80.0,
};

var data = {
    MidnightBlueLoader: MidnightBlueLoader,
    WhiteLoader: WhiteLoader,
    DotsLoader: DotsLoader,
};

var createLottieConfig = function (_a) {
    var loop = _a.loop, autoplay = _a.autoplay;
    return ({
        loop: loop,
        autoplay: autoplay,
    });
};
var Lottie = function (_a) {
    var name = _a.name, _b = _a.loop, loop = _b === void 0 ? true : _b, _c = _a.autoplay, autoplay = _c === void 0 ? true : _c, args = __rest(_a, ["name", "loop", "autoplay"]);
    var animationData = data[name];
    var loaderRef = useRef(null);
    var lottieConfig = createLottieConfig({ loop: loop, autoplay: autoplay });
    React__default.useEffect(function () {
        if (loaderRef.current) {
            lottie.loadAnimation(__assign(__assign({}, lottieConfig), { animationData: animationData, container: loaderRef.current }));
        }
        return function () {
            lottie.destroy();
        };
    }, [loaderRef, animationData, lottieConfig]);
    return React__default.createElement(Box, __assign({ "data-testid": "lottie", ref: loaderRef }, args));
};
Lottie.displayName = 'Lottie';

var Message = forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'default' : _b, props = __rest(_a, ["variant"]);
    return (React__default.createElement(Box, __assign({ ref: ref, variant: variant, tx: "form.message", sx: {
            fontSize: 1,
        } }, props)));
});
Message.displayName = 'Message';

var Radio = forwardRef(function (props, ref) {
    return (React__default.createElement(Flex, { alignItems: "center", m: 2 },
        React__default.createElement(Box, __assign({ as: "input", type: "radio", ref: ref, id: "".concat(props.name, "-").concat(props.value), "data-cy": "".concat(props.name, "-").concat(props.value), tx: "form.radio" // Uses theme.form.radio as base CSS
            , height: "radioHeight", width: "radioHeight", mr: 4, borderRadius: "circle", borderWidth: "input", borderStyle: "solid", __css: {
                appearance: 'none',
                ':disabled': {
                    borderColor: 'neutralsBorderGray',
                    bg: 'neutralsBackgroundGray',
                },
            } }, props)),
        React__default.createElement(Flex, { flexDirection: "column", justifyContent: "center", fontWeight: "medium" },
            React__default.createElement(Label$1, { variant: props.disabled ? 'disabled' : 'default', fontSize: 2, htmlFor: "".concat(props.name, "-").concat(props.value) }, props.label),
            props.subLabel && (React__default.createElement(Label$1, { variant: props.disabled ? 'disabled' : props.variant, fontSize: 0, fontWeight: "400", color: "neutralsTextGray", mt: 1 }, props.subLabel)))));
});
Radio.displayName = 'Radio';
var RadioGroup = forwardRef(function (_a, ref) {
    var name = _a.name, options = _a.options, selected = _a.selected, onChange = _a.onChange, containerProps = _a.containerProps, props = __rest(_a, ["name", "options", "selected", "onChange", "containerProps"]);
    var _b = useState(selected), selectedValue = _b[0], setSelectedValue = _b[1];
    var handleOnchange = function (e) {
        onChange && onChange(e);
        setSelectedValue(e.target.value);
    };
    return (React__default.createElement(Flex, __assign({ flexDirection: "column" }, containerProps), options.map(function (option) { return (React__default.createElement(Radio, __assign({ ref: ref, key: option.value, name: name, "data-testid": "".concat(name, "-").concat(option.value), checked: selectedValue === option.value, onChange: handleOnchange, "aria-label": name }, props, option))); })));
});
RadioGroup.displayName = 'RadioGroup';

var Search = forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'default' : _b, onSearch = _a.onSearch, props = __rest(_a, ["variant", "onSearch"]);
    var handleKeyDown = useCallback(function (event) {
        var key = event.key;
        if (key === 'Enter' || key === 'NumpadEnter') {
            onSearch && onSearch();
        }
    }, [onSearch]);
    return (React__default.createElement(Box, { position: "relative", width: "full" },
        React__default.createElement(Input, __assign({ variant: variant, onKeyDown: handleKeyDown }, props, { ref: ref })),
        React__default.createElement(Flex, { justifyContent: "center", alignItems: "center", position: "absolute", top: "0.063rem", right: "0.063rem", width: "2.875rem", height: "2.875rem", onClick: onSearch, sx: {
                cursor: 'pointer',
            } },
            React__default.createElement(Icon, { name: "search", size: "1.5rem", color: !(props === null || props === void 0 ? void 0 : props.value) || props.disabled
                    ? colors.neutralsBorderGray
                    : colors.neutralsTextGray }))));
});
Search.displayName = 'Search';
var SearchGroup = forwardRef(function (_a, ref) {
    var variant = _a.variant, label = _a.label, message = _a.message, disabled = _a.disabled, containerProps = _a.containerProps, messageProps = _a.messageProps, error = _a.error, props = __rest(_a, ["variant", "label", "message", "disabled", "containerProps", "messageProps", "error"]);
    var variantType = error ? 'error' : variant;
    var messageText = error || message;
    return (React__default.createElement(Flex, __assign({ flexDirection: "column", p: 4, width: "100%" }, containerProps),
        React__default.createElement(Label$1, { variant: disabled ? 'disabled' : 'default', mb: 2, htmlFor: props.id || props.name, "aria-label": props.id || props.name }, label),
        React__default.createElement(Search, __assign({ ref: ref, variant: variantType, disabled: disabled, maxWidth: "none", width: "full", "aria-label": props.id || props.name }, props)),
        messageText && (React__default.createElement(Message, __assign({ variant: variantType, mt: 1 }, messageProps), messageText))));
});
SearchGroup.displayName = 'SearchGroup';

var getListRowStyles = function (isSelected, subLabel) {
    var optionColors = {
        iconContainerColor: 'brandBlueWash',
        iconColor: 'brandMidnightBlue',
        subLabelColor: 'neutralsTextGray',
    };
    if (isSelected) {
        optionColors = {
            iconContainerColor: 'overlayBlack15',
            iconColor: 'neutralsWhite',
            labelColor: 'neutralsWhite',
            subLabelColor: 'neutralsWhite',
        };
    }
    var labelVariant = subLabel ? 'medium' : 'regular';
    return __assign(__assign({}, optionColors), { labelVariant: labelVariant });
};
var Option = function (props) {
    var _a = props.data, label = _a.label, subLabel = _a.subLabel, icon = _a.icon;
    var isSelected = props.isSelected;
    return (React__default.createElement(components.Option, __assign({}, props),
        React__default.createElement(Box, { px: "1rem" },
            React__default.createElement(ListRow, __assign({ icon: icon, label: label, subLabel: subLabel }, getListRowStyles(isSelected, subLabel))))));
};
var DropdownIndicator = function (props) {
    return (React__default.createElement(components.DropdownIndicator, __assign({}, props),
        React__default.createElement(Icon, { name: "drop-down" })));
};
var ClearIndicator = function (props) {
    return (React__default.createElement(components.ClearIndicator, __assign({}, props),
        React__default.createElement(Icon, { name: "x" })));
};
var IndicatorSeparator = function () { return null; };

var Select = forwardRef(function (_a, ref) {
    var options = _a.options, isDisabled = _a.isDisabled, error = _a.error, noOptionsText = _a.noOptionsText, _b = _a.isSearchable, isSearchable = _b === void 0 ? false : _b, props = __rest(_a, ["options", "isDisabled", "error", "noOptionsText", "isSearchable"]);
    var verticalPadding = {
        paddingTop: '0.75rem',
        paddingBottom: '0.75rem',
    };
    var customStyles = {
        control: function (base, state) { return (__assign(__assign({}, base), { fontFamily: theme.fonts.regular, maxWidth: props.width || theme.sizes.inputMaxWidth, height: theme.sizes.inputHeight, borderColor: error
                ? colors.utilityDarkErrorRed
                : state.isFocused
                    ? colors.utilityFocusBlue
                    : colors.neutralsBorderGray })); },
        menu: function (base) { return (__assign(__assign({}, base), { fontFamily: theme.fonts.regular, maxWidth: props.width || theme.sizes.inputMaxWidth })); },
        menuList: function (base) { return (__assign(__assign({}, base), { padding: 0 })); },
        option: function (styles, _a) {
            var isFocused = _a.isFocused, isSelected = _a.isSelected;
            return (__assign(__assign(__assign({}, styles), verticalPadding), { paddingLeft: 0, paddingRight: 0, background: isSelected
                    ? colors.utilityFocusBlue
                    : isFocused && colors.brandBlueWash, color: isSelected && colors.neutralsWhite }));
        },
        clearIndicator: function (base) { return (__assign(__assign({}, base), { padding: 0 })); },
        valueContainer: function (base) { return (__assign(__assign({}, base), { paddingLeft: '1rem' })); },
        dropdownIndicator: function (base) { return (__assign(__assign({}, base), { paddingRight: '1rem' })); },
        noOptionsMessage: function (base) { return (__assign(__assign(__assign({}, base), verticalPadding), { color: colors.neutralsTextGray })); },
    };
    return (React__default.createElement(ReactSelect, __assign({}, props, { ref: ref, options: options, isDisabled: isDisabled, isSearchable: isSearchable, noOptionsMessage: function () { return noOptionsText || 'No Match Found'; }, components: {
            ClearIndicator: ClearIndicator,
            DropdownIndicator: DropdownIndicator,
            IndicatorSeparator: IndicatorSeparator,
            Option: Option,
        }, styles: customStyles, theme: function (rsTheme) { return (__assign(__assign({}, rsTheme), { borderRadius: 2, colors: __assign(__assign({}, rsTheme.colors), { primary: error
                    ? colors.utilityDarkErrorRed
                    : colors.utilityFocusBlue, primary25: colors.brandBlueWash, primary50: colors.brandBlueWash, primary75: colors.brandMediumBlue }) })); } })));
});
Select.displayName = 'Select';
var SelectGroup = forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'default' : _b, label = _a.label, message = _a.message, isDisabled = _a.isDisabled, messageProps = _a.messageProps, containerProps = _a.containerProps, error = _a.error, isSearchable = _a.isSearchable, props = __rest(_a, ["variant", "label", "message", "isDisabled", "messageProps", "containerProps", "error", "isSearchable"]);
    var variantType = error ? 'error' : variant;
    var messageText = error || message;
    return (React__default.createElement(Flex, __assign({ flexDirection: "column", p: 4, width: "100%" }, containerProps),
        React__default.createElement(Label$1, { as: "label", variant: isDisabled ? 'disabled' : 'default', mb: 2, htmlFor: props.id || props.name }, label),
        React__default.createElement(Select, __assign({ ref: ref, isSearchable: isSearchable, isDisabled: isDisabled, error: error, maxWidth: "none", width: "100%" }, props)),
        messageText && (React__default.createElement(Message, __assign({ role: "alert", variant: variantType, mt: 1 }, messageProps), messageText))));
});
SelectGroup.displayName = 'SelectGroup';

var Switch = forwardRef(function (props, ref) {
    var switchRef = useRef(null);
    var id = props.id, checked = props.checked, disabled = props.disabled, onChange = props.onChange, label = props.label, subLabel = props.subLabel, labelIcon = props.labelIcon, labelPosition = props.labelPosition;
    var toggleOnSpacebar = useCallback(function (e) {
        var _a;
        e.key === ' ' && ((_a = switchRef === null || switchRef === void 0 ? void 0 : switchRef.current) === null || _a === void 0 ? void 0 : _a.click());
    }, []);
    var isDisabledText = useCallback(function () {
        return disabled ? 'neutralsPlaceholderGray' : 'neutralsTextBlack';
    }, [disabled]);
    return (React__default.createElement(Flex, __assign({ alignItems: "center", justifyContent: "space-between", flexDirection: !labelPosition || labelPosition === 'left' ? 'row' : 'row-reverse' }, props.containerProps),
        label && (React__default.createElement(Flex, { as: "label", alignItems: "center", htmlFor: "checkbox-".concat(id), maxWidth: "90%" },
            labelIcon && (React__default.createElement(Box, { mr: 4, minWidth: "1.25rem" },
                React__default.createElement(Icon, { name: labelIcon, size: "alertIcon", color: isDisabledText() }))),
            React__default.createElement(Flex, { flexDirection: "column" },
                React__default.createElement(Text, { variant: "medium", color: isDisabledText() }, label),
                subLabel && (React__default.createElement(Text, { color: disabled ? 'neutralsPlaceholderGray' : 'neutralsTextGray', variant: "body", mt: 1, fontSize: 1 }, subLabel))))),
        React__default.createElement(Box, { ref: ref, p: "2px", tabIndex: !disabled ? 0 : -1, onKeyPress: toggleOnSpacebar, __css: {
                ':focus': {
                    outlineStyle: 'solid',
                    outlineWidth: '2px',
                    outlineColor: 'utilityFocusBlue',
                },
            } },
            React__default.createElement(Flex, __assign({ as: "label", htmlFor: "checkbox-".concat(id), ref: switchRef, position: "relative", alignItems: "center", justifyContent: "space-between", height: "1.75rem", width: "3rem", borderRadius: "3rem", borderStyle: "solid", borderWidth: "input", __css: __assign(__assign({ cursor: 'pointer', backgroundColor: 'neutralsBackgroundGray', borderColor: 'neutralsBorderGray' }, (checked && {
                    backgroundColor: 'brandMidnightBlue',
                    borderColor: 'brandMidnightBlue',
                })), (disabled && __assign({ cursor: 'not-allowed' }, (checked && {
                    backgroundColor: 'neutralsBorderGray',
                    borderColor: 'neutralsBorderGray',
                })))) }, props),
                React__default.createElement(Box, { as: "input", type: "checkbox", disabled: disabled, checked: checked, onChange: onChange, id: "checkbox-".concat(id), height: 0, width: 0, __css: {
                        visibility: 'hidden',
                    } }),
                React__default.createElement(Box, { as: "span", __css: __assign(__assign({ content: '', position: 'absolute', top: '-1px', left: '-2px', width: '1.75rem', height: '1.75rem', transition: '0.3s', backgroundColor: 'neutralsWhite', borderColor: 'neutralsBorderGray', borderRadius: '1.75rem', borderStyle: 'solid', borderWidth: 'input' }, (checked && {
                        left: 'calc(100% + 2px)',
                        transform: 'translateX(-100%)',
                        borderColor: 'brandMidnightBlue',
                    })), (disabled && __assign({ backgroundColor: 'neutralsBackgroundGray' }, (checked && {
                        borderColor: 'neutralsBorderGray',
                    })))) })))));
});
Switch.displayName = 'Switch';

var Tag = function (_a) {
    var icon = _a.icon, text = _a.text, _b = _a.onClose, onClose = _b === void 0 ? null : _b, _c = _a.onClick, onClick = _c === void 0 ? null : _c, _d = _a.variant, variant = _d === void 0 ? 'info' : _d, props = __rest(_a, ["icon", "text", "onClose", "onClick", "variant"]);
    var variantIconColor = (tagVariants[variant] || {}).variantIconColor;
    return (React__default.createElement(Box, __assign({ tx: "tag", variant: variant, display: "inline-flex", justifyContent: "space-between", onClick: onClick }, props),
        React__default.createElement(Flex, { alignItems: "center" },
            icon && (React__default.createElement(Box, { mr: 2 },
                React__default.createElement(Icon, { name: icon, color: variantIconColor, size: "1.15rem" }))),
            text && (React__default.createElement(Text, { variant: "medium", color: variantIconColor }, text)),
            onClose && (React__default.createElement(Box, { ml: 2, flexShrink: 0 },
                React__default.createElement(Icon, { name: "x", size: "1.15rem", cursor: "pointer", color: variantIconColor, iconClick: function (event) {
                        event.stopPropagation();
                        onClose(event);
                    } }))))));
};
Tag.displayName = 'Tag';

var Text = forwardRef(function (props, ref) {
    return React__default.createElement(Box, __assign({ as: "span", ref: ref, variant: "default", tx: "text" }, props));
});
Text.displayName = 'Text';

var TextArea = forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'default' : _b, props = __rest(_a, ["variant"]);
    return (React__default.createElement(Box, { sx: {
            position: 'relative',
        } },
        React__default.createElement(Box, __assign({ as: "textarea", ref: ref, tx: "form.input", variant: variant, id: props.id || props.name, maxWidth: "inputMaxWidth", height: "inputHeight", py: 3, px: 4, borderWidth: "input", borderStyle: "solid", borderRadius: "input", borderColor: "neutralsBorderGray", color: "neutralsTextBlack" }, props, { __css: {
                background: "".concat(colors.neutralsWhite),
                backgroundSize: '1.5rem',
                outline: 'none',
                ':disabled': {
                    color: 'neutralsPlaceholderGray',
                    borderColor: 'neutralsLightGray',
                    bg: 'neutralsBackgroundGray',
                },
                '::placeholder': {
                    color: 'neutralsPlaceholderGray',
                },
                ':disabled::placeholder': {
                    color: 'neutralsBorderGray',
                },
            } }))));
});
TextArea.displayName = 'TextArea';
var TextAreaGroup = forwardRef(function (_a, ref) {
    var variant = _a.variant, label = _a.label, message = _a.message, disabled = _a.disabled, containerProps = _a.containerProps, messageProps = _a.messageProps, error = _a.error, props = __rest(_a, ["variant", "label", "message", "disabled", "containerProps", "messageProps", "error"]);
    var variantType = error ? 'error' : variant;
    var messageText = error || message;
    return (React__default.createElement(Flex, __assign({ flexDirection: "column", p: 4, width: "100%" }, containerProps),
        React__default.createElement(Label$1, { as: "label", variant: disabled ? 'disabled' : 'default', mb: 2, htmlFor: props.id || props.name }, label),
        React__default.createElement(TextArea, __assign({ ref: ref, variant: variantType, disabled: disabled, maxWidth: "none", width: "100%" }, props)),
        messageText && (React__default.createElement(Message, __assign({ variant: variantType, mt: 1 }, messageProps), messageText))));
});
TextAreaGroup.displayName = 'TextAreaGroup';

var ListRowSet = function (props) {
    var containerWidth = props.containerWidth, listItems = props.listItems, other = __rest(props, ["containerWidth", "listItems"]);
    return (React__default.createElement(Box, __assign({ borderWidth: "input", borderStyle: "solid", borderRadius: "0.5rem", textAlign: "start", flexDirection: "column", width: containerWidth ? "".concat(containerWidth * 10, "rem") : '100%', borderColor: "neutralsBorderGray", overflow: "hidden" }, other), listItems &&
        listItems.map(function (itemProps, index) {
            var onClick = itemProps.onClick, variant = itemProps.variant, rest = __rest(itemProps, ["onClick", "variant"]);
            return (React__default.createElement(Box, { as: "button", key: index, minHeight: "4.5rem", tx: "boxSelector", width: "100%", border: "none", padding: "0", variant: "singleSelect", borderRadius: "0", sx: {
                    cursor: onClick ? 'pointer' : 'default',
                    ':active': {
                        borderRadius: 0,
                    },
                }, borderColor: "neutralsBorderGray", borderTopStyle: "solid", borderTopWidth: index === 0 ? '0' : '0.0625rem', onClick: onClick, textAlign: "left", "aria-label": "".concat(itemProps.label, " Button") },
                React__default.createElement(ListRow, __assign({}, rest, { p: 4, variant: variant || 'actionIcon' }))));
        })));
};

var Pill = function (props) {
    var iconColor = props.iconColor, iconName = props.iconName, backgroundColor = props.backgroundColor, isLoading = props.isLoading, lottieComponent = props.lottieComponent;
    var width = props.width || '4rem';
    if (!width.includes('rem')) {
        throw new Error('Must supply a rem value');
    }
    var widthNumber = parseInt(width.replace('rem', ''), 10);
    var pillToIconAspectRatioREM = 0.5;
    var pillWidthToHeightAspectRatioREM = 1.703125;
    var pillToBorderRadiusAspectRatioREM = 0.5;
    var iconWidth = "".concat(widthNumber * pillToIconAspectRatioREM, "rem");
    var iconHeight = "".concat(widthNumber * pillToIconAspectRatioREM, "rem");
    var pillHeight = "".concat(widthNumber * pillWidthToHeightAspectRatioREM, "rem");
    var borderRadius = "".concat(widthNumber * pillToBorderRadiusAspectRatioREM, "rem");
    var component = iconName ? (React__default.createElement(Icon, { name: iconName, color: iconColor, width: iconWidth, height: iconHeight })) : (React__default.createElement(Box, { position: "absolute", justifyContent: "center", alignItems: "center", zIndex: "3", width: iconWidth, height: iconHeight }, lottieComponent));
    return (React__default.createElement(Box, { display: "flex", backgroundColor: backgroundColor, flexDirection: "column", justifyContent: "center", alignItems: "center", width: width, height: pillHeight, borderRadius: borderRadius }, isLoading && lottieComponent ? (React__default.createElement(Box, { position: "absolute", justifyContent: "center", alignItems: "center", zIndex: "3", width: "1.5rem", height: "1.5rem" }, lottieComponent)) : (React__default.createElement(React__default.Fragment, null, component))));
};
Pill.displayName = 'Pill';

var animations = {
    fadeInUp: {
        initial: { opacity: 0, y: 16 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.5, ease: 'easeOut' },
    },
    fadeInDown: {
        initial: { opacity: 0, y: -16 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.5, ease: 'easeOut' },
    },
};

var loadFeatures = function () {
    return Promise.resolve().then(function () { return framerMotionFeatures; }).then(function (res) { return res.default; });
};
var getAnimationDetails = function (_a) {
    var delay = _a.delay, animationName = _a.animationName, customAnimationDetails = _a.customAnimationDetails;
    if (customAnimationDetails)
        return customAnimationDetails;
    if (!animationName)
        return null;
    if (delay) {
        return __assign(__assign({}, animations[animationName]), { transition: __assign(__assign({}, animations[animationName].transition), { delay: delay }) });
    }
    else {
        return animations[animationName];
    }
};
var Transition = function (_a) {
    var children = _a.children, animationName = _a.animationName, customAnimationDetails = _a.customAnimationDetails, delay = _a.delay;
    var animationDetails = useMemo(function () {
        return getAnimationDetails({
            animationName: animationName,
            delay: delay,
            customAnimationDetails: customAnimationDetails,
        });
    }, [animationName, delay, customAnimationDetails]);
    return (React__default.createElement(LazyMotion, { strict: true, features: loadFeatures },
        React__default.createElement(m.div, __assign({}, animationDetails), children)));
};

var clearEmailRegex = 
// eslint-disable-next-line no-control-regex
/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

// @ts-ignore

var framerMotionFeatures = /*#__PURE__*/Object.freeze({
  __proto__: null,
  'default': domAnimation
});

export { ADAThemeProvider, Accordion, Alert, Box, BoxSelector, Button, Card, Checkbox, Datepicker, DatepickerGroup, Dropdown, DropdownGroup, Flex, GlobalStyle, Icon, Input, InputGroup, Label$1 as Label, Link, ListRow, ListRowSet, Logo, Lottie, Message, Pill, Radio, RadioGroup, SVG, Search, SearchGroup, Select, SelectGroup, Switch, Tag, Text, TextArea, TextAreaGroup, Transition, index as Variants, clearEmailRegex, createLottieConfig, getSVGUrl, theme };
//# sourceMappingURL=index.esm.js.map
